import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import http from '../services/httpClient';
import UnitMappingService from '../services/unitMappingService';
import { formatQuantity, formatQuantityParts, formatUnitLabel, formatFixedDot,
  convert, toCanonicalUnit, getUnitFamily, normalizeToBase } from '../utils/unitDisplay';
import ClosingStockOCRReview from './ClosingStockOCRReview';
import ClosingStockAttestation from './ClosingStockAttestation';
import { piecePackLine, packetsSentence } from '../utils/piecePackReadback';
import NotInMenuWorklist from './notInMenu/NotInMenuWorklist';
import { checkGate } from './notInMenu/notInMenuApi';
import NotInRecipePopup from './notInRecipe/NotInRecipePopup';
import { checkNotInRecipe, reachesMonthEnd } from './notInRecipe/notInRecipeApi';
import BlockedBySalesNameCheck, { isSalesNameCheckBlock } from './shared/BlockedBySalesNameCheck';
import ClosingDatePicker, { formatLongDate } from './ClosingDatePicker';
import CcsPreviewModal from './CcsPreviewModal';
import ContinuousStockPanel from './ContinuousStockPanel';
import SmartUnitDropdown from './SmartUnitDropdown';
import { filenameFromContentDisposition } from '../utils/contentDisposition';
import { StandardSearch } from '../styles/standardStyles';
import { sortBySearchPriority } from '../utils/searchPriority';
import './ClosingStockPage.css';
import { FaEllipsisV, FaQrcode } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';

const getISTDateString = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// Dot decimal separator regardless of device locale — see formatFixedDot.
// toLocaleString('en-IN') falls back to the host locale on WebViews without
// that locale data, turning 0.1 into "0,1", which reads as zero on a sheet.
const formatCurrency = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '₹0';
  return '₹' + formatFixedDot(n, 2);
};

// Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
const formatQty = (val, unit) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '0';
  return formatQuantity(n, unit);
};

const getComponentName = (component) =>
  component?.item_name ||
  component?.ingredient_name ||
  component?.raw_item_name ||
  component?.component_name ||
  component?.name ||
  component?.item ||
  '';

// Composite key for per-category entries
const eKey = (catId, itemId) => `${catId}_${itemId}`;
// Matches OCR_MAX_PAGES on the backend, which enforces it for real.
const MAX_OCR_PAGES = 12;
// A filled template comes back as a spreadsheet: it is read directly, with no
// AI involved, so it must never be routed down the photo path. `.xls` is
// included for POS stock reports (Petpooja exports an HTML table under that
// extension) — the backend sniffs which kind of sheet arrived.
const SHEET_ACCEPT = '.xlsx,.xlsm,.xls,.csv';
const isSheetFile = (file) => {
  const name = (file?.name || '').toLowerCase();
  const type = (file?.type || '').toLowerCase();
  return /\.(xlsx|xlsm|csv|xls)$/.test(name)
    || type.includes('spreadsheetml')
    || type === 'application/vnd.ms-excel'
    || type === 'text/csv';
};
const isImageFile = (file) => (file?.type || '').startsWith('image/')
  || /\.(jpe?g|png|webp|heic|heif)$/i.test(file?.name || '');
// Matches DRAFT_MAX_DAYS on the backend — used only to seed the banner's
// "expires in N days" before the server's own value is fetched.
const DRAFT_TTL_DAYS = 5;

// The units a raw item's unit can be swapped to inline on the Consolidated
// Sheet — same physical family only (never g↔ml), so /apply's conversion stays
// valid. Count items have no alternative.
const CS_UNIT_FAMILY = { g: ['g', 'kg'], kg: ['g', 'kg'], ml: ['ml', 'l'], l: ['ml', 'l'], pcs: ['pcs'] };
const csUnitOptions = (u) => {
  const k = String(u || '').toLowerCase();
  return CS_UNIT_FAMILY[k] || (k ? [k] : []);
};
// Quantities land in DECIMAL(12,4) columns, so keep added values there too —
// otherwise 0.1 + 0.2 accumulates float noise across repeated scans.
const addQty = (a, b) => Math.round(((Number(a) || 0) + (Number(b) || 0)) * 10000) / 10000;

// Does this entry hold a real count? A cleared box ('') and a missing entry
// both mean NOT COUNTED and must never reach the payload — the backend reads a
// blank as 0 and would zero the item's stock. An explicit 0 IS a count.
// Whether the downloaded count sheet carries a price column, remembered per
// business. It used to tick itself only while a business had no closing stock
// history at all — so it appeared once, at onboarding, and never again, which
// read to owners as "the price column is gone". Prices are ON unless the owner
// has said otherwise, and their answer sticks.
//
// The sheet stamps its own answer into the CS token (-P), so the importer reads
// prices only from a sheet printed with them. That is what makes this safe to
// decide per download rather than once per business.
const pricePrefKey = (bid) => `cs_price_col_${bid}`;
const readPriceColPref = (bid) => {
  try {
    const v = localStorage.getItem(pricePrefKey(bid));
    return v === null ? true : v === 'true';   // never chosen -> prices ON
  } catch (e) {
    return true;   // private mode / storage blocked: fall back to the default
  }
};
// Identity of one breakdown request: the same item at the same quantity in the
// same unit always expands the same way.
const expansionKey = (it, qty) => `${it && it.item_type}:${it && it.item_id}:${qty}:${(it && it.unit) || ''}`;

const csHasCount = (q) =>
  q !== undefined && q !== null && String(q).trim() !== '' && Number.isFinite(Number(q));
// Per-ingredient price override keys. Preps, complimentary recipes and menu
// items have separate id sequences, so they need separate prefixes.
const prepPriceKey = (prepId, rawId) => `prep_component_${prepId}_${rawId}`;
const compPriceKey = (compId, rawId) => `comp_component_${compId}_${rawId}`;
const menuPriceKey = (menuId, rawId) => `menu_component_${menuId}_${rawId}`;

// The three countable composites — a preparation (per batch), a complimentary
// recipe (per portion) and a menu item / dish (per plate). All three are counted
// as a header row that decomposes into atomic raw ingredients, so they share
// every downstream path and differ only in how they expand.
const CS_COMPOSITE_TYPES = ['preparation', 'complimentary', 'menu'];
const isCompositeType = (t) => CS_COMPOSITE_TYPES.includes(t);
// Entry-key prefix per item type ('prep'/'comp'/'menu'/'raw' id spaces overlap).
const kindOfType = (t) => (
  t === 'preparation' ? 'prep' : t === 'complimentary' ? 'comp' : t === 'menu' ? 'menu' : 'raw'
);
const componentPriceKey = (itemType, parentId, rawId) => (
  itemType === 'menu' ? menuPriceKey(parentId, rawId)
    : itemType === 'complimentary' ? compPriceKey(parentId, rawId)
      : prepPriceKey(parentId, rawId)
);

// Conversion is the app's, not this page's. `convert` returns null across
// families and for unknown units — every caller here handles that null, because
// the page used to keep its own table whose fallback returned the number
// unchanged, and that is how "200 ml of a 1 kg batch" previewed as 200 batches.
const convertQty = (qty, fromUnit, toUnit) => {
  const v = convert(qty, fromUnit, toUnit);
  return v === null ? null : v;
};

// Re-express one counted row in another unit of the SAME family.
//
// Switching the unit changes how the stock is written down, never how much of
// it exists — so both halves of the row move together. The count multiplies by
// the conversion factor and the per-unit price divides by it, which leaves the
// row's total value untouched: 2 kg @ Rs400/kg and 2000 g @ Rs0.40/g are the
// same Rs800. Converting the quantity alone would make that row Rs800,000.
//
// Returns null when the units cannot be converted (different families, unknown
// symbol) so the caller can refuse rather than write a fabricated number.
const reexpressRowUnit = (quantity, unitPrice, fromUnit, toUnit) => {
  const factor = convertQty(1, fromUnit, toUnit);   // new units in one old unit
  if (factor === null || !Number.isFinite(factor) || factor <= 0) return null;
  // An UNCOUNTED row stays uncounted: converting '' into 0 would turn 'not
  // counted yet' into a signed-off 'none left'.
  const qtyOut = csHasCount(quantity)
    ? String(Number((Number(quantity) * factor).toFixed(6)))
    : quantity;
  const priceOut = (unitPrice === '' || unitPrice === undefined || unitPrice === null
                    || !Number.isFinite(Number(unitPrice)))
    ? unitPrice
    : String(Number((Number(unitPrice) / factor).toFixed(6)));
  return { quantity: qtyOut, unit_price: priceOut };
};

// ── One ingredient = ONE price on a count ─────────────────────────────────
//
// An ingredient's price is kept once, as ₹ per its SHEET unit, and every row,
// recipe line, total, popup and the /apply payload reads that one value. It used
// to live in several places at once — the price written on the raw row was read
// by that row alone, while recipe lines and the "Enter price to save" popup read
// a separate shared store that the sheet import and the inline ₹ box never
// wrote. BID 198 imported "₹33 for 1 kg" of salt and was still asked for salt's
// price inside 7 recipes (56 of its 61 recipe ingredients were priced that way).

// The same goods in two units: ₹ per `fromUnit` → ₹ per `toUnit` (₹33/kg is
// ₹0.033/g). null when the units do not convert — never a guessed number.
const rebasePrice = (price, fromUnit, toUnit) => {
  const p = Number(price);
  if (!Number.isFinite(p)) return null;
  if (!fromUnit || !toUnit || String(fromUnit).toLowerCase() === String(toUnit).toLowerCase()) return p;
  const fromPerTo = convertQty(1, toUnit, fromUnit);   // fromUnits in one toUnit
  if (fromPerTo === null || !Number.isFinite(fromPerTo) || fromPerTo <= 0) return null;
  return Number((p * fromPerTo).toFixed(6));
};

// A "fill" is a price about to become an ingredient's one price:
// { id, price, unit } with `price` in ₹ per `unit`. `sheetUnitOf(id, fallback)`
// names the unit prices are stored per. Both halves below take the same fills,
// so the stored price and any row text can never be written apart.
//
// The stored half: ₹ per the item's sheet unit. A price of ''/0 clears it.
const storeIngredientPrices = (prev, fills, sheetUnitOf) => {
  let next = prev;
  for (const f of fills || []) {
    const su = sheetUnitOf(f.id, f.unit);
    const p = rebasePrice(f.price, f.unit || su, su);
    if (p === null) continue;   // unconvertible: leave the price as it was
    next = { ...next };
    if (p > 0) next[f.id] = p; else delete next[f.id];
  }
  return next;
};

// The row half: a row that shows its OWN ₹ text (typed into its box, or kept by
// an older draft) is rewritten to the same price in that row's unit. Rows with
// no text of their own need nothing — they display the stored price.
const syncRowPrices = (prev, fills, sheetUnitOf) => {
  let next = prev;
  for (const f of fills || []) {
    const key = eKey('raw', f.id);
    const e = prev[key];
    if (!e || e.unit_price === undefined) continue;
    const su = sheetUnitOf(f.id, f.unit);
    const p = rebasePrice(f.price, f.unit || su, su);
    if (p === null) continue;
    const rowP = p > 0 ? rebasePrice(p, su, e.unit || su) : null;
    const out = { ...e };
    if (rowP === null || !(rowP > 0)) delete out.unit_price; else out.unit_price = String(rowP);
    next = { ...next, [key]: out };
  }
  return next;
};

// The ₹ text a raw row carries itself (typed, or kept by an older draft), as a
// number in that row's unit; null when the row has none.
const rowPriceText = (e) => (e && e.unit_price !== undefined && String(e.unit_price).trim() !== ''
  && Number.isFinite(Number(e.unit_price))) ? Number(e.unit_price) : null;

// The "Enter price to save" popup's typed prices as fills. Each popup row is
// one ingredient, priced per the unit the row shows (its sheet unit).
const guideFills = (items, values) => (items || [])
  .filter(g => Number(values[g.key]) > 0)
  .map(g => ({ id: g.item_id, price: Number(values[g.key]), unit: g.unit }));

// The recursive breakdown of a preparation / complimentary recipe / dish USED
// to live here as well as in /apply — four hand-mirrored functions that drifted
// apart. That drift is the "pastry" bug: the sheet showed one breakdown and the
// write path credited another, and nothing compared them.
//
// There is one implementation now, in backend/services/closingStockExpansion,
// reached through POST /closing-stock/expand. A breakdown this screen does not
// have renders as unavailable rather than being guessed locally, because the
// guess is exactly how the two copies came apart.

const ClosingStockPage = ({ goTo, isOnboarding = false, onOnboardingComplete = null, onOpenSalesNameCheck = null }) => {
  const [loading, setLoading] = useState(true);
  // New data model: master categories with nested ingredients/preparations
  const [masterCategories, setMasterCategories] = useState([]);
  const [uncategorizedIngredients, setUncategorizedIngredients] = useState([]);
  const [allPreparations, setAllPreparations] = useState([]);
  const [allComplimentary, setAllComplimentary] = useState([]);
  const [allMenuItems, setAllMenuItems] = useState([]);
  // Saved preparations' never-bought twin items (server: placeholder_twins). The
  // server leaves them out of the item lists — a preparation's stock lives in its
  // ingredients — and names them here so a count already written on one (an
  // older printed sheet, a saved draft) is shown, never silently dropped.
  const [placeholderTwins, setPlaceholderTwins] = useState([]);
  // Active tab: 'all' or a master_category_id number
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [stockStep, setStockStep] = useState('overview');
  const [showSelectionChecklist, setShowSelectionChecklist] = useState(false);
  // Unmapped-TSR gate: bill lines that never resolved to a menu item have not
  // deducted any stock. Counting the shelf while those deductions are missing
  // would bake the discrepancy into the count, so intake is blocked until every
  // pending line is mapped-and-deducted or skipped (Sales Report → mapping flow).
  // null = not checked / clear; {count, amount} = blocked.
  const [unmappedGate, setUnmappedGate] = useState(null);
  const [unmappedGateChecking, setUnmappedGateChecking] = useState(false);
  const [nimOpen, setNimOpen] = useState(false);
  const [nimFrom, setNimFrom] = useState('intake'); // which step the gate interrupted
  // Not in Recipe (spec 2026-10-27): at the count that reaches a month end, every item no dish
  // uses needs an expense head. Off unless the server says the business has it (lock-status),
  // and then the screen never asks — exactly as before the feature.
  const [notInRecipeEnabled, setNotInRecipeEnabled] = useState(false);
  const [nirOpen, setNirOpen] = useState(false);
  const [nirFrom, setNirFrom] = useState('intake'); // which step the check interrupted
  const [nirApplyMessage, setNirApplyMessage] = useState(null); // the 409's reason, when apply refused
  const [nirGate, setNirGate] = useState(null); // { error, from } when the check could not answer
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [verificationAccepted, setVerificationAccepted] = useState(false);
  // prepsSaved state removed — no more step locking
  const [entries, setEntries] = useState({}); // compositeKey -> { quantity, unit_price }
  const [prepComponentPrices, setPrepComponentPrices] = useState({});
  // The ONE stored price per ingredient: ₹ per its SHEET unit, keyed by item_id
  // (see storeIngredientPrices). Set salt's price anywhere — its row, inside a
  // recipe, the popup, an import — and every place salt appears reads it.
  const [ingredientPrices, setIngredientPrices] = useState({});
  // Which unit an ingredient's price is stored per. Filled in once the item
  // list is known (below); a ref so writers declared above can use it.
  const sheetUnitOfRef = useRef((id, fallback) => fallback || null);
  // THE writer: every price set anywhere on the sheet goes through here.
  const applyPriceFills = useCallback((fills) => {
    if (!fills || fills.length === 0) return;
    const su = (id, fallback) => sheetUnitOfRef.current(id, fallback);
    setIngredientPrices(prev => storeIngredientPrices(prev, fills, su));
    setEntries(prev => syncRowPrices(prev, fills, su));
  }, []);
  const [prepPriceDialog, setPrepPriceDialog] = useState(null);
  const [flags, setFlags] = useState({}); // item_id -> { status, message }
  const [closingDate, setClosingDate] = useState(getISTDateString());
  // Date-first calendar (Continuous Closing Stock spec §4.8): nothing is entered until
  // the day this count belongs to is chosen. countedDate is the day the shelves were
  // actually counted — the same day unless "Counted on a later day" was ticked, and
  // only then does the app roll the count back for what moved in between.
  const [dateChosen, setDateChosen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(true);
  const [countedDate, setCountedDate] = useState(getISTDateString());
  const [managerPin, setManagerPin] = useState(null);
  // Continuous Closing Stock: a switched-on business applies its count as a difference and
  // sees the preview (expected / counted / difference) before anything is saved.
  const [continuous, setContinuous] = useState(false);
  const [ccsPreview, setCcsPreview] = useState(null);
  const [showCcsPanel, setShowCcsPanel] = useState(false);
  const [ccsStaff, setCcsStaff] = useState(false);
  const [lockActive, setLockActive] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinValue, setPinValue] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [pinAttempts, setPinAttempts] = useState(0);
  const [backdatePreview, setBackdatePreview] = useState(null);
  const [showBackdateConfirm, setShowBackdateConfirm] = useState(false);
  // Item 3 — "Not counted" pre-commit warning
  const [showNotCountedWarning, setShowNotCountedWarning] = useState(false);
  const [notCountedList, setNotCountedList] = useState([]);
  const [notCountedAcknowledged, setNotCountedAcknowledged] = useState(false);
  // Item 2 — guided mandatory-price fill
  const [showPriceGuide, setShowPriceGuide] = useState(false);
  const [priceGuideItems, setPriceGuideItems] = useState([]);
  const [priceGuideValues, setPriceGuideValues] = useState({});
  const [priceGuideSearch, setPriceGuideSearch] = useState('');
  const [expandedGuideKeys, setExpandedGuideKeys] = useState(new Set());
  // Raw item ids the user chose to "price from first purchase" — confirmed
  // with a provisional value; the first stock-in bill backfills the line.
  const [deferredPriceIds, setDeferredPriceIds] = useState([]);
  const [pendingCommit, setPendingCommit] = useState(false);
  // Negative Stock Clearance — items oversold below zero must be physically
  // counted (which overrides & clears the negative) before a count is confirmed.
  const [showNegativeModal, setShowNegativeModal] = useState(false);
  // C6-4 — the attestation captured at Confirm & Sync; cleared once the count lands.
  const [showAttestation, setShowAttestation] = useState(false);
  // Components the server could not credit on the last apply (cross-family rows).
  const [appliedSkipped, setAppliedSkipped] = useState([]);
  const attestationRef = useRef(null);
  const [negativeModalRows, setNegativeModalRows] = useState([]);   // working copy in the modal
  // Cleared negatives carry their own {item_id, item_name, unit} so they render
  // and commit even when the item is outside the counted categories.
  const [negativeClearanceItems, setNegativeClearanceItems] = useState([]);
  const [negChecking, setNegChecking] = useState(false);
  // PIECE GUARD read-back (utils/piecePackReadback.js): `pcs` counts said back as
  // packets, from the Stock In answer, just before the attestation. null = not showing.
  const [piecePackReview, setPiecePackReview] = useState(null);
  const [pieceChecking, setPieceChecking] = useState(false);
  const pieceCheckingRef = useRef(false);
  // Saved-draft resume banner
  const [resumeDraft, setResumeDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftToast, setDraftToast] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showClearSecondConfirm, setShowClearSecondConfirm] = useState(false);
  const [clearToast, setClearToast] = useState(false);
  // Redo — restore the most recently deleted draft.
  const [redoInfo, setRedoInfo] = useState(null);        // { closing_date, payload } | null
  const [showRedoConfirm, setShowRedoConfirm] = useState(false);
  const [redoBusy, setRedoBusy] = useState(false);
  const [removeItemConfirm, setRemoveItemConfirm] = useState(null);
  const [categoryGuideItem, setCategoryGuideItem] = useState(null);
  const lastValidQuantityRef = useRef({});
  const hydratingRef = useRef(false);
  // Monotonic counter that records the order items are placed from a scan, so
  // the consolidated sheet can be shown in the same order they appear on paper.
  // First placement wins (a re-scan of the same item keeps its original slot);
  // it survives across "Add More" scans because the ref persists.
  const ocrOrderRef = useRef(0);
  // Item 7 — keyboard flow (qty → price → next qty)
  const inputRefs = useRef({});
  // Item 2 — where to continue after the guided price fill ('verify' | 'commit')
  const priceGuideResumeRef = useRef('commit');

  const toggleGuideExpand = useCallback((key) => {
    setExpandedGuideKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const [showCornerMenu, setShowCornerMenu] = useState(false);
  const [downloading, setDownloading] = useState(false); // download spinner overlay
  const [showOCRReview, setShowOCRReview] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrImagePreview, setOcrImagePreview] = useState(null);
  const [ocrImagePreviews, setOcrImagePreviews] = useState([]);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Mobile browsers ignore `multiple` when `capture` is set, so the camera and
  // the gallery need separate inputs and a small chooser between them.
  const ocrCameraInputRef = useRef(null);
  const [showScanSourceChooser, setShowScanSourceChooser] = useState(false);
  const [showScanSourceMenu, setShowScanSourceMenu] = useState(false);
  const [showScanModeChooser, setShowScanModeChooser] = useState(false);
  const [scanCategoryMode, setScanCategoryMode] = useState('master'); // 'master' | 'normal'
  const [isDragging, setIsDragging] = useState(false);
  const [cameFromSummary, setCameFromSummary] = useState(false);
  const [showAddMoreChooser, setShowAddMoreChooser] = useState(false);
  const [summarySearchTerm, setSummarySearchTerm] = useState('');
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const ocrFileInputRef = useRef(null);
  const sheetFileInputRef = useRef(null);
  const pinRefs = useRef([]);
  const validateTimerRef = useRef({});
  const menuRef = useRef(null);
  const addMoreRef = useRef(null);

  // Only a touch device has a camera worth offering; a laptop gets the file
  // options only.
  const isTouchDevice = useMemo(() => typeof window !== 'undefined'
    && (('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0), []);

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState(null);
  const savedStateRef = useRef({ entries: {}, prepComponentPrices: {}, ingredientPrices: {} });
  // The updated_at we last saw for this draft — sent back on save so the server
  // can reject a write that would clobber a newer copy from another device.
  const draftUpdatedAtRef = useRef(null);

  const checkIsDirty = useCallback(() => {
    if (selectedFiles && selectedFiles.length > 0) return true;
    const saved = savedStateRef.current || { entries: {}, prepComponentPrices: {}, ingredientPrices: {} };
    if (JSON.stringify(entries || {}) !== JSON.stringify(saved.entries || {})) return true;
    if (JSON.stringify(prepComponentPrices || {}) !== JSON.stringify(saved.prepComponentPrices || {})) return true;
    if (JSON.stringify(ingredientPrices || {}) !== JSON.stringify(saved.ingredientPrices || {})) return true;
    return false;
  }, [entries, prepComponentPrices, ingredientPrices, selectedFiles]);

  // ── Calculated values (hoisted for early availability) ───────
  const enteredCount = useMemo(() => {
    return Object.values(entries || {}).filter(e => e.quantity !== undefined && e.quantity !== '' && Number(e.quantity) >= 0).length;
  }, [entries]);

  // Counts sitting on a preparation's placeholder twin. They are never part of
  // the count (the twin is in no item list, so getAllItems skips it, and /apply
  // refuses it); this only says so, and offers to clear them.
  const twinCounts = useMemo(() => placeholderTwins
    .map(t => ({ ...t, key: eKey('raw', t.item_id), entry: entries[eKey('raw', t.item_id)] }))
    .filter(t => csHasCount(t.entry?.quantity) && Number(t.entry.quantity) > 0),
  [placeholderTwins, entries]);
  const removeTwinCounts = useCallback(() => {
    const keys = new Set(twinCounts.map(t => t.key));
    setEntries(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.has(k))));
  }, [twinCounts]);

  // `releaseLock` is only true when the user is actually LEAVING. Parking a
  // draft while the sheet stays open used to release the Stock-In lock (and
  // every held invoice) with the commit button still live one click away — a
  // stock-in could land and then be zeroed by Confirm & Sync.
  const saveDraftPayload = async ({
    releaseLock = false,
    overrideEntries = null,
    overridePrepComponentPrices = null,
    overrideIngredientPrices = null,
    overrideNegativeClearanceItems = null,
    overrideSelectedGroupIds = null
  } = {}) => {
    const activeEntries = overrideEntries !== null ? overrideEntries : entries;
    const activePrepPrices = overridePrepComponentPrices !== null ? overridePrepComponentPrices : prepComponentPrices;
    const activeIngPrices = overrideIngredientPrices !== null ? overrideIngredientPrices : ingredientPrices;
    const activeNegItems = overrideNegativeClearanceItems !== null ? overrideNegativeClearanceItems : negativeClearanceItems;
    const activeGroupIds = overrideSelectedGroupIds !== null ? overrideSelectedGroupIds : selectedGroupIds;

    const payload = {
      schema_version: 1,
      manual: true,
      // The date this count BELONGS to, inside the payload. Without it a draft
      // saved at 23:50 is unreachable at 00:10 and silently becomes a
      // backdated entry for the next day if it is ever restored.
      closing_date: closingDate,
      // The day the shelves were counted ("Counted on a later day"), so a resumed
      // draft rolls back exactly as it would have when it was saved.
      counted_date: countedDate,
      saved_at: new Date().toISOString(),
      entries: activeEntries,
      prepComponentPrices: activePrepPrices,
      ingredientPrices: activeIngPrices,
      selectedGroupIds: activeGroupIds,
      negativeClearanceItems: activeNegItems
    };
    const res = await http.put('/closing-stock/draft', {
      businessId, closingDate, payload, baseUpdatedAt: draftUpdatedAtRef.current || null
    });
    if (res && res.success === false && res.code === 'DRAFT_CONFLICT') {
      alert('This count was changed on another device. Reload the page to see the newer version before saving again.');
      return false;
    }
    draftUpdatedAtRef.current = res?.updated_at || null;

    if (releaseLock) {
      http.post('/closing-stock/toggle-lock', { businessId, enabled: false }).catch(() => {});
    }
    // Prime the banner so it is ready without a refetch if they return to the
    // overview this session; the full payload lets Continue rehydrate.
    setResumeDraft({ payload, created_at: new Date().toISOString(), expires_in_days: DRAFT_TTL_DAYS });
    savedStateRef.current = {
      entries: activeEntries || {},
      prepComponentPrices: activePrepPrices || {},
      ingredientPrices: activeIngPrices || {}
    };
    return true;
  };

  const handleSaveDraftAndExit = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      let activeIngPrices = ingredientPrices;
      let activeEntries = entries;
      // Prices typed into the popup count only while the popup is OPEN — its
      // values outlive it, and re-applying them later would overwrite a price
      // changed since.
      if (showPriceGuide) {
        const fills = guideFills(priceGuideItems, priceGuideValues);
        const su = (id, fallback) => sheetUnitOfRef.current(id, fallback);
        activeIngPrices = storeIngredientPrices(ingredientPrices, fills, su);
        activeEntries = syncRowPrices(entries, fills, su);
      }
      // Leaving the sheet → it is safe to let Stock-In through again.
      await saveDraftPayload({
        releaseLock: true,
        overrideEntries: activeEntries,
        overrideIngredientPrices: activeIngPrices
      });
      setShowExitConfirm(false);
      setShowPriceGuide(false);
      setShowNegativeModal(false);
      if (pendingExitAction) pendingExitAction();
      setPendingExitAction(null);
    } catch (e) {
      alert('Could not save draft: ' + (e.message || 'unknown error'));
    } finally {
      setSavingDraft(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if ((checkIsDirty() || enteredCount > 0) && stockStep !== 'overview' && stockStep !== 'done') {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Would you like to save them as a draft before leaving?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [checkIsDirty, enteredCount, stockStep]);

  const businessId = UnitMappingService.getBusinessId() || localStorage.getItem('businessId');
  // The count is rebuilt for the chosen date ONLY when the shelves were counted on a
  // later day. A month-end count typed up days later needs no rebuilding: its numbers
  // already are the chosen date's stock.
  const rollbackRequested = countedDate > closingDate;

  // Disable footer navigation when any overlay/modal is open
  useEffect(() => {
    const isModalOpen = !!(
      downloading ||
      prepPriceDialog ||
      showSelectionChecklist ||
      stockStep === 'verify' ||
      showBackdateConfirm ||
      showNotCountedWarning ||
      showPriceGuide ||
      showNegativeModal ||
      showExitConfirm ||
      (priceGuideItems && priceGuideItems.length > 0) ||
      showPinModal ||
      showClearConfirm ||
      showClearSecondConfirm ||
      removeItemConfirm ||
      showScanModeChooser ||
      showScanSourceChooser ||
      showFilePreview ||
      expandedImage ||
      ocrProcessing ||
      categoryGuideItem
    );

    if (isModalOpen) {
      document.body.classList.add('modal-open-footer-disabled');
    } else {
      document.body.classList.remove('modal-open-footer-disabled');
    }

    return () => {
      document.body.classList.remove('modal-open-footer-disabled');
    };
  }, [
    downloading,
    prepPriceDialog,
    showSelectionChecklist,
    stockStep,
    showBackdateConfirm,
    showNotCountedWarning,
    showPriceGuide,
    showNegativeModal,
    showExitConfirm,
    priceGuideItems,
    showPinModal,
    showClearConfirm,
    showClearSecondConfirm,
    removeItemConfirm,
    showScanModeChooser,
    showScanSourceChooser,
    showFilePreview,
    expandedImage,
    ocrProcessing,
    categoryGuideItem
  ]);

  const isSheetClear = useMemo(() => {
    const hasEntries = Object.values(entries).some(
      entry => entry && entry.quantity !== undefined && entry.quantity !== null && entry.quantity !== ''
    );
    const hasPrepComponentPrices = Object.keys(prepComponentPrices).length > 0 || Object.keys(ingredientPrices).length > 0;
    const hasResumeDraft = resumeDraft !== null;
    return !hasEntries && !hasPrepComponentPrices && !hasResumeDraft;
  }, [entries, prepComponentPrices, ingredientPrices, resumeDraft]);

  const handleHeaderBack = () => {
    if (stockStep === 'entry' && cameFromSummary) {
      setCameFromSummary(false);
      setStockStep('summary');
    } else if (stockStep === 'entry' || stockStep === 'summary' || stockStep === 'verify') {
      const exitAction = () => {
        setStockStep('overview');
        setShowSelectionChecklist(false);
      };
      if (checkIsDirty() || enteredCount > 0) {
        setPendingExitAction(() => exitAction);
        setShowExitConfirm(true);
      } else {
        exitAction();
      }
    } else {
      if ((checkIsDirty() || enteredCount > 0) && stockStep !== 'done') {
        const exitAction = () => {
          goTo(isOnboarding ? 'inventory' : 'settings');
        };
        setPendingExitAction(() => exitAction);
        setShowExitConfirm(true);
      } else {
        goTo(isOnboarding ? 'inventory' : 'settings');
      }
    }
  };

  // ── Load Items (new API) ─────────────────────────────────────
  const loadShelvesRef = useRef(null);
  // Spec §10.2/§10.4: while an onboarding Sales Name Check is in progress the
  // closing-stock routes answer 409 SALES_NAME_CHECK_IN_PROGRESS.
  const [blockedBySnc, setBlockedBySnc] = useState(false);
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await http.get(`/closing-stock/items-by-menu-category?businessId=${businessId}`);
      if (result.success) {
        setMasterCategories(result.master_categories || []);
        setUncategorizedIngredients(result.uncategorized_ingredients || []);
        setAllPreparations(result.all_preparations || []);
        setAllComplimentary(result.all_complimentary || []);
        setAllMenuItems(result.all_menu_items || []);
        setPlaceholderTwins(Array.isArray(result.placeholder_twins) ? result.placeholder_twins : []);

        // Auto-select all categories and expand
        const catIds = (result.master_categories || []).map(c => c.master_category_id);
        setSelectedGroupIds(catIds);
        setExpandedGroups(new Set(['all', ...catIds, 'uncategorized']));
      }
    } catch (error) {
      if (isSalesNameCheckBlock(error)) setBlockedBySnc(true);
      console.error('Failed to load items:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { loadItems(); }, [loadItems]);
  // Shelves are needed by the per-row category dropdown, not only by the
  // download popup, so they load with the page.
  useEffect(() => { loadShelvesRef.current && loadShelvesRef.current(); }, []);

  // Load the initial lock status from backend
  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;
    (async () => {
      try {
        const res = await http.get(`/closing-stock/lock-status?businessId=${businessId}`);
        if (!cancelled && res.success) {
          setLockActive(res.locked);
          setContinuous(!!res.continuous);
          setNotInRecipeEnabled(res.not_in_recipe_enabled === true);
          http.get('/ccs/status')
            .then((st) => { if (!cancelled) setCcsStaff(!!(st && st.you && st.you.is_invexis_admin)); })
            .catch(() => {});
        }
      } catch (e) {
        console.error('Failed to load closing stock lock status:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  // Not in Recipe check (spec 2026-10-27 §5.3), run after the Not in Menu check passes.
  // true = go on to what the check interrupted. Nothing is asked when the feature is off or the
  // count does not reach its month end. FAIL CLOSED: a thrown error or { success:false } shows
  // "Couldn't check items" with Retry — the count does not start. A response without
  // enabled: true is "nothing to review". /closing-stock/apply enforces it anyway.
  const checkNotInRecipeThen = useCallback(async (from) => {
    if (!notInRecipeEnabled || !reachesMonthEnd(closingDate)) return true;
    const r = await checkNotInRecipe(closingDate);
    if (!r.ok) { setNirGate({ error: r.error, from }); return false; }
    if (r.enabled === true && r.blocking === true) {
      setNirFrom(from);
      setNirOpen(true);
      return false;
    }
    return true;
  }, [notInRecipeEnabled, closingDate]);

  // Drafts are now saved ONLY by the explicit "Save Draft" button on the
  // Consolidated Sheet (see saveDraftFromSummary). The old debounced auto-save
  // was removed — a draft exists only when the user chooses to keep one.

  // ── Item 6 — on open or returning to overview, surface a resumable draft (≤5 days old) ──
  const checkDraft = useCallback(async () => {
    if (!businessId || !dateChosen) return;
    try {
      // The draft of the CHOSEN date only (`exact`): the calendar already offered the
      // latest draft of any other date, so the old midnight-rollover fallback would
      // only show a different day's count under this one.
      const res = await http.get(`/closing-stock/draft?businessId=${businessId}&date=${closingDate}&exact=true`);
      if (res.success && res.draft && res.draft.payload && Object.keys(res.draft.payload.entries || {}).length > 0) {
        setResumeDraft({ ...res.draft, fellBack: !!res.fellBack });
        draftUpdatedAtRef.current = res.draft.updated_at || null;
      } else if (res.success) {
        setResumeDraft(null);
      }
    } catch (e) {
      if (isSalesNameCheckBlock(e)) setBlockedBySnc(true); // anything else: no draft
    }
  }, [businessId, closingDate, dateChosen]);

  // ── Redo — is there a deleted draft to restore? (defined above the overview
  // effect that depends on it, to avoid a temporal-dead-zone crash.)
  const checkRedo = useCallback(async () => {
    if (!businessId) { setRedoInfo(null); return; }
    try {
      const res = await http.get(`/closing-stock/draft/trash?businessId=${businessId}`);
      setRedoInfo(res.success && res.trash ? res.trash : null);
    } catch (e) { setRedoInfo(null); }
  }, [businessId]);

  useEffect(() => {
    if (stockStep === 'overview') {
      checkDraft();
      checkRedo();
    }
  }, [stockStep, checkDraft, checkRedo]);

  // Rehydrate a draft payload into the sheet and open it (Continue / Redo both use this).
  const hydrateFromPayload = useCallback((p) => {
    if (!p) return;
    hydratingRef.current = true;
    attestationRef.current = null;   // a resumed draft must be attested afresh
    // Adopt the date the draft BELONGS to. Without this, a draft written
    // yesterday at 23:50 is applied as today's count — which silently turns it
    // into a backdated entry and runs the sales/delivery adjustment.
    if (p.closing_date) {
      setClosingDate(p.closing_date);
      setCountedDate(p.counted_date && p.counted_date > p.closing_date ? p.counted_date : p.closing_date);
      setDateChosen(true);
      setShowDatePicker(false);
    }
    if (p.entries) setEntries(p.entries);
    if (p.prepComponentPrices) setPrepComponentPrices(p.prepComponentPrices);
    if (p.ingredientPrices) setIngredientPrices(p.ingredientPrices);
    if (Array.isArray(p.negativeClearanceItems)) setNegativeClearanceItems(p.negativeClearanceItems);
    if (Array.isArray(p.selectedGroupIds) && p.selectedGroupIds.length) setSelectedGroupIds(p.selectedGroupIds);
    savedStateRef.current = {
      entries: p.entries || {},
      prepComponentPrices: p.prepComponentPrices || {},
      ingredientPrices: p.ingredientPrices || {}
    };
    // A manual draft was saved FROM the Consolidated Sheet, so it lands there —
    // the summary rebuilds from the hydrated entries on the next render.
    // (Legacy auto-saved drafts, mid-count, resume to the entry list instead.)
    setStockStep(p.manual ? 'summary' : 'entry');
    setLockActive(true);
    setActiveTab('all');
    setResumeDraft(null);
    // Resuming continues the count → keep Stock-In blocked.
    http.post('/closing-stock/toggle-lock', { businessId, enabled: true }).catch(() => {});
    setTimeout(() => { hydratingRef.current = false; }, 150);
  }, [businessId]);

  const resumeFromDraft = useCallback(() => {
    hydrateFromPayload(resumeDraft?.payload);
  }, [resumeDraft, hydrateFromPayload]);

  const restoreRedo = useCallback(async () => {
    if (redoBusy) return;
    setRedoBusy(true);
    try {
      const res = await http.post('/closing-stock/draft/restore', { businessId });
      if (res.success && res.draft) {
        setShowRedoConfirm(false);
        setRedoInfo(null);
        hydrateFromPayload(res.draft.payload);   // brings the draft back on the Consolidated Sheet
      } else {
        alert(res.error || 'There is no deleted draft to restore.');
      }
    } catch (e) {
      alert('Could not restore the draft: ' + (e.message || 'unknown error'));
    } finally {
      setRedoBusy(false);
    }
  }, [redoBusy, businessId, hydrateFromPayload]);

  const discardDraft = useCallback(async () => {
    // The draft on screen is the chosen date's — deleting "today's" removed the wrong one
    // whenever the two differed.
    try { await http.delete('/closing-stock/draft', { businessId, closingDate }); } catch (e) { /* ignore */ }
    setResumeDraft(null);
    // Dismissed draft goes to the trash → Redo can bring it back.
    checkRedo();
  }, [businessId, closingDate, checkRedo]);

  // ── Clear Sheet ──────────────────────────────────────────────
  // Wipes everything: deletes ALL saved drafts for this business (server) and
  // resets every value entered on the sheet (in-memory). Destructive, so it is
  // gated behind a confirm. Leaves the item list intact — only the counts go —
  // and drops back to the entry step if a later step was open.
  const clearSheet = useCallback(async () => {
    setShowClearConfirm(false);
    setShowClearSecondConfirm(false);
    try {
      await http.delete('/closing-stock/draft', { businessId, all: true });
    } catch (e) { /* best-effort — still clear the in-memory sheet */ }
    setEntries({});
    setPrepComponentPrices({});
    setIngredientPrices({});
    setFlags({});
    setNegativeClearanceItems([]);
    attestationRef.current = null;
    setResumeDraft(null);
    setCameFromSummary(false);
    setNotCountedAcknowledged(false);
    setVerificationAccepted(false);
    savedStateRef.current = { entries: {}, prepComponentPrices: {}, ingredientPrices: {} };
    setStockStep(prev => (prev === 'summary' || prev === 'verify') ? 'entry' : prev);
    setClearToast(true);
    setTimeout(() => setClearToast(false), 2400);
    // The just-cleared draft is now in the trash → Redo becomes available.
    checkRedo();
  }, [businessId, checkRedo]);

  // ── Save Draft (explicit, from the Consolidated Sheet) ───────
  // Persists the whole in-progress count so it can be resumed later. Parking a
  // count means it is no longer actively in progress, so the Stock-In block is
  // released; Continue re-blocks it. Stays on the consolidated page (a toast
  // confirms) — the "Draft saved · continue?" banner appears whenever the user
  // next lands on the overview, in this session or a later one.
  const saveDraftFromSummary = useCallback(async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      await saveDraftPayload();
      setDraftToast(true);
      setTimeout(() => setDraftToast(false), 2600);
    } catch (e) {
      alert('Could not save draft: ' + (e.message || 'unknown error'));
    } finally {
      setSavingDraft(false);
    }
  }, [savingDraft, entries, prepComponentPrices, ingredientPrices, selectedGroupIds, negativeClearanceItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowCornerMenu(false);
      }
      if (addMoreRef.current && !addMoreRef.current.contains(event.target)) {
        setShowAddMoreChooser(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ── Get items/preps for the active tab ───────────────────────
  const getTabIngredients = useCallback((tabId) => {
    if (tabId === 'all') {
      const allIngredients = [];
      const seenIds = new Set();
      for (const cat of masterCategories) {
        if (!selectedGroupIds.includes(cat.master_category_id)) continue;
        for (const ing of cat.ingredients) {
          if (!seenIds.has(ing.item_id)) {
            seenIds.add(ing.item_id);
            allIngredients.push(ing);
          }
        }
      }
      for (const ing of uncategorizedIngredients) {
        if (!seenIds.has(ing.item_id)) {
          seenIds.add(ing.item_id);
          allIngredients.push(ing);
        }
      }
      return allIngredients;
    }
    if (tabId === 'uncategorized') return uncategorizedIngredients;
    const cat = masterCategories.find(c => c.master_category_id === tabId);
    return cat ? cat.ingredients : [];
  }, [masterCategories, uncategorizedIngredients, selectedGroupIds]);

  const getTabPreparations = useCallback((tabId) => {
    if (tabId === 'all') {
      return allPreparations;
    }

    // Find preps assigned to specific categories
    const assignedPrepIds = new Set();
    masterCategories.forEach(c => {
      c.preparations.forEach(p => assignedPrepIds.add(p.item_id));
    });

    if (tabId === 'uncategorized') {
      // Orphaned preps (not assigned to any category) go here
      return allPreparations.filter(p => !assignedPrepIds.has(p.item_id));
    }

    const cat = masterCategories.find(c => c.master_category_id === tabId);
    return cat ? cat.preparations : [];
  }, [masterCategories, allPreparations]);

  // Complimentary recipes for a tab. Mirrors getTabPreparations: a recipe is
  // placed by the dish it is served with, and anything unlinked falls into
  // "Other" so it is never unreachable.
  const getTabComplimentary = useCallback((tabId) => {
    if (tabId === 'all') return allComplimentary;

    const assignedIds = new Set();
    masterCategories.forEach(c => {
      (c.complimentary || []).forEach(x => assignedIds.add(x.item_id));
    });

    if (tabId === 'uncategorized') {
      return allComplimentary.filter(x => !assignedIds.has(x.item_id));
    }

    const cat = masterCategories.find(c => c.master_category_id === tabId);
    return cat ? (cat.complimentary || []) : [];
  }, [masterCategories, allComplimentary]);

  // Menu items (dishes) for a tab. A dish has an explicit category chain, so
  // unlike preparations there is no name-matching — anything without a mapped
  // category falls into "Other" so it is never unreachable.
  const getTabMenuItems = useCallback((tabId) => {
    if (tabId === 'all') return allMenuItems;

    const assignedIds = new Set();
    masterCategories.forEach(c => {
      (c.menu_items || []).forEach(x => assignedIds.add(x.item_id));
    });

    if (tabId === 'uncategorized') {
      return allMenuItems.filter(x => !assignedIds.has(x.item_id));
    }

    const cat = masterCategories.find(c => c.master_category_id === tabId);
    return cat ? (cat.menu_items || []) : [];
  }, [masterCategories, allMenuItems]);

  // ── Entry Handlers (with composite key) ─────────────────────
  const updateEntry = useCallback((compositeKey, field, value) => {
    setEntries(prev => ({
      ...prev,
      [compositeKey]: { ...(prev[compositeKey] || {}), [field]: value }
    }));
  }, []);

  const handleQuantityBlur = useCallback((compositeKey, value, unit, itemName) => {
    if (value === '' || value === null || value === undefined) {
      const prev = lastValidQuantityRef.current[compositeKey];
      if (prev !== undefined && prev !== '') {
        setRemoveItemConfirm({ key: compositeKey, itemName: itemName || 'this item', previousQuantity: prev });
      }
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      updateEntry(compositeKey, 'quantity', parseFloat(parsed.toFixed(4)).toString());
    }
  }, [updateEntry]);

  const handleCancelRemoveItem = useCallback(() => {
    if (removeItemConfirm) {
      const { key, previousQuantity } = removeItemConfirm;
      if (previousQuantity !== undefined && previousQuantity !== '') {
        setEntries(prev => ({
          ...prev,
          [key]: { ...(prev[key] || {}), quantity: previousQuantity }
        }));
      }
    }
    setRemoveItemConfirm(null);
  }, [removeItemConfirm]);

  const handleConfirmRemoveItem = useCallback(() => {
    if (removeItemConfirm) {
      const { key } = removeItemConfirm;
      setEntries(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    setRemoveItemConfirm(null);
  }, [removeItemConfirm]);

  const handleQuantityChange = useCallback((compositeKey, itemId, value, unit) => {
    updateEntry(compositeKey, 'quantity', value);

    if (validateTimerRef.current[compositeKey]) {
      clearTimeout(validateTimerRef.current[compositeKey]);
    }

    const numVal = Number(value);
    if (!Number.isFinite(numVal) || numVal <= 0) {
      setFlags(prev => { const n = { ...prev }; delete n[itemId]; return n; });
      return;
    }

    validateTimerRef.current[compositeKey] = setTimeout(async () => {
      try {
        const result = await http.post('/closing-stock/validate-entry', {
          businessId, item_id: itemId, quantity: numVal, unit
        });
        if (result.success && result.status !== 'ok') {
          setFlags(prev => ({ ...prev, [itemId]: { status: result.status, message: result.message } }));
        } else {
          setFlags(prev => { const n = { ...prev }; delete n[itemId]; return n; });
        }
      } catch (e) {
        // Silent fail on validation
      }
    }, 800);
  }, [businessId, updateEntry]);

  // ── Raw-ingredient quantity (Item 4 — one canonical value per item) ──
  // Every category tab writes the SAME `raw_<itemId>` entry, so counting an
  // item under two categories never doubles it. `sourceTab`/`sourceTabName`
  // records where it was entered so other tabs can show "Already counted".
  const handleRawQuantityChange = useCallback((itemId, value, unit, tabId, tabName) => {
    const key = eKey('raw', itemId);
    setEntries(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), quantity: value, sourceTab: tabId, sourceTabName: tabName }
    }));

    if (validateTimerRef.current[key]) clearTimeout(validateTimerRef.current[key]);
    const numVal = Number(value);
    if (!Number.isFinite(numVal) || numVal <= 0) {
      setFlags(prev => { const n = { ...prev }; delete n[itemId]; return n; });
      return;
    }
    validateTimerRef.current[key] = setTimeout(async () => {
      try {
        const result = await http.post('/closing-stock/validate-entry', {
          businessId, item_id: itemId, quantity: numVal, unit
        });
        if (result.success && result.status !== 'ok') {
          setFlags(prev => ({ ...prev, [itemId]: { status: result.status, message: result.message } }));
        } else {
          setFlags(prev => { const n = { ...prev }; delete n[itemId]; return n; });
        }
      } catch (e) {
        // Silent fail on validation
      }
    }, 800);
  }, [businessId]);

  // Item 3 — explicit "Sold out" sets an explicit 0 (Counted), distinct from
  // a blank field (Not counted → stock untouched).
  const markSoldOut = useCallback((itemId, unit, tabId, tabName) => {
    const key = eKey('raw', itemId);
    setEntries(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), quantity: '0', sourceTab: tabId, sourceTabName: tabName }
    }));
    if (validateTimerRef.current[key]) clearTimeout(validateTimerRef.current[key]);
    setFlags(prev => { const n = { ...prev }; delete n[itemId]; return n; });
  }, []);

  // ── Toggle Lock ─────────────────────────────────────────────
  const toggleLock = useCallback(async () => {
    const newState = !lockActive;
    try {
      await http.post('/closing-stock/toggle-lock', { businessId, enabled: newState });
      setLockActive(newState);
    } catch (e) {
      console.error('Failed to toggle lock:', e);
    }
  }, [businessId, lockActive]);

  // ── Toggle Group ────────────────────────────────────────────
  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // ── PIN Modal ───────────────────────────────────────────────
  const openPinModal = useCallback((itemId) => {
    setPinTarget(itemId);
    setPinValue(['', '', '', '']);
    setPinError('');
    setShowPinModal(true);
    setTimeout(() => pinRefs.current[0]?.focus(), 100);
  }, []);

  const handlePinInput = useCallback((index, value) => {
    if (!/^\d?$/.test(value)) return;
    setPinValue(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 3) {
      pinRefs.current[index + 1]?.focus();
    }
  }, []);

  const handlePinKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !pinValue[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  }, [pinValue]);

  const verifyPin = useCallback(async () => {
    const pin = pinValue.join('');
    if (pin.length !== 4) return;

    try {
      const result = await http.post('/closing-stock/verify-pin', { businessId, pin });
      if (result.success && result.valid) {
        setFlags(prev => { const n = { ...prev }; delete n[pinTarget]; return n; });
        setShowPinModal(false);
        setPinTarget(null);
        setPinAttempts(0);
      } else {
        setPinError('Incorrect PIN. Please try again.');
        setPinAttempts(prev => prev + 1);
        setPinValue(['', '', '', '']);
        pinRefs.current[0]?.focus();
      }
    } catch (e) {
      setPinError(e.message || 'Failed to verify PIN');
    }
  }, [businessId, pinTarget, pinValue]);

  // ── Download Handlers ───────────────────────────────────────
  const getApiBaseUrl = useCallback(() => {
    const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
    if (envBase) {
      const clean = envBase.replace(/\/+$/, '');
      return clean.endsWith('/api') ? clean : `${clean}/api`;
    }
    return '/api';
  }, []);

  // Download via fetch → blob so the backend URL is never opened/exposed in a
  // browser tab; the file arrives through a masked blob: URL instead.
  const serializeSheetRef = useRef(null);
  const downloadFile = useCallback(async (endpoint, filename, opts = {}) => {
    setDownloading(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (businessId) headers['X-Business-Id'] = String(businessId);
      // The live report POSTs the sheet: it is far past what a query string can
      // carry, and it is data, not addressing.
      const init = { headers };
      if (opts.method === 'POST') {
        init.method = 'POST';
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(opts.body || {});
      }
      const res = await fetch(`${getApiBaseUrl()}${endpoint}`, init);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // The SERVER names the file: only it knows the business name and the
      // closing date. Every download used to arrive as the caller's hardcoded
      // string, so three outlets' sheets landed as Template(1..3). The passed
      // `filename` is now only the fallback for when the header is absent —
      // which, on a cross-origin API, means Content-Disposition was not in
      // Access-Control-Expose-Headers.
      a.download = filenameFromContentDisposition(res.headers.get('Content-Disposition')) || filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (e) {
      alert('Download failed: ' + (e.message || 'Please try again.'));
    } finally {
      setDownloading(false);
    }
  }, [businessId, getApiBaseUrl]);

  // The sheet is grouped by STOCK category (Dairy, Oils, Meat…), so the download
  // chooser offers those — not the menu categories the entry tabs use, which
  // are about which station counts what.
  const [stockCategories, setStockCategories] = useState([]);
  const [downloadCatIds, setDownloadCatIds] = useState([]);      // [] = every shelf
  const [uncategorisedCount, setUncategorisedCount] = useState(0);

  // Restrict the sheet to the chosen STOCK categories — but only when a subset
  // (not all) is picked; "All" downloads every shelf.
  // Which question the download is answering. 'all' = the consolidated sheet
  // (shelves); 'menu' = one station's recipe-chain sheet. They are different ID
  // spaces — MasterMenuCategories vs InventoryCategories — and the server
  // refuses both at once, so exactly one of the two params is ever sent.
  const [downloadScope, setDownloadScope] = useState('all');
  const [downloadMenuCatId, setDownloadMenuCatId] = useState(null);
  // "One menu category" with no station picked sends no scope at all, and the
  // server answers that with the whole-store sheet — so nothing may download
  // until a station is chosen.
  const needsMenuCategory = downloadScope === 'menu' && !downloadMenuCatId;
  // The Excel workbook's kitchen tabs: null = every master category gets a tab.
  const [downloadKitchenIds, setDownloadKitchenIds] = useState(null);

  const selectedCategoryParam = useCallback(() => {
    if (downloadScope === 'menu') {
      return downloadMenuCatId ? `&menuCategoryIds=${downloadMenuCatId}` : '';
    }
    const total = stockCategories.filter(c => c.consumable).length;
    const allSelected = downloadCatIds.length === 0 || (total > 0 && downloadCatIds.length >= total);
    return (!allSelected && downloadCatIds.length > 0)
      ? `&stockCategoryIds=${downloadCatIds.join(',')}` : '';
  }, [stockCategories, downloadCatIds, downloadScope, downloadMenuCatId]);

  const handleDownloadTemplate = useCallback((format, withRecipes = true, priceColumn = false) => {
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    // Only the Excel workbook has kitchen tabs; absent = every kitchen.
    const kitchenParam = (format !== 'pdf' && downloadScope === 'all' && downloadKitchenIds !== null)
      ? `&kitchenIds=${downloadKitchenIds.length ? downloadKitchenIds.join(',') : 'none'}` : '';
    const endpoint = `/closing-stock/download/template?businessId=${businessId}&format=${format}&withRecipes=${withRecipes}&priceColumn=${priceColumn}${selectedCategoryParam()}${kitchenParam}`;
    downloadFile(endpoint, `Closing_Stock_Template.${ext}`);
  }, [businessId, downloadFile, selectedCategoryParam, downloadScope, downloadKitchenIds]);

  const handleDownloadReport = useCallback((format, withRecipes = true) => {
    const password = prompt('Enter password to protect the report (or leave blank for no protection):');
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    const name = `Closing_Stock_Report_${closingDate}.${ext}`;
    // A sheet is open ⇒ report THIS sheet, which exists nowhere in the database
    // yet. Otherwise report the confirmed count for the date (the server 409s if
    // there is none, rather than printing live stock under that date).
    // Reached through a ref: the serializer needs summaryRows and is therefore
    // defined far below this handler, so naming it directly here would be a
    // temporal-dead-zone crash on first render.
    const rows = stockStep === 'summary' && serializeSheetRef.current ? serializeSheetRef.current() : null;
    if (rows && rows.length > 0) {
      downloadFile('/closing-stock/download/report-live', name, {
        method: 'POST',
        body: { businessId, format, closingDate, withRecipes, password: password || null, rows },
      });
      return;
    }
    const pwParam = password ? `&password=${encodeURIComponent(password)}` : '';
    const endpoint = `/closing-stock/download/report?businessId=${businessId}&format=${format}&date=${closingDate}&withRecipes=${withRecipes}${pwParam}${selectedCategoryParam()}`;
    downloadFile(endpoint, name);
  }, [businessId, closingDate, downloadFile, selectedCategoryParam, stockStep]);

  // Downloads first ask With Recipes / Without Recipes (centered modal below).
  const [recipeChoice, setRecipeChoice] = useState(null); // { kind:'template'|'report', format:'pdf'|'xlsx' }
  // "Include price column" — ON until the owner turns it off, then remembered.
  const [includePriceCol, setIncludePriceCol] = useState(() => readPriceColPref(businessId));
  const chooseIncludePriceCol = useCallback((next) => {
    setIncludePriceCol(next);
    try { localStorage.setItem(pricePrefKey(businessId), String(next)); } catch (e) { /* storage blocked */ }
  }, [businessId]);
  // ── Which shelf an item sits on, editable from the row ────────────────────
  //
  // The count sheet is grouped by InventoryItems.category_id, and until now
  // nothing in the app could change it: a mis-filed item stayed mis-filed, and a
  // business whose items were never categorised printed the whole sheet as one
  // "Other / Uncategorized" dump with no way out. The classifier's fallback puts
  // anything it cannot place into Vegetables, so this is not a rare case.
  const [itemCategories, setItemCategories] = useState({});   // item_id -> category_id
  const [savingCategoryFor, setSavingCategoryFor] = useState(null);

  const changeItemCategory = useCallback(async (itemId, categoryId) => {
    const next = categoryId === '' ? null : Number(categoryId);
    setSavingCategoryFor(itemId);
    try {
      const res = await http.put(`/inventory/items/${itemId}/category`, { category_id: next });
      if (!res || res.success === false) throw new Error((res && res.error) || 'Could not move the item');
      setItemCategories(prev => ({ ...prev, [itemId]: next }));
      // The sheet's sections come from the server's grouping, so it has to be
      // re-read for the row to appear under its new shelf.
      loadItems();
    } catch (e) {
      alert(`Could not change the category: ${e.message || 'unknown error'}`);
    } finally {
      setSavingCategoryFor(null);
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadShelves = useCallback(() => {
    http.get(`/closing-stock/inventory-categories?businessId=${businessId}`)
      .then(res => {
        const cats = (res && res.categories) || [];
        setStockCategories(cats);
        setDownloadCatIds([]);   // default: every shelf
        setUncategorisedCount(Number(res?.uncategorised_count) || 0);
      })
      .catch(() => { setStockCategories([]); setDownloadCatIds([]); });
  }, [businessId]);

  loadShelvesRef.current = loadShelves;

  const openDownloadChoice = useCallback((kind, format) => {
    setShowCornerMenu(false);
    setRecipeChoice({ kind, format });
    setDownloadScope('all');
    setDownloadMenuCatId(null);
    setDownloadKitchenIds(null);
    // Shelves are only meaningful for the consolidated sheet, so they are only
    // fetched for it (loadShelves runs again if the user switches back).
    loadShelves();
    // No server round-trip for this any more: the last answer is the default,
    // and re-reading it here keeps a choice made in another tab honoured.
    if (kind === 'template') setIncludePriceCol(readPriceColPref(businessId));
  }, [businessId, loadShelves]);
  const runDownloadChoice = useCallback((withRecipes) => {
    if (needsMenuCategory) return;
    const choice = recipeChoice;
    setRecipeChoice(null);
    if (!choice) return;
    if (choice.kind === 'template') handleDownloadTemplate(choice.format, withRecipes, includePriceCol);
    else handleDownloadReport(choice.format, withRecipes);
  }, [recipeChoice, includePriceCol, handleDownloadTemplate, handleDownloadReport, needsMenuCategory]);

  // ── OCR Handlers ────────────────────────────────────────────
  const openScanModeChooser = useCallback(() => {
    setShowCornerMenu(false);
    setShowScanSourceMenu(false);
    setShowScanModeChooser(true);
  }, []);

  const startScanFlow = useCallback((mode) => {
    setScanCategoryMode(mode);
    setShowScanModeChooser(false);
    setShowScanSourceMenu(false);
    setShowScanSourceChooser(true);
  }, []);

  // The chooser is worth showing everywhere now: a desktop user has no camera
  // but does have the filled-template upload, which is a different pipeline.
  const openScanPicker = useCallback(() => {
    setShowScanSourceChooser(true);
  }, []);

  // Adds to the current selection rather than replacing it, so "Add" in the
  // preview modal and the camera both accumulate toward one scan.
  const handleOCRFileSelect = useCallback(async (e) => {
    const picked = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (picked.length === 0) return;

    // Catch the wrong file type here rather than letting one bad file 400 the
    // whole batch server-side.
    const rejected = picked.filter(f => !isImageFile(f));
    const images = picked.filter(isImageFile);
    if (rejected.length > 0) {
      alert(rejected.length === picked.length
        ? `Photos only here — "${rejected[0].name}" is not an image.\n\nIf that is a filled count sheet, use "Upload Filled Sheet" instead.`
        : `Skipped ${rejected.length} file${rejected.length > 1 ? 's' : ''} that ${rejected.length > 1 ? 'are' : 'is'} not a photo: ${rejected.map(f => f.name).join(', ')}`);
    }
    if (images.length === 0) return;

    setSelectedFiles(prev => {
      const room = MAX_OCR_PAGES - prev.length;
      if (room <= 0) {
        alert(`You can scan up to ${MAX_OCR_PAGES} pages at a time.`);
        return prev;
      }
      const accepted = images.slice(0, room);
      if (images.length > room) {
        alert(`Only ${room} more page${room === 1 ? '' : 's'} can be added — up to ${MAX_OCR_PAGES} per scan.`);
      }
      setOcrImagePreviews(prevUrls => {
        const next = [...prevUrls, ...accepted.map(f => URL.createObjectURL(f))];
        setOcrImagePreview(next[0] || null);
        return next;
      });
      return [...prev, ...accepted];
    });
    setCurrentImageIndex(0);
    setShowFilePreview(true);
  }, []);

  const handleRemoveFile = useCallback((index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = ocrImagePreviews.filter((_, i) => i !== index);
    if (ocrImagePreviews[index] && ocrImagePreviews[index].startsWith('blob:')) {
      URL.revokeObjectURL(ocrImagePreviews[index]);
    }
    setSelectedFiles(updatedFiles);
    setOcrImagePreviews(updatedPreviews);
    if (updatedFiles.length === 0) setShowFilePreview(false);
  }, [selectedFiles, ocrImagePreviews]);

  const handleClearAllFiles = useCallback(() => {
    ocrImagePreviews.forEach(preview => {
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    });
    setSelectedFiles([]);
    setOcrImagePreviews([]);
    setOcrImagePreview(null);
    setShowFilePreview(false);
    if (ocrFileInputRef.current) ocrFileInputRef.current.value = '';
  }, [ocrImagePreviews]);

  // A filled template needs no preview step and no confirmation: the numbers are
  // already typed text, so it goes straight from the drop to the review table.
  const handleSheetImport = useCallback(async (file) => {
    if (!file) return;
    setShowScanSourceChooser(false);
    setShowFilePreview(false);
    setOcrProcessing(true);
    setOcrProgress({ current: 0, total: 1, mode: 'sheet' });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('businessId', businessId);

      const result = await http.postFormData('/closing-stock/import/sheet', formData);
      if (!result.success) {
        alert(result.error || 'That sheet could not be read. Please upload the template downloaded from Closing Stock.');
        return;
      }

      // Cells the sheet could not turn into a number — named, so the user knows
      // exactly which rows to go back and fix.
      const bad = result.metadata?.cell_errors || [];
      if (bad.length > 0) {
        const shown = bad.slice(0, 8).map(c => `• ${c.name || c.code}: ${c.error}`);
        alert(`${result.metadata.items_with_values} value${result.metadata.items_with_values === 1 ? '' : 's'} imported.\n\n`
          + `${bad.length} cell${bad.length > 1 ? 's' : ''} could not be read and ${bad.length > 1 ? 'were' : 'was'} skipped:\n`
          + shown.join('\n')
          + (bad.length > shown.length ? `\n…and ${bad.length - shown.length} more` : '')
          + `\n\nYou can type those values in the review, or fix the sheet and upload it again.`);
      }

      // No images to peek at, so clear any left over from an earlier photo scan.
      ocrImagePreviews.forEach(p => { if (p && p.startsWith('blob:')) URL.revokeObjectURL(p); });
      setOcrImagePreviews([]);
      setOcrImagePreview(null);
      setSelectedFiles([]);
      setOcrResult(result);
      setShowOCRReview(true);
    } catch (error) {
      alert('Sheet import failed: ' + (error.message || 'Unknown error'));
    } finally {
      setOcrProcessing(false);
      setOcrProgress(null);
      if (sheetFileInputRef.current) sheetFileInputRef.current.value = '';
    }
  }, [businessId, ocrImagePreviews]);

  const handleSheetInputChange = useCallback((e) => {
    const file = (e.target.files || [])[0];
    if (e.target) e.target.value = '';
    if (file) handleSheetImport(file);
  }, [handleSheetImport]);

  // One entry point for anything dropped anywhere in the scan flow: a filled
  // sheet parses immediately, photos go to the preview step.
  const handleDroppedFiles = useCallback((files) => {
    const dropped = Array.from(files || []);
    if (dropped.length === 0) return;

    const sheet = dropped.find(isSheetFile);
    if (sheet) {
      if (dropped.length > 1) {
        alert(`Reading "${sheet.name}". A filled sheet is imported on its own — the other dropped file${dropped.length > 2 ? 's were' : ' was'} ignored.`);
      }
      handleSheetImport(sheet);
      return;
    }

    setShowScanSourceChooser(false);
    handleOCRFileSelect({ target: { files: dropped } });
  }, [handleSheetImport, handleOCRFileSelect]);

  const handleConfirmFiles = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    setShowFilePreview(false);
    setOcrProcessing(true);
    setOcrProgress({ current: 0, total: selectedFiles.length });

    try {
      // All pages go in ONE request: the backend builds the item list once and
      // reads the pages concurrently, so this costs about one page of waiting
      // instead of one per page. Merging/conflicts are resolved server-side.
      const formData = new FormData();
      for (const file of selectedFiles) formData.append('images', file);
      formData.append('businessId', businessId);

      const result = await http.postFormData('/closing-stock/ocr/process', formData);
      if (!result.success) {
        alert(result.error || 'OCR failed. Please try again.');
        return;
      }

      const failed = result.metadata?.failed_pages || [];
      if (failed.length > 0) {
        // Say WHY each page failed — a rate limit reads very differently from a
        // blurry photo, and the fix is different too.
        const why = {
          rate_limit: 'rate limited — wait a minute and retry',
          overloaded: 'the AI service is briefly overloaded — retry shortly',
          network: 'a network hiccup — retry',
          breaker_open: 'scanning is paused after repeated errors — retry in a couple of minutes',
          auth: 'a configuration problem — contact support',
          bad_image: 'the photo is too small or unreadable — retake it',
          parse: 'an unreadable response — retry'
        };
        const lines = failed.map(f => `Page ${f.page}: ${why[f.error_class] || f.error || 'could not be read'}`);
        alert(`${result.metadata.pages_read} of ${result.metadata.pages_submitted} pages were read.\n\n`
          + lines.join('\n')
          + `\n\nYou can add the failed page${failed.length > 1 ? 's' : ''} again after reviewing.`);
      }

      setOcrResult(result);
      setShowOCRReview(true);
    } catch (error) {
      alert('OCR processing failed: ' + (error.message || 'Unknown error'));
    } finally {
      setOcrProcessing(false);
      setOcrProgress(null);
      if (ocrFileInputRef.current) ocrFileInputRef.current.value = '';
      if (ocrCameraInputRef.current) ocrCameraInputRef.current.value = '';
    }
  }, [businessId, selectedFiles]);

  // Scanned values ADD to what is already on the sheet.
  //
  // A scan is "here is a batch of stock I counted", not "here is the final
  // number for this item" — so scanning the walk-in and then hitting Add More
  // for the dry store gives you the total of both. This is deliberately
  // different from two boxes for the same item WITHIN one scan, which is a
  // disagreement about a single measurement: the backend flags those as
  // conflicts and the review makes you pick one before it gets here.
  //
  // The review modal shows what is already counted on each row, so re-scanning
  // the same page by mistake is visible before it is applied.
  const handleOCRConfirm = useCallback(async (confirmedItems) => {
    // Route each scanned value to the SAME composite-key bucket the manual
    // entry flow uses, based on item_type. Raw ingredients and preparations
    // share overlapping numeric ids, so we must trust item_type (with a legacy
    // fallback to prep-id membership) — never the id alone.
    try {
    const prepIds = new Set(allPreparations.map(p => p.item_id));
    const compById = new Map(allComplimentary.map(c => [c.item_id, c]));

    // Lookup of raw-ingredient details (by id) so OCR entries can carry a unit
    // price, exactly like a manually-typed row defaults to its vendor price.
    const rawItemById = new Map();
    for (const cat of masterCategories) {
      for (const ing of (cat.ingredients || [])) {
        if (!rawItemById.has(ing.item_id)) rawItemById.set(ing.item_id, ing);
      }
    }
    for (const ing of uncategorizedIngredients) {
      if (!rawItemById.has(ing.item_id)) rawItemById.set(ing.item_id, ing);
    }

    const newEntries = { ...entries };
    const priceFills = [];
    let placedCount = 0;
    for (const item of confirmedItems) {
      // Complimentary recipes are counted in portions — no unit conversion,
      // the sheet and the app agree.
      if (item.item_type === 'complimentary') {
        const comp = compById.get(item.item_id);
        if (!comp) continue;
        const key = eKey('comp', item.item_id);
        const prevComp = newEntries[key] || {};
        newEntries[key] = { ...prevComp, quantity: String(addQty(prevComp.quantity, item.quantity)), _order: prevComp._order ?? ocrOrderRef.current++ };
        placedCount++;
        continue;
      }

      // Menu items are counted in plates — likewise no unit conversion. A dish
      // with no recipe is never printed on the sheet, so it cannot arrive here.
      if (item.item_type === 'menu') {
        const menu = allMenuItems.find(m => m.item_id === item.item_id);
        if (!menu || !menu.has_recipe) continue;
        const key = eKey('menu', item.item_id);
        const prevMenu = newEntries[key] || {};
        newEntries[key] = { ...prevMenu, quantity: String(addQty(prevMenu.quantity, item.quantity)), _order: prevMenu._order ?? ocrOrderRef.current++ };
        placedCount++;
        continue;
      }

      const isPrep = item.item_type
        ? item.item_type === 'preparation'
        : prepIds.has(item.item_id);

      if (isPrep) {
        // Preparations always live under the stable 'prep' prefix, regardless of tab.
        // The scanned qty is in the sheet's display unit (kg/L/pcs); convert it
        // back to the prep's standard unit so storage stays exact.
        const key = eKey('prep', item.item_id);
        const prep = allPreparations.find(p => p.item_id === item.item_id);
        const stdUnit = prep?.unit || item.unit;
        const stdQty = convertQty(Number(item.quantity), item.unit, stdUnit);
        const prevPrep = newEntries[key] || {};
        newEntries[key] = { ...prevPrep, quantity: String(addQty(prevPrep.quantity, stdQty)), _order: prevPrep._order ?? ocrOrderRef.current++ };
        placedCount++;
        continue;
      }

      // Raw ingredient: one entry per item, shared across every tab. A scan ADDS
      // to whatever is already there — see the note above handleOCRConfirm.
      // Determine which tab "owns" it so other tabs can show "Already counted".
      const matchingCats = masterCategories.filter(cat =>
        (cat.ingredients || []).some(ing => ing.item_id === item.item_id));
      const targetCat = matchingCats.find(cat => selectedGroupIds.includes(cat.master_category_id))
        || matchingCats[0];

      let sourceTab = 'uncategorized';
      let sourceTabName = 'Other';
      if (targetCat) {
        sourceTab = targetCat.master_category_id;
        sourceTabName = targetCat.master_category_name;
      }

      const key = eKey('raw', item.item_id);
      const prev = newEntries[key] || {};
      const src = rawItemById.get(item.item_id);
      // The scanned qty is in the sheet's display unit (kg/L/pcs); convert it
      // back to this ingredient's standard (app) unit so storage stays exact.
      const stdUnit = src?.unit || item.unit;
      const stdQty = convertQty(Number(item.quantity), item.unit, stdUnit);
      const nextEntry = { ...prev, quantity: String(addQty(prev.quantity, stdQty)), sourceTab, sourceTabName, _order: prev._order ?? ocrOrderRef.current++ };
      // A price DECIDED in review (sheet ink, the workbook's Store tab, or a
      // keep-which pick) becomes the ingredient's one price — so salt priced on
      // the sheet is priced inside every recipe too. Unpriced rows simply show
      // the vendor price through the same lookup; nothing is copied onto them.
      if (Number(item.unit_price) > 0) {
        priceFills.push({ id: item.item_id, price: Number(item.unit_price), unit: item.unit });
      }
      // The review's audit (raw ink, written unit, paid/pack, flags + resolutions)
      // rides the entry so /apply can persist it (spec D7).
      if (item.audit) nextEntry.audit = item.audit;
      newEntries[key] = nextEntry;
      placedCount++;
    }
    console.log(`📝 [OCR] Applied ${placedCount}/${confirmedItems.length} scanned entries to the sheet`);

    // POS import: teach the mapping memory from every pick a human made, so
    // next month's report auto-greens what was confirmed this month. Strictly
    // fire-and-forget — the count is already placed and must not depend on it.
    const picks = confirmedItems
      .filter(i => i.source_name && i.picked_item_id)
      .map(i => ({ source_name: i.source_name, item_id: i.picked_item_id, item_type: i.item_type || 'raw' }));
    if (picks.length > 0) {
      http.post('/closing-stock/import/confirm-mapping', { businessId, picks })
        .then(() => console.log(`🧠 [Import] Remembered ${picks.length} item mapping(s)`))
        .catch(() => {});
    }

    setEntries(newEntries);
    applyPriceFills(priceFills);
    // Show the consolidated "All" view so every filled value is visible
    // regardless of which tab the scan was launched from.
    setActiveTab('all');
    // Jump straight to the Consolidated Summary so the user can commit in one
    // click ("Confirm & Sync") — matching the manual flow's final step. The
    // summary reads the freshly-set entries on the next render.
    if (placedCount > 0) {
      setLockActive(true);
      setStockStep('summary');
    }
    } catch (err) {
      console.error('❌ [OCR] Failed to apply scanned entries:', err);
    } finally {
    setShowOCRReview(false);
    setOcrResult(null);
    ocrImagePreviews.forEach(preview => {
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    });
    setOcrImagePreview(null);
    setOcrImagePreviews([]);
    setSelectedFiles([]);
    setCurrentImageIndex(0);
    }
  }, [entries, ocrImagePreviews, masterCategories, uncategorizedIngredients, allPreparations, allMenuItems, selectedGroupIds, applyPriceFills]);

  // ── getAllItems — ONE canonical value per raw item (Item 4) ──
  const getIngredientDetails = useCallback((ingredientId) => {
    for (const cat of masterCategories) {
      const ing = cat.ingredients.find(i => i.item_id === ingredientId);
      if (ing) return ing;
    }
    const uncat = uncategorizedIngredients.find(i => i.item_id === ingredientId);
    if (uncat) return uncat;
    return null;
  }, [masterCategories, uncategorizedIngredients]);

  // Prices are stored per the ingredient's sheet unit (the unit its row shows).
  sheetUnitOfRef.current = (id, fallback) => getIngredientDetails(id)?.unit || fallback || null;

  // ── THE price of an ingredient on this sheet ──────────────────────────────
  // ₹ per its SHEET unit. A price written on its own row (the ₹ box, or an older
  // draft that kept prices only on rows — in grams after a revert) is read
  // first, re-expressed from the row's unit; then the one stored price. null =
  // nobody has priced it on this sheet. Every row, recipe line, total, the popup
  // and the /apply payload read through here, so a price set anywhere is the
  // price everywhere.
  const explicitPriceOf = useCallback((id) => {
    const e = entries[eKey('raw', id)];
    const su = getIngredientDetails(id)?.unit || e?.unit || null;
    const own = rowPriceText(e);
    if (own !== null) {
      const p = rebasePrice(own, e.unit || su, su);
      if (p !== null) return p;
    }
    const stored = Number(ingredientPrices[id]);
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  }, [entries, ingredientPrices, getIngredientDetails]);

  // The same price, per whatever unit a row is shown in — falling back to the
  // vendor price. 0 when nothing prices it.
  const ingredientPriceIn = useCallback((id, unit) => {
    const e = entries[eKey('raw', id)];
    const details = getIngredientDetails(id);
    const su = details?.unit || e?.unit || unit || null;
    const own = rowPriceText(e);
    // A row's own text, asked for in that row's own unit, is returned as typed.
    if (own !== null && unit && String(e.unit || su || '').toLowerCase() === String(unit).toLowerCase()) return own;
    const explicit = explicitPriceOf(id);
    const base = explicit !== null ? explicit : Number(details?.latest_vendor_price || 0);
    const p = rebasePrice(base, su, unit || su);
    return p === null ? 0 : p;
  }, [entries, explicitPriceOf, getIngredientDetails]);

  // An ingredient inside a prep / complimentary / dish: its one price → a legacy
  // per-parent override (very old drafts) → its vendor price. 0 = unpriced.
  const componentEffectivePrice = useCallback((itemType, parentId, rawId) => {
    const own = explicitPriceOf(rawId);
    if (own !== null) return own;
    const legacy = prepComponentPrices[componentPriceKey(itemType, parentId, rawId)];
    if (typeof legacy === 'number' && Number.isFinite(legacy) && legacy > 0) return legacy;
    return Number(getIngredientDetails(rawId)?.latest_vendor_price || 0);
  }, [explicitPriceOf, prepComponentPrices, getIngredientDetails]);

  // What this item already holds on the sheet, expressed in the SAME unit the
  // scan sheet uses (kg/L/pcs) — entries are stored in standard units (g/ml).
  // The review shows this so an accidental re-scan is obvious before it lands.
  const ocrExistingQtyOf = useCallback((it) => {
    const kind = it.item_type === 'preparation' ? 'prep'
      : it.item_type === 'complimentary' ? 'comp'
      : it.item_type === 'menu' ? 'menu' : 'raw';
    const stored = entries[eKey(kind, it.item_id)]?.quantity;
    const n = Number(stored);
    if (stored === undefined || stored === '' || !Number.isFinite(n) || n <= 0) return 0;

    if (kind === 'comp' || kind === 'menu') return n;    // portions/plates, no conversion
    const stdUnit = kind === 'prep'
      ? (allPreparations.find(x => x.item_id === it.item_id)?.unit || it.unit)
      : (getIngredientDetails(it.item_id)?.unit || it.unit);
    return convertQty(n, stdUnit, it.unit);
  }, [entries, allPreparations, getIngredientDetails]);


  // Expand a preparation entry into its atomic raw ingredients (for the entered qty).
  // ── The breakdown comes from the SERVER ──────────────────────────────────
  //
  // What a preparation / complimentary recipe / dish breaks down into is the
  // same walk /apply performs when it credits a count back. It used to be
  // recomputed here in four hand-mirrored functions, and the two copies drifted
  // — that drift IS the "pastry" bug, where the sheet showed one breakdown and
  // the write path credited another, with nothing comparing them.
  //
  // POST /expand answers with exactly what /apply will credit. A breakdown that
  // has not arrived, or that failed, renders as unavailable — never as a locally
  // guessed one, because the guess is how they drifted apart.
  const [expansions, setExpansions] = useState({});      // key -> raws[] | null
  // What the server's walk REFUSED to credit, and why. These are shown under
  // their parent so a composite never looks like it simply has fewer
  // ingredients than it does.
  const [expansionSkipped, setExpansionSkipped] = useState([]);
  const expansionsRef = useRef({});
  expansionsRef.current = expansions;

  // Misses are recorded here and drained by the effect below. Driving the
  // request off what the SCREEN actually asked for — rather than off a separate
  // walk of the item list — means the key that misses is exactly the key that
  // gets fetched; the two cannot disagree about how a request is spelled.
  const wantedRef = useRef(new Map());

  // The server answers in each raw's STANDARD unit (g/ml), but every price on
  // this sheet is per the ingredient's SHEET unit (₹/kg, ₹/L) — that is what
  // the ingredient list carries and what /apply divides back out. Multiplying
  // 2941 g by ₹294/kg is how a ₹865 line printed as ₹8,64,706. So each raw is
  // re-expressed in its ingredient's sheet unit before anything prices it.
  // A pair that will not convert (or an ingredient not on the sheet) is left
  // as the server sent it rather than guessed.
  const toSheetUnit = useCallback((r) => {
    const sheetUnit = getIngredientDetails(r.atomic_item_id)?.unit;
    if (!sheetUnit || !r.unit || String(sheetUnit).toLowerCase() === String(r.unit).toLowerCase()) return r;
    const q = convertQty(r.qty, r.unit, sheetUnit);
    return (q === null || !Number.isFinite(q)) ? r : { ...r, qty: q, unit: sheetUnit };
  }, [getIngredientDetails]);

  const getExpandedRaws = useCallback((it, qty) => {
    if (!it || !(Number(qty) > 0)) return [];
    const key = expansionKey(it, qty);
    const hit = expansionsRef.current[key];
    if (hit === undefined && it.item_type && it.item_id) {
      wantedRef.current.set(key, {
        key, item_id: it.item_id, item_type: it.item_type,
        item_name: it.item_name, quantity: Number(qty), unit: it.unit,
      });
    }
    return Array.isArray(hit) ? hit.map(toSheetUnit) : null;   // null = not known (yet, or refused)
  }, [expansions, toSheetUnit]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The breakdown AS SHOWN: what the server credited, plus what it refused.
  // A refusal has to appear next to its parent — dropping it is what let a
  // composite quietly count for less than the counter believed it did.
  const skippedFor = useCallback((it) => expansionSkipped
    .filter(sk => String(sk.parent_name || '') === String(it?.item_name || ''))
    .map(sk => ({
      atomic_item_id: sk.item_id, item_name: sk.item_name, unit: '', qty: 0,
      unresolvable: true, reason: sk.reason || null,
    })), [expansionSkipped]);

  const getExpandedRawsShown = useCallback((it, qty) => {
    const credited = getExpandedRaws(it, qty);
    if (credited === null) return null;
    return [...credited, ...skippedFor(it)];
  }, [getExpandedRaws, skippedFor]);

  // The three shapes callers already use, all reading the one cache. `null`
  // means the breakdown is not known — callers show it as unavailable rather
  // than treating it as an empty recipe.
  const getExpandedPrepRaws = useCallback((prep, qty) =>
    getExpandedRawsShown(prep && { ...prep, item_type: 'preparation' }, qty), [getExpandedRawsShown]);
  const getExpandedCompRaws = useCallback((comp, portions) =>
    getExpandedRawsShown(comp && { ...comp, item_type: 'complimentary' }, portions), [getExpandedRawsShown]);
  const getExpandedMenuRaws = useCallback((menu, plates) =>
    getExpandedRawsShown(menu && { ...menu, item_type: 'menu' }, plates), [getExpandedRawsShown]);

  const getAllItems = useCallback(() => {
    const allItems = [];
    const seenRaw = new Set();

    // Each raw item has a single `raw_<itemId>` entry shared across every tab,
    // so we read it once — never sum across categories.
    const collectRaw = (item) => {
      if (seenRaw.has(item.item_id)) return;
      const entry = entries[eKey('raw', item.item_id)];
      // '' means the box was cleared = NOT COUNTED, and must never reach the
      // payload: the backend would read it as 0 and zero the item's stock.
      // An explicit 0 IS a count and is deliberately kept.
      if (csHasCount(entry?.quantity)) {
        seenRaw.add(item.item_id);
        // A unit edited inline on the Consolidated Sheet overrides the item's
        // default display unit; /apply converts using whatever unit we send —
        // and the ingredient's one price is sent per that same unit.
        const rowUnit = entry.unit || item.unit;
        const price = ingredientPriceIn(item.item_id, rowUnit);
        allItems.push({
          item_id: item.item_id,
          item_name: item.item_name,
          item_type: 'raw',
          quantity: Number(entry.quantity),
          unit: rowUnit,
          unit_price: price,
          // Deferred pricing: still unpriced AND explicitly deferred — /apply
          // stores the line with the pending marker for the backfill.
          ...(deferredPriceIds.includes(item.item_id) && !(price > 0) ? { price_pending: true } : {}),
          ...(entry.audit ? { audit: entry.audit } : {})
        });
      }
    };

    for (const cat of masterCategories) {
      if (!selectedGroupIds.includes(cat.master_category_id)) continue;
      for (const item of cat.ingredients) collectRaw(item);
    }
    for (const item of uncategorizedIngredients) collectRaw(item);

    // Preparations — use stable 'prep' prefix keys
    for (const prep of allPreparations) {
      const key = eKey('prep', prep.item_id);
      const entry = entries[key];
      // A composite counted 0 is NOT a count — you cannot have made zero
      // batches of a preparation; you simply did not count it. Raws keep 0.
      if (csHasCount(entry?.quantity) && Number(entry.quantity) > 0) {
        const qty = Number(entry.quantity);

        // Fully expand to atomic raw ingredients; submit each with any custom
        // price (per std unit). The backend re-derives quantities via the same
        // recursion — we only send prices keyed by atomic_item_id.
        const expandedRaws = getExpandedPrepRaws(prep, typeof qty === 'number' ? qty : 0) || [];
        // Submit the effective price (what the user sees): shared ingredient
        // price → legacy per-parent → vendor. Un-priced items are blocked
        // earlier by the guided-price gate.
        const customComponents = expandedRaws.filter(r => !r.unresolvable).map((raw) => {
          const p = componentEffectivePrice('preparation', prep.item_id, raw.atomic_item_id);
          return {
            atomic_item_id: raw.atomic_item_id,
            item_name: raw.item_name,
            unit_price: p > 0 ? p : null
          };
        });

        allItems.push({
          item_id: prep.item_id,
          item_name: prep.item_name,
          item_type: 'preparation',
          quantity: qty,
          unit: prep.unit,
          unit_price: 0,
          components: customComponents
        });
      }
    }

    // Complimentary recipes — counted in portions, otherwise identical to preps.
    // Own 'comp' key prefix: comp_recipe_id, prep id and item_id are separate
    // sequences that overlap, so a bare id would collide across the three.
    for (const comp of allComplimentary) {
      const key = eKey('comp', comp.item_id);
      const entry = entries[key];
      // A composite counted 0 is NOT a count — you cannot have made zero
      // batches of a preparation; you simply did not count it. Raws keep 0.
      if (csHasCount(entry?.quantity) && Number(entry.quantity) > 0) {
        const qty = Number(entry.quantity);

        const expandedRaws = getExpandedCompRaws(comp, typeof qty === 'number' ? qty : 0) || [];
        const customComponents = expandedRaws.filter(r => !r.unresolvable).map((raw) => {
          const p = componentEffectivePrice('complimentary', comp.item_id, raw.atomic_item_id);
          return {
            atomic_item_id: raw.atomic_item_id,
            item_name: raw.item_name,
            unit_price: p > 0 ? p : null
          };
        });

        allItems.push({
          item_id: comp.item_id,
          item_name: comp.item_name,
          item_type: 'complimentary',
          quantity: qty,
          unit: comp.unit,
          unit_price: 0,
          components: customComponents
        });
      }
    }

    // Menu items (dishes) — counted in plates, otherwise identical to preps.
    // A dish with no recipe cannot decompose, so it is never submitted.
    for (const menu of allMenuItems) {
      if (!menu.has_recipe) continue;
      const key = eKey('menu', menu.item_id);
      const entry = entries[key];
      // A composite counted 0 is NOT a count — you cannot have made zero
      // batches of a preparation; you simply did not count it. Raws keep 0.
      if (csHasCount(entry?.quantity) && Number(entry.quantity) > 0) {
        const qty = Number(entry.quantity);

        const expandedRaws = getExpandedMenuRaws(menu, typeof qty === 'number' ? qty : 0) || [];
        const customComponents = expandedRaws.filter(r => !r.unresolvable).map((raw) => {
          const p = componentEffectivePrice('menu', menu.item_id, raw.atomic_item_id);
          return {
            atomic_item_id: raw.atomic_item_id,
            item_name: raw.item_name,
            unit_price: p > 0 ? p : null
          };
        });

        allItems.push({
          item_id: menu.item_id,
          item_name: menu.item_name,
          item_type: 'menu',
          quantity: qty,
          unit: menu.unit,
          unit_price: 0,
          components: customComponents
        });
      }
    }

    // Negative Stock Clearance rows — guarantee each cleared negative is in the
    // payload even when its item sits outside the counted categories (collectRaw
    // above only walks the selected groups + uncategorized list).
    for (const nc of negativeClearanceItems) {
      if (seenRaw.has(nc.item_id)) continue;
      const entry = entries[eKey('raw', nc.item_id)];
      if (!csHasCount(entry?.quantity)) continue;
      seenRaw.add(nc.item_id);
      allItems.push({
        item_id: nc.item_id,
        item_name: nc.item_name,
        item_type: 'raw',
        quantity: Number(entry.quantity),
        unit: entry.unit || nc.unit,
        unit_price: ingredientPriceIn(nc.item_id, entry.unit || nc.unit)
      });
    }

    return allItems;
  }, [masterCategories, uncategorizedIngredients, allPreparations, allComplimentary, allMenuItems, entries, selectedGroupIds, componentEffectivePrice, ingredientPriceIn, getExpandedPrepRaws, getExpandedCompRaws, getExpandedMenuRaws, negativeClearanceItems, deferredPriceIds]);

  // ── Item 3 — items in scope that have no entry (stock stays untouched) ──
  const getNotCountedItems = useCallback(() => {
    const notCounted = [];
    const seen = new Set();
    const considerRaw = (item) => {
      if (seen.has(item.item_id)) return;
      seen.add(item.item_id);
      const entry = entries[eKey('raw', item.item_id)];
      if (!(entry?.quantity !== undefined && entry.quantity !== '')) {
        notCounted.push({ key: `raw_${item.item_id}`, item_name: item.item_name });
      }
    };
    for (const cat of masterCategories) {
      if (!selectedGroupIds.includes(cat.master_category_id)) continue;
      for (const item of cat.ingredients) considerRaw(item);
    }
    for (const item of uncategorizedIngredients) considerRaw(item);
    for (const prep of allPreparations) {
      const entry = entries[eKey('prep', prep.item_id)];
      if (!(entry?.quantity !== undefined && entry.quantity !== '')) {
        notCounted.push({ key: `prep_${prep.item_id}`, item_name: prep.item_name });
      }
    }
    for (const comp of allComplimentary) {
      const entry = entries[eKey('comp', comp.item_id)];
      if (!(entry?.quantity !== undefined && entry.quantity !== '')) {
        notCounted.push({ key: `comp_${comp.item_id}`, item_name: comp.item_name });
      }
    }
    // Dishes with no recipe are not countable, so they are never "not counted".
    for (const menu of allMenuItems) {
      if (!menu.has_recipe) continue;
      const entry = entries[eKey('menu', menu.item_id)];
      if (!(entry?.quantity !== undefined && entry.quantity !== '')) {
        notCounted.push({ key: `menu_${menu.item_id}`, item_name: menu.item_name });
      }
    }
    return notCounted;
  }, [masterCategories, uncategorizedIngredients, allPreparations, allComplimentary, allMenuItems, entries, selectedGroupIds]);

  // Lookup used to expand nested preparations down to atomic raw ingredients.
  const getItemBreakdown = useCallback((itemId) => {
    const rawKey = eKey('raw', itemId);
    const rawEntry = entries[rawKey];
    const rawHasCount = csHasCount(rawEntry?.quantity);
    const rawQty = rawHasCount ? Number(rawEntry.quantity) : 0;
    const rawDetails = getIngredientDetails(itemId);
    const unit = rawEntry?.unit || rawDetails?.unit || 'g';
    const rawUnitPrice = ingredientPriceIn(itemId, unit);

    const directRecipes = [];
    const savedPreps = [];

    for (const it of getAllItems()) {
      if (!isCompositeType(it.item_type)) continue;
      const isPrep = it.item_type === 'preparation';
      const raws = getExpandedRawsShown(it, it.quantity) || [];

      for (const r of raws) {
        if (r.unresolvable) continue;
        if (Number(r.atomic_item_id) === Number(itemId)) {
          let convertedQty = r.qty;
          if (r.unit && unit && r.unit.toLowerCase() !== unit.toLowerCase()) {
            const conv = convertQty(r.qty, r.unit, unit);
            if (conv !== null && Number.isFinite(conv)) convertedQty = conv;
          }
          if (isPrep) {
            const existing = savedPreps.find(x => x.name === it.item_name);
            if (existing) {
              existing.qty = addQty(existing.qty, convertedQty);
            } else {
              savedPreps.push({ name: it.item_name, qty: convertedQty, unit });
            }
          } else {
            const existing = directRecipes.find(x => x.name === it.item_name);
            if (existing) {
              existing.qty = addQty(existing.qty, convertedQty);
            } else {
              directRecipes.push({ name: it.item_name, qty: convertedQty, unit });
            }
          }
        }
      }
    }

    const recipesTotalQty = directRecipes.reduce((acc, r) => addQty(acc, r.qty), 0);
    const prepsTotalQty = savedPreps.reduce((acc, p) => addQty(acc, p.qty), 0);
    const totalRequiredQty = addQty(rawQty, addQty(recipesTotalQty, prepsTotalQty));

    return {
      itemId,
      name: rawDetails?.item_name || `Item ${itemId}`,
      unit,
      closingStockQty: rawQty,
      countedOnSheet: rawHasCount,
      closingStockValue: rawQty * rawUnitPrice,
      directRecipes,
      recipesTotalQty,
      savedPreps,
      prepsTotalQty,
      totalRequiredQty,
      unitPrice: rawUnitPrice,
      totalValue: totalRequiredQty * rawUnitPrice,
    };
  }, [entries, getIngredientDetails, ingredientPriceIn, getAllItems, getExpandedRawsShown]);

  // ── Item 2 — counted raw items (qty>0) that still have no price ──
  // Item 2 — every counted ingredient must have a price. This covers plain raw
  // items AND each atomic ingredient of a counted preparation (a prep ingredient
  // is "priced" if the user set a price OR it auto-fills from a vendor price).
  const getMissingPriceItems = useCallback(() => {
    // ONE row per unpriced INGREDIENT — however many recipes contain it. Setting
    // that one price feeds the shared tier, so every occurrence clears at once
    // ("set oil once" — no more clove asked under each gravy separately).
    const byIngredient = new Map(); // rawId -> row
    const addNeed = (rawId, name, unit, qty, parentName, parentType) => {
      let row = byIngredient.get(rawId);
      if (!row) {
        const ingDetail = getIngredientDetails(rawId);
        const preferredUnit = ingDetail?.unit || unit;
        row = {
          key: `ing_${rawId}`,
          kind: 'ingredient',
          item_id: rawId,
          base_name: ingDetail?.item_name || name,
          unit: preferredUnit,
          quantity: 0,
          parents: new Set(),
          counted_raw: false,
          closing_stock_qty: 0,
          direct_recipes: [],
          saved_preps: []
        };
        byIngredient.set(rawId, row);
      }

      let normalizedQty = qty;
      if (unit && row.unit && unit.toLowerCase() !== row.unit.toLowerCase()) {
        const conv = convertQty(qty, unit, row.unit);
        if (conv !== null && Number.isFinite(conv)) normalizedQty = conv;
      }

      row.quantity = addQty(row.quantity, normalizedQty);
      if (parentName) row.parents.add(parentName);

      if (parentType === 'raw') {
        row.counted_raw = true;
        row.closing_stock_qty = addQty(row.closing_stock_qty, normalizedQty);
      } else if (parentType === 'preparation') {
        const existing = row.saved_preps.find(p => p.name === parentName);
        if (existing) {
          existing.qty = addQty(existing.qty, normalizedQty);
        } else {
          row.saved_preps.push({ name: parentName, qty: normalizedQty, unit: row.unit });
        }
      } else if (parentType === 'menu' || parentType === 'complimentary') {
        const existing = row.direct_recipes.find(r => r.name === parentName);
        if (existing) {
          existing.qty = addQty(existing.qty, normalizedQty);
        } else {
          row.direct_recipes.push({ name: parentName, qty: normalizedQty, unit: row.unit });
        }
      }
    };

    for (const it of getAllItems()) {
      if (it.item_type === 'raw') {
        if (Number(it.quantity) > 0 && !(Number(it.unit_price) > 0)) {
          addNeed(it.item_id, it.item_name, it.unit, Number(it.quantity), null, 'raw');
        }
      } else if (isCompositeType(it.item_type)) {
        const isComp = it.item_type === 'complimentary';
        const isMenu = it.item_type === 'menu';
        const src = (isMenu ? allMenuItems : isComp ? allComplimentary : allPreparations).find(x => x.item_id === it.item_id);
        if (!src) continue;
        // null = the breakdown has not arrived; a row cannot be judged
        // missing-a-price until it is known to exist.
        for (const raw of (getExpandedRawsShown(it, it.quantity) || [])) {
          // A component that cannot be credited is not priced either.
          if (raw.unresolvable) continue;
          if (!(componentEffectivePrice(it.item_type, it.item_id, raw.atomic_item_id) > 0)) {
            addNeed(raw.atomic_item_id, raw.item_name, raw.unit, raw.qty, src.item_name, it.item_type);
          }
        }
      }
    }

    // An item whose name matches a saved preparation is that prep's "shell" —
    // never bought, made in-house. Flagged so the guide can mark it.
    const prepNames = new Set(allPreparations.map(p => String(p.item_name || '').toLowerCase().trim()));

    return [...byIngredient.values()].map(row => {
      const rawEntry = entries[eKey('raw', row.item_id)];
      const rawHasCount = csHasCount(rawEntry?.quantity);
      const sheetCountVal = rawHasCount ? Number(rawEntry.quantity) : 0;
      const closingStockQty = row.counted_raw ? row.closing_stock_qty : (rawHasCount ? sheetCountVal : 0);

      return {
        ...row,
        closing_stock_qty: closingStockQty,
        counted_raw: row.counted_raw || rawHasCount,
        parents: [...row.parents],
        is_prep: prepNames.has(String(row.base_name || '').toLowerCase().trim()),
        item_name: row.parents.size > 0
          ? `${row.base_name}  ·  ${[...row.parents].length === 1 ? [...row.parents][0] : `${[...row.parents].length} recipes`}`
          : row.base_name
      };
    });
  }, [getAllItems, allPreparations, allComplimentary, allMenuItems, getExpandedRawsShown, componentEffectivePrice, entries]);

  /**
   * PIECE GUARD read-back for this count: one line per counted `pcs` ingredient whose
   * Stock In answer is a packet. Item-level answers (no vendor — a count has none), the
   * same rows Settings › Piece Guard edits. Never throws: an answer that cannot be read
   * leaves that item out, because a hint must never stand between somebody and their count.
   */
  const piecePackLinesForCount = useCallback(async (pcsCounts) => {
    const ids = [...new Set(pcsCounts.map(i => i.item_id))];
    const profiles = {};
    // The endpoint answers at most 200 ids per call.
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      try {
        const res = await http.get(`/stock-in/v2/piece-profiles?businessId=${businessId}&itemIds=${chunk.join(',')}`);
        if (res && res.success && !res.unavailable) Object.assign(profiles, res.profiles || {});
      } catch (e) {
        console.debug('Piece packet read-back skipped:', e.message);
      }
    }
    return pcsCounts
      .map(i => piecePackLine(
        { id: i.item_id, itemId: i.item_id, name: i.item_name, pieces: Number(i.quantity) },
        profiles[i.item_id]
      ))
      .filter(Boolean);
  }, [businessId]);

  const handleApply = useCallback(async (opts = {}) => {
    const { skipPrice = false, skipNotCounted = false, skipNegative = false, skipPiecePacks = false, ccsConfirmed = false } = opts;
    const allItems = getAllItems();
    if (allItems.length === 0) {
      alert('Please enter at least one item quantity.');
      return;
    }

    const blockedItems = allItems.filter(i => flags[i.item_id]?.status === 'blocked');
    if (blockedItems.length > 0) {
      alert(`${blockedItems.length} item(s) are blocked by the guard rail. Please fix them or use the Manager Override PIN.`);
      return;
    }

    // ── Negative Stock Clearance gate ─────────────────────────────
    // Before committing, any ingredient oversold below zero must be physically
    // counted. Only the negatives NOT already counted on this sheet are surfaced;
    // counting them overrides stock to the real figure, clearing the debt.
    if (!skipNegative) {
      try {
        setNegChecking(true);
        const resp = await http.get(`/closing-stock/negative-stock?businessId=${businessId}`);
        const negs = (resp && resp.success && Array.isArray(resp.items)) ? resp.items : [];
        const unresolved = negs.filter(n => {
          const en = entries[eKey('raw', n.item_id)];
          const counted = en && en.quantity !== undefined && en.quantity !== '';
          return !counted;   // "only not-yet-counted"
        });
        if (unresolved.length > 0) {
          setNegativeModalRows(unresolved.map(n => ({
            item_id: n.item_id,
            name: n.item_name,
            current_stock: n.current_stock,          // negative, shown as context
            unit: n.unit,
            // Prefill existing count if present, else 0.
            qty: (() => {
              const en = entries[eKey('raw', n.item_id)];
              return (en && en.quantity !== undefined && en.quantity !== '') ? String(en.quantity) : '0';
            })(),
            // The ingredient's one price, per this row's unit; a negative item
            // outside the sheet's lists falls back to the server's vendor price.
            unit_price: (() => {
              const p = ingredientPriceIn(n.item_id, n.unit);
              return p > 0 ? String(p) : (n.latest_vendor_price > 0 ? String(n.latest_vendor_price) : '');
            })()
          })));
          setShowNegativeModal(true);
          return;
        }
      } catch (e) {
        // A failed check must not silently skip clearance nor hard-block the count;
        // log and fall through so the rest of the commit still runs.
        console.error('Negative-stock check failed:', e);
      } finally {
        setNegChecking(false);
      }
    }

    // Commit sequence: price fill → not-counted → backdate → apply.
    // Item 2 — every counted item must carry a price (guided fill), except
    // those explicitly deferred to first-purchase pricing.
    if (!skipPrice) {
      // Breakdowns first: an in-flight one looks like an empty recipe.
      await flushExpansions();
      const missing = getMissingPriceItems().filter(m => !deferredPriceIds.includes(m.item_id));
      if (missing.length > 0) {
        priceGuideResumeRef.current = 'commit';
        setPriceGuideItems(missing);
        setPriceGuideValues(Object.fromEntries(missing.map(m => [m.key, ''])));
        setPriceGuideSearch('');
        setExpandedGuideKeys(new Set());
        setShowPriceGuide(true);
        return;
      }
    }

    // Item 3 — one-time "N items not counted" warning before committing.
    if (!skipNotCounted && !notCountedAcknowledged) {
      const nc = getNotCountedItems();
      if (nc.length > 0) {
        setNotCountedList(nc);
        setShowNotCountedWarning(true);
        return;
      }
    }

    // C6-4 — the attestation is mandatory on every path (Verify or Move to
    // Consolidate alike): owner + manager sign-off, preps checked, timing.
    if (!attestationRef.current) {
      // PIECE GUARD — counted `pcs` ingredients are said back as packets before
      // anybody signs for them. Raw rows counted in pieces only; with none, the
      // attestation opens exactly as before.
      const pcsCounts = skipPiecePacks ? [] : allItems.filter(i =>
        i.item_type === 'raw' && Number(i.quantity) > 0 &&
        UnitMappingService.normalizeUnitSymbol(i.unit) === 'pcs'
      );
      if (pcsCounts.length > 0) {
        if (pieceCheckingRef.current) return;
        pieceCheckingRef.current = true;
        setPieceChecking(true);
        let lines = [];
        try {
          lines = await piecePackLinesForCount(pcsCounts);
        } finally {
          pieceCheckingRef.current = false;
          setPieceChecking(false);
        }
        if (lines.length > 0) {
          setPiecePackReview(lines);
          return;
        }
      }
      setShowAttestation(true);
      return;
    }

    // Continuous Closing Stock (spec §5.2): show what applying will do — expected,
    // counted and the difference per line, lines to check, items not counted — before
    // anything is saved. The server runs the whole apply and rolls it back (dryRun).
    if (continuous && !ccsConfirmed) {
      setApplying(true);
      try {
        const preview = await http.post('/closing-stock/apply', {
          businessId, closingStockDate: closingDate, items: allItems,
          physicalCountDate: countedDate, managerPin: managerPin || undefined,
          attestation: attestationRef.current, dryRun: true
        });
        if (preview && preview.success === false && preview.code === 'NEEDS_MANAGER') {
          alert(preview.error);
          setShowDatePicker(true);
          return;
        }
        if (preview && preview.success && preview.preview) {
          setCcsPreview(preview);
          return;
        }
        alert((preview && preview.error) || 'Could not prepare the preview.');
      } catch (error) {
        alert(error.message || 'Could not prepare the preview.');
      } finally {
        setApplying(false);
      }
      return;
    }

    if (rollbackRequested && !continuous && !showBackdateConfirm) {
      try {
        const preview = await http.post('/closing-stock/backdate-preview', {
          businessId, targetDate: closingDate, physicalCountDate: countedDate, items: allItems
        });
        if (preview.success && preview.is_backdated) {
          setBackdatePreview(preview);
          setShowBackdateConfirm(true);
          return;
        }
      } catch (e) {
        console.error('Backdate preview failed:', e);
      }
    }

    setApplying(true);
    try {
      const result = await http.post('/closing-stock/apply', {
        businessId, closingStockDate: closingDate, items: allItems,
        // Always sent: without it the server assumed the shelves were counted TODAY
        // and rolled every earlier-dated count back for sales and deliveries.
        physicalCountDate: countedDate,
        managerPin: managerPin || undefined,
        attestation: attestationRef.current
      });
      if (result && result.success === false && result.code === 'NEEDS_MANAGER') {
        alert(result.error);
        setShowDatePicker(true);   // where the manager PIN is entered
        return;
      }
      if (result.success) {
        // The server credits nothing for a component it cannot convert; say which.
        setAppliedSkipped(Array.isArray(result.skipped) ? result.skipped : []);
        setStockStep('done');
        setLockActive(false);
        setVerificationAccepted(false);
        setNotCountedAcknowledged(false);
        setNegativeClearanceItems([]);
        attestationRef.current = null;
        // The committed count deleted its draft server-side; drop the local
        // banner state too so it can't reappear on the overview this session.
        setResumeDraft(null);
        setCcsPreview(null);
      } else {
        alert(result.error || 'Failed to apply');
      }
    } catch (error) {
      if (error?.data?.code === 'UNMAPPED_SALES_PENDING') {
        // The server refused the count: lines it would absorb invisibly. The draft is kept.
        setUnmappedGate({ count: error.data.count, amount: error.data.amount, dishes: error.data.dishes || [], from: 'apply' });
        return;
      }
      if (error?.data?.code === 'NOT_IN_RECIPE_PENDING') {
        // The server refused the count: items no dish uses still need a head or a rate. The
        // popup reopens with the server's list; the draft is kept.
        setNirFrom('apply');
        setNirApplyMessage(error.data.error || null);
        setNirOpen(true);
        return;
      }
      alert(error.message || 'Failed to apply closing stock');
    } finally {
      setApplying(false);
      setShowBackdateConfirm(false);
      setBackdatePreview(null);
    }
  }, [getAllItems, flags, rollbackRequested, continuous, showBackdateConfirm, businessId, closingDate, countedDate, managerPin, getNotCountedItems, notCountedAcknowledged, getMissingPriceItems, entries, ingredientPriceIn, deferredPriceIds, piecePackLinesForCount]);

  // Confirm the Negative Stock Clearance modal: write each counted value into the
  // sheet as a raw entry (so it appears under the "Negative Stock Clearance"
  // section of the consolidated) and record the ids that drive that section.
  // Deliberately does NOT auto-continue — the user reviews the added rows on the
  // consolidated and presses Confirm & Sync again.
  const confirmNegativeClearance = useCallback(() => {
    setEntries(prev => {
      const next = { ...prev };
      for (const r of negativeModalRows) {
        const key = eKey('raw', r.item_id);
        next[key] = {
          ...(next[key] || {}),
          quantity: String(r.qty),
          unit: r.unit,
          unit_price: String(r.unit_price),
          _order: next[key]?._order ?? ocrOrderRef.current++
        };
      }
      return next;
    });
    // These prices identify real ingredients — each becomes that ingredient's
    // one price, so any recipe containing it prices itself without asking again.
    const fills = negativeModalRows
      .filter(r => Number(r.unit_price) > 0)
      .map(r => ({ id: r.item_id, price: Number(r.unit_price), unit: r.unit }));
    setIngredientPrices(prev => storeIngredientPrices(prev, fills, (id, fb) => sheetUnitOfRef.current(id, fb)));
    setNegativeClearanceItems(prev => {
      const byId = new Map(prev.map(x => [x.item_id, x]));
      for (const r of negativeModalRows) {
        byId.set(r.item_id, { item_id: r.item_id, item_name: r.name, unit: r.unit });
      }
      return Array.from(byId.values());
    });
    setShowNegativeModal(false);
    setNegativeModalRows([]);
  }, [negativeModalRows]);

  // Item 2 — write guided prices, then auto-resume the commit once state settles.
  const savePriceGuide = useCallback(() => {
    // Pricing does not have to be finished in one sitting. Whatever HAS been
    // filled is banked; anything left blank simply stays unpriced and will be
    // asked for again next time (the gate recomputes from live state).
    // Every guide row is one INGREDIENT; its price becomes that ingredient's one
    // price — on its own row and inside every prep / complimentary / dish.
    const filled = guideFills(priceGuideItems, priceGuideValues);
    applyPriceFills(filled);

    setShowPriceGuide(false);

    // Still rows without a price → do NOT resume the commit (it would only be
    // blocked again). Land on the Consolidated Sheet so the prices just entered
    // can be reviewed and parked with Save Draft.
    if (filled.length < priceGuideItems.length) {
      setCameFromSummary(true);
      setStockStep('summary');
      return;
    }

    // Everything priced → resume where the guide was triggered.
    if (priceGuideResumeRef.current === 'verify') {
      setVerificationAccepted(false);
      setStockStep('verify');
    } else {
      setPendingCommit(true);
    }
  }, [priceGuideItems, priceGuideValues, applyPriceFills]);

  // Item 2b — "Price from first purchase": bank whatever WAS filled, defer the
  // rest to the stock-in backfill, and resume the commit. Onboarding never
  // stalls on a price dispute (spec Part 2 §8).
  const deferPriceGuide = useCallback(() => {
    applyPriceFills(guideFills(priceGuideItems, priceGuideValues));
    const unpriced = priceGuideItems.filter(g => !(Number(priceGuideValues[g.key]) > 0));
    setDeferredPriceIds(prev => [...new Set([...prev, ...unpriced.map(g => g.item_id)])]);
    setShowPriceGuide(false);
    if (priceGuideResumeRef.current === 'verify') {
      setVerificationAccepted(false);
      setStockStep('verify');
    } else {
      setPendingCommit(true);
    }
  }, [priceGuideItems, priceGuideValues, applyPriceFills]);

  // Save Draft from the "Enter price to save" guided fill modal
  const saveDraftFromPriceGuide = useCallback(async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      const fills = guideFills(priceGuideItems, priceGuideValues);
      const su = (id, fb) => sheetUnitOfRef.current(id, fb);
      const nextIngredientPrices = storeIngredientPrices(ingredientPrices, fills, su);
      const nextEntries = syncRowPrices(entries, fills, su);
      if (fills.length) {
        setIngredientPrices(nextIngredientPrices);
        setEntries(nextEntries);
      }

      const ok = await saveDraftPayload({
        releaseLock: false,
        overrideEntries: nextEntries,
        overrideIngredientPrices: nextIngredientPrices
      });

      if (ok !== false) {
        setShowPriceGuide(false);
        setExpandedGuideKeys(new Set());
        setCameFromSummary(true);
        setStockStep('summary');
        setDraftToast(true);
        setTimeout(() => setDraftToast(false), 3000);
      }
    } catch (e) {
      alert('Could not save draft: ' + (e.message || 'unknown error'));
    } finally {
      setSavingDraft(false);
    }
  }, [savingDraft, priceGuideItems, priceGuideValues, ingredientPrices, entries, saveDraftPayload]);

  const handleCancelPriceGuide = useCallback(() => {
    applyPriceFills(guideFills(priceGuideItems, priceGuideValues));
    setShowPriceGuide(false);
    setExpandedGuideKeys(new Set());
    setCameFromSummary(true);
    setStockStep('summary');
  }, [priceGuideItems, priceGuideValues, applyPriceFills]);

  // Save Draft from the Negative Stock Clearance modal
  const saveDraftFromNegativeModal = useCallback(async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      let nextEntries = { ...entries };
      const nextNegativeItemsMap = new Map(negativeClearanceItems.map(x => [x.item_id, x]));
      const fills = [];

      for (const r of negativeModalRows) {
        if (r.qty !== '' && Number.isFinite(Number(r.qty)) && Number(r.qty) >= 0) {
          const key = eKey('raw', r.item_id);
          nextEntries[key] = {
            ...(nextEntries[key] || {}),
            quantity: String(r.qty),
            unit: r.unit,
            unit_price: String(r.unit_price || ''),
            _order: nextEntries[key]?._order ?? ocrOrderRef.current++
          };
          nextNegativeItemsMap.set(r.item_id, { item_id: r.item_id, item_name: r.name, unit: r.unit });
          if (Number(r.unit_price) > 0) fills.push({ id: r.item_id, price: Number(r.unit_price), unit: r.unit });
        }
      }
      const nextIngredientPrices = storeIngredientPrices(ingredientPrices, fills, (id, fb) => sheetUnitOfRef.current(id, fb));

      const nextNegItems = Array.from(nextNegativeItemsMap.values());
      setEntries(nextEntries);
      setIngredientPrices(nextIngredientPrices);
      setNegativeClearanceItems(nextNegItems);

      const ok = await saveDraftPayload({
        releaseLock: false,
        overrideEntries: nextEntries,
        overrideIngredientPrices: nextIngredientPrices,
        overrideNegativeClearanceItems: nextNegItems
      });

      if (ok !== false) {
        setShowNegativeModal(false);
        setNegativeModalRows([]);
        setCameFromSummary(true);
        setStockStep('summary');
        setDraftToast(true);
        setTimeout(() => setDraftToast(false), 3000);
      }
    } catch (e) {
      alert('Could not save draft: ' + (e.message || 'unknown error'));
    } finally {
      setSavingDraft(false);
    }
  }, [savingDraft, negativeModalRows, entries, ingredientPrices, negativeClearanceItems, saveDraftPayload]);

  const handleCancelNegativeModal = useCallback(() => {
    setShowNegativeModal(false);
    setNegativeModalRows([]);
    setCameFromSummary(true);
    setStockStep('summary');
  }, []);

  useEffect(() => {
    if (!pendingCommit) return;
    setPendingCommit(false);
    handleApply({ skipPrice: true });
  }, [pendingCommit, handleApply]);

  const priceGuideFilledCount = priceGuideItems.filter(
    item => Number(priceGuideValues[item.key]) > 0
  ).length;
  const priceGuideComplete = priceGuideItems.length > 0 && priceGuideFilledCount === priceGuideItems.length;

  // Item 5 — per-tab counted/total counters and overall scope size.
  const isRawCounted = useCallback((itemId) => {
    const e = entries[eKey('raw', itemId)];
    return e?.quantity !== undefined && e.quantity !== '';
  }, [entries]);

  const tabCounts = useMemo(() => {
    const counts = {};
    for (const cat of masterCategories) {
      let c = 0;
      for (const ing of cat.ingredients) if (isRawCounted(ing.item_id)) c++;
      counts[cat.master_category_id] = { counted: c, total: cat.ingredients.length };
    }
    let uc = 0;
    for (const ing of uncategorizedIngredients) if (isRawCounted(ing.item_id)) uc++;
    counts['uncategorized'] = { counted: uc, total: uncategorizedIngredients.length };
    return counts;
  }, [masterCategories, uncategorizedIngredients, isRawCounted]);

  const totalScopeCount = useMemo(() => {
    const seen = new Set();
    let total = 0;
    for (const cat of masterCategories) {
      if (!selectedGroupIds.includes(cat.master_category_id)) continue;
      for (const ing of cat.ingredients) { if (!seen.has(ing.item_id)) { seen.add(ing.item_id); total++; } }
    }
    for (const ing of uncategorizedIngredients) { if (!seen.has(ing.item_id)) { seen.add(ing.item_id); total++; } }
    // Dishes with no recipe cannot be counted, so they are not in scope.
    return total + allPreparations.length + allComplimentary.length
      + allMenuItems.filter(m => m.has_recipe).length;
  }, [masterCategories, uncategorizedIngredients, allPreparations, allComplimentary, allMenuItems, selectedGroupIds]);


  const totalValue = useMemo(() => {
    return getAllItems().reduce((sum, item) => {
      if (item.item_type === 'raw') {
        return sum + (item.quantity * item.unit_price);
      } else if (isCompositeType(item.item_type)) {
        // Value the fully-expanded atomic raws (qty already in the sheet unit the price is per).
        const expandedRaws = getExpandedRawsShown(item, item.quantity) || [];
        const compValSum = expandedRaws.reduce((cSum, raw) => (
          raw.unresolvable ? cSum
            : cSum + (raw.qty * componentEffectivePrice(item.item_type, item.item_id, raw.atomic_item_id))
        ), 0);

        return sum + compValSum;
      }
      return sum;
    }, 0);
  }, [getAllItems, componentEffectivePrice, getExpandedRaws]);

  const allItemsForSummary = getAllItems();

  // Drain whatever the render just asked for. Debounced, because typing a
  // quantity changes the key on every keystroke.
  const pendingExpansionRef = useRef(null);
  const inflightExpansionRef = useRef(null);

  // Fetch whatever is outstanding NOW, and hand back a promise.
  //
  // The price gate decides what is unpriced by walking each composite's
  // breakdown. While a breakdown is still in flight it looks like an empty
  // recipe, so a fast click on Submit sailed straight past the gate and wrote
  // unpriced stock. The gates await this first.
  const flushExpansions = useCallback(() => {
    const wanted = [...wantedRef.current.values()].filter(w => expansionsRef.current[w.key] === undefined);
    if (!wanted.length) return inflightExpansionRef.current || Promise.resolve();
    clearTimeout(pendingExpansionRef.current);
    wantedRef.current.clear();
    const p = http.post('/closing-stock/expand', { businessId, items: wanted })
      .then((res) => {
        const next = {};
        for (const exp of (res && res.expansions) || []) {
          const w = wanted.find(x => Number(x.item_id) === Number(exp.item_id) && x.item_type === exp.item_type);
          if (w) next[w.key] = exp.unavailable ? null : (exp.raws || []).map(r => ({
            atomic_item_id: Number(r.item_id), item_name: r.name, unit: r.unit, qty: Number(r.qty) || 0,
          }));
        }
        expansionsRef.current = { ...expansionsRef.current, ...next };
        if (Object.keys(next).length) setExpansions(prev => ({ ...prev, ...next }));
        setExpansionSkipped(Array.isArray(res && res.skipped) ? res.skipped : []);
      })
      .catch(() => {
        const off = Object.fromEntries(wanted.map(w => [w.key, null]));
        expansionsRef.current = { ...expansionsRef.current, ...off };
        setExpansions(prev => ({ ...prev, ...off }));
      })
      .finally(() => { inflightExpansionRef.current = null; });
    inflightExpansionRef.current = p;
    return p;
  }, [businessId]);
  useEffect(() => {
    const wanted = [...wantedRef.current.values()].filter(w => expansions[w.key] === undefined);
    if (!wanted.length) return undefined;
    clearTimeout(pendingExpansionRef.current);
    pendingExpansionRef.current = setTimeout(() => {
      wantedRef.current.clear();
      http.post('/closing-stock/expand', { businessId, items: wanted })
        .then((res) => {
          const next = {};
          for (const exp of (res && res.expansions) || []) {
            const w = wanted.find(x => Number(x.item_id) === Number(exp.item_id) && x.item_type === exp.item_type);
            // The server speaks { item_id, name, unit, qty }; this screen has
            // always spoken { atomic_item_id, item_name, ... }.
            if (w) next[w.key] = exp.unavailable ? null : (exp.raws || []).map(r => ({
              atomic_item_id: Number(r.item_id), item_name: r.name, unit: r.unit, qty: Number(r.qty) || 0,
            }));
          }
          if (Object.keys(next).length) setExpansions(prev => ({ ...prev, ...next }));
          setExpansionSkipped(Array.isArray(res && res.skipped) ? res.skipped : []);
        })
        .catch(() => {
          // Unavailable, not guessed.
          setExpansions(prev => ({ ...prev, ...Object.fromEntries(wanted.map(w => [w.key, null])) }));
        });
    }, 300);
    return () => clearTimeout(pendingExpansionRef.current);
  });
  const anomalyItems = allItemsForSummary.filter(item => flags[item.item_id]?.status);

  // Consolidated Sheet rows — preparations are broken down into their atomic
  // raw ingredients (each with its own price/value); raw items shown as-is.
  const summaryRows = useMemo(() => {
    const rows = [];
    // Show items in the order they were scanned off the sheet. Each entry
    // carries the sheet position stamped at scan time (_order); manually-typed
    // items have none and keep their existing (structural) order after the
    // scanned ones. A prep-header and its ↳ children stay together because the
    // header's item is what gets ordered.
    const orderOf = (item) => {
      const o = entries[eKey(kindOfType(item.item_type), item.item_id)]?._order;
      return (typeof o === 'number') ? o : Number.MAX_SAFE_INTEGER;
    };
    const ordered = [...allItemsForSummary]
      .map((item, i) => ({ item, i }))
      .sort((a, b) => (orderOf(a.item) - orderOf(b.item)) || (a.i - b.i))
      .map(x => x.item);

    // Build the rows for one item (composite → header + ↳ children; raw → one row).
    const pushItem = (item) => {
      if (isCompositeType(item.item_type)) {
        // No phantom headers: a composite with no quantity expands to zero
        // children and would render a "(breaks down to)" row with nothing
        // under it — which reads as a counted item that produced nothing.
        if (!(Number(item.quantity) > 0)) return;
        const isComp = item.item_type === 'complimentary';
        const children = getExpandedRawsShown(item, item.quantity);
        // null = the server has not answered yet, or refused. The row says so
        // rather than rendering as a composite that breaks down into nothing.
        const pending = children === null;
        rows.push({
          key: `${kindOfType(item.item_type)}-${item.item_id}`, kind: 'prep-header',
          item_id: item.item_id, item_type: item.item_type,
          name: item.item_name, quantity: item.quantity, unit: item.unit,
          complimentary: isComp,
          hasChildren: !pending && children.length > 0,
          breakdownPending: pending
        });
        for (const raw of (children || [])) {
          const compKey = componentPriceKey(item.item_type, item.item_id, raw.atomic_item_id);
          const price = componentEffectivePrice(item.item_type, item.item_id, raw.atomic_item_id);
          rows.push({
            key: `${compKey}`, kind: 'prep-child',
            atomic_item_id: raw.atomic_item_id,
            name: raw.item_name, quantity: raw.qty, unit: raw.unit,
            unitPrice: price, total: raw.qty * price,
            unresolvable: !!raw.unresolvable, reason: raw.reason || null
          });
        }
      } else {
        rows.push({
          key: `raw-${item.item_id}`, kind: 'raw',
          item_id: item.item_id,
          name: item.item_name, quantity: item.quantity, unit: item.unit,
          unitPrice: item.unit_price, total: item.quantity * item.unit_price
        });
      }
    };

    // Split off the negative-stock-clearance raws into their own section so the
    // user can see, at a glance, which counts were entered to clear oversell debt.
    const clearanceSet = new Set(negativeClearanceItems.map(x => x.item_id));
    const normal = ordered.filter(it => !(it.item_type === 'raw' && clearanceSet.has(it.item_id)));
    const clearance = ordered.filter(it => it.item_type === 'raw' && clearanceSet.has(it.item_id));

    for (const item of normal) pushItem(item);
    if (clearance.length > 0) {
      rows.push({ key: 'neg-section-header', kind: 'neg-section-header', count: clearance.length });
      for (const item of clearance) pushItem(item);
    }
    return rows;
  }, [allItemsForSummary, entries, getExpandedRawsShown, componentEffectivePrice, getIngredientDetails, negativeClearanceItems]);

  // ── The sheet, as the report should print it ──────────────────────────────
  // The report used to be built server-side from live current_stock x ladder
  // prices, so it had no connection to this screen: preparations printed 0.00
  // (crediting a count zeroes their stock) under a grand total of live
  // raw-ingredient value. A count in progress exists NOWHERE in the database,
  // so the only honest source is the sheet itself — sent in its own order, in
  // the units and at the prices the user is looking at.
  //
  // Totals travel for reference only; the server recomputes them.
  const serializeSheetForReport = useCallback(() => {
    const RAW = 'Counted Items';
    const PREP = 'Preparations & Semi-Finished';
    const NEG = 'Negative Stock Clearance';
    let inClearance = false;
    const out = [];
    for (const row of summaryRows) {
      if (row.kind === 'neg-section-header') { inClearance = true; continue; }
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty)) continue;
      const price = Number(row.unitPrice) || 0;
      if (row.kind === 'prep-header') {
        // The batch count itself. Its money lives on the children below it, so
        // sending a price here would double-count the preparation.
        out.push({
          section: PREP, code: null, item_id: row.item_id, item_type: row.item_type,
          item_name: row.name, quantity: qty, unit: row.unit, unit_price: 0, total: 0,
        });
      } else if (row.kind === 'prep-child') {
        out.push({
          section: PREP, code: null, item_id: row.item_id ?? null, item_type: 'raw',
          item_name: row.name, quantity: qty, unit: row.unit, unit_price: price,
          total: Number(row.total) || qty * price,
        });
      } else if (row.kind === 'raw') {
        out.push({
          section: inClearance ? NEG : RAW, code: null, item_id: row.item_id, item_type: 'raw',
          item_name: row.name, quantity: qty, unit: row.unit, unit_price: price,
          total: Number(row.total) || qty * price,
        });
      }
    }
    return out;
  }, [summaryRows]);

  serializeSheetRef.current = serializeSheetForReport;

  const filteredSummaryRows = useMemo(() => {
    if (!summarySearchTerm || !summarySearchTerm.trim()) return summaryRows;
    const term = summarySearchTerm.trim().toLowerCase();
    const result = [];
    let currentPrepHeader = null;
    let currentPrepHeaderAdded = false;
    let negHeader = null;
    let negHeaderAdded = false;

    for (const row of summaryRows) {
      if (row.kind === 'neg-section-header') {
        // Hold the section header; only emit it if a row under it survives search.
        negHeader = row;
        negHeaderAdded = false;
      } else if (row.kind === 'prep-header') {
        currentPrepHeader = row;
        currentPrepHeaderAdded = false;
        if (row.name && row.name.toLowerCase().includes(term)) {
          result.push(row);
          currentPrepHeaderAdded = true;
        }
      } else if (row.kind === 'prep-child') {
        const matchesChild = row.name && row.name.toLowerCase().includes(term);
        if (matchesChild || currentPrepHeaderAdded) {
          if (!currentPrepHeaderAdded && currentPrepHeader) {
            result.push(currentPrepHeader);
            currentPrepHeaderAdded = true;
          }
          result.push(row);
        }
      } else {
        if (row.name && row.name.toLowerCase().includes(term)) {
          if (negHeader && !negHeaderAdded) { result.push(negHeader); negHeaderAdded = true; }
          result.push(row);
        }
      }
    }
    return result;
  }, [summaryRows, summarySearchTerm]);

  // ── Tab Data ────────────────────────────────────────────────
  const selectedMasterCategories = useMemo(() => {
    return masterCategories.filter(c => selectedGroupIds.includes(c.master_category_id));
  }, [masterCategories, selectedGroupIds]);

  const tabIngredients = useMemo(() => getTabIngredients(activeTab), [activeTab, getTabIngredients]);
  const tabPreparations = useMemo(() => getTabPreparations(activeTab), [activeTab, getTabPreparations]);

  const filteredTabIngredients = useMemo(() => {
    return sortBySearchPriority(tabIngredients, searchTerm, ing => ing.item_name);
  }, [tabIngredients, searchTerm]);

  const filteredTabPreparations = useMemo(() => {
    return sortBySearchPriority(tabPreparations, searchTerm, prep => prep.item_name);
  }, [tabPreparations, searchTerm]);

  const tabComplimentary = useMemo(() => getTabComplimentary(activeTab), [activeTab, getTabComplimentary]);
  const filteredTabComplimentary = useMemo(() => {
    return sortBySearchPriority(tabComplimentary, searchTerm, c => c.item_name);
  }, [tabComplimentary, searchTerm]);

  const tabMenuItems = useMemo(() => getTabMenuItems(activeTab), [activeTab, getTabMenuItems]);
  const filteredTabMenuItems = useMemo(() => {
    return sortBySearchPriority(tabMenuItems, searchTerm, m => m.item_name);
  }, [tabMenuItems, searchTerm]);

  // Step locking removed — preps and raw ingredients shown together

  // ── Checklist ───────────────────────────────────────────────
  const checklistGroups = masterCategories.map(cat => ({
    id: cat.master_category_id,
    name: cat.master_category_name,
    count: cat.ingredients.length
  }));

  const allChecklistSelected = checklistGroups.length > 0 && selectedGroupIds.length === checklistGroups.length;
  const toggleChecklistGroup = (groupId) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };
  const selectAllChecklistGroups = () => {
    if (allChecklistSelected) setSelectedGroupIds([]);
    else setSelectedGroupIds(checklistGroups.map(group => group.id));
  };

  // Starting a count also raises the backend lock so Stock-In / Manual Entry
  // are blocked while counting (Item 1).
  const raiseBackendLock = () => {
    http.post('/closing-stock/toggle-lock', { businessId, enabled: true }).catch(() => {});
  };

  const startStockTake = () => {
    setCameFromSummary(false);
    setStockStep('entry');
    setShowSelectionChecklist(false);
    setLockActive(true);
    setActiveTab('all');
    setNotCountedAcknowledged(false);
    raiseBackendLock();
  };

  const startConsolidatedStockTake = () => {
    setCameFromSummary(false);
    setSelectedGroupIds(checklistGroups.map(group => group.id));
    setStockStep('entry');
    setShowSelectionChecklist(false);
    setLockActive(true);
    setActiveTab('all');
    setNotCountedAcknowledged(false);
    raiseBackendLock();
  };

  // "Add More → Enter Manually" — drop back into the entry form to type values.
  // Entries already flow into the Consolidated Sheet, so anything typed here
  // shows up when they return to the summary. Re-raises the Stock-In block since
  // the count is active again.
  const openManualEntry = () => {
    setShowAddMoreChooser(false);
    setLockActive(true);
    setActiveTab('all');
    setCameFromSummary(true);
    setStockStep('entry');
    raiseBackendLock();
  };

  const openVerification = async () => {
    if (enteredCount === 0) {
      alert('Please enter at least one item quantity.');
      return;
    }
    // Item 2 — a counted item without a price cannot proceed. Enforce it here
    // (before verification), then again as a backstop at final commit.
    // Breakdowns first: an in-flight one looks like an empty recipe, and a fast
    // click used to sail past this gate and write unpriced stock.
    await flushExpansions();
    const missing = getMissingPriceItems();
    if (missing.length > 0) {
      priceGuideResumeRef.current = 'verify';
      setPriceGuideItems(missing);
      setPriceGuideValues(Object.fromEntries(missing.map(m => [m.key, ''])));
      setPriceGuideSearch('');
      setExpandedGuideKeys(new Set());
      setShowPriceGuide(true);
      return;
    }
    setVerificationAccepted(false);
    setStockStep('verify');
  };

  // "Move to Consolidate" — a fast path from the entry list straight to the
  // Consolidated Sheet, skipping the Verify step AND the guided price gate.
  // The items just entered already live in `entries`, so the derived
  // consolidated view picks them up automatically (added to whatever is already
  // there from a scan or a previous entry). Prices are editable on the
  // consolidated and the price gate still fires at Confirm & Sync as a backstop.
  const moveToConsolidate = () => {
    if (enteredCount === 0) {
      alert('Please enter at least one item quantity.');
      return;
    }
    setCameFromSummary(true);
    setStockStep('summary');
  };

  // `id` here is the ATOMIC INGREDIENT id — the saved price goes into the shared
  // per-ingredient tier, so it applies inside every recipe that uses it.
  const openPrepPriceDialog = (component, rawId, quantity, unit, currentVal) => {
    setPrepPriceDialog({
      id: rawId,
      name: getComponentName(component),
      quantity: Number.isFinite(quantity) ? quantity : 0,
      unit: unit || component.unit_symbol || component.unit || '',
      currentPrice: currentVal,
      target: 'prep-child'
    });
  };

  const closePrepPriceDialog = () => setPrepPriceDialog(null);
  // (legacy savePrepComponentPrice removed — component prices now write the
  // shared per-ingredient tier via setIngredientPrice)

  // Guided price fill (Item 2) — open the rich "Set price" dialog for one item.
  const openGuidePriceDialog = (g) => {
    const existing = Number(priceGuideValues[g.key]);
    setPrepPriceDialog({
      id: g.key,
      name: g.item_name,
      quantity: Number.isFinite(Number(g.quantity)) ? Number(g.quantity) : 0,
      unit: g.unit || '',
      currentPrice: Number.isFinite(existing) && existing > 0 ? existing : undefined,
      target: 'guide'
    });
  };

  const openRawPriceDialog = (row, qtyVal, unitVal, priceVal) => {
    const rawKey = eKey('raw', row.item_id);
    setPrepPriceDialog({
      id: rawKey,
      rawId: row.item_id,
      name: row.name,
      quantity: Number(qtyVal) || 0,
      unit: unitVal || row.unit || 'g',
      currentPrice: Number(priceVal) || 0,
      target: 'raw'
    });
  };

  const openPrepChildPriceDialog = (row, priceVal) => {
    setPrepPriceDialog({
      id: row.atomic_item_id,
      name: row.name,
      quantity: Number(row.quantity) || 0,
      unit: row.unit || 'g',
      currentPrice: Number(priceVal) || 0,
      target: 'prep-child'
    });
  };

  // Negative Stock Clearance rows use the same rich "Set price" dialog as the
  // consolidated. The result is written back to the modal's working copy (not to
  // `entries`) since the row is not committed to the sheet until Confirm.
  const openNegativePriceDialog = (r) => {
    setPrepPriceDialog({
      id: r.item_id,
      name: r.name,
      quantity: Number(r.qty) || 0,
      unit: r.unit || 'g',
      currentPrice: Number(r.unit_price) || 0,
      target: 'negative'
    });
  };

  // Route the "Set price" dialog's save. The dialog always answers in ₹ per the
  // unit it was opened with (`prepPriceDialog.unit`), and every path that names
  // a real ingredient makes that the ingredient's ONE price (applyPriceFills) —
  // so a price set anywhere covers that ingredient everywhere it appears.
  const handlePrepDialogSave = (id, price, newQty, newUnit) => {
    const dialogUnit = prepPriceDialog?.unit;
    if (prepPriceDialog?.target === 'guide') {
      setPriceGuideValues(prev => ({ ...prev, [id]: String(price) }));
      setPrepPriceDialog(null);
    } else if (prepPriceDialog?.target === 'raw') {
      // ONLY an explicitly edited quantity may touch the count. A null here
      // means the user just set a price — the row's own count must survive,
      // and an UNCOUNTED row must stay uncounted.
      if (newQty !== null && newQty !== undefined && newQty !== '' && !isNaN(Number(newQty))) {
        updateEntry(id, 'quantity', String(newQty));
      }
      // A unit change CONVERTS the counted quantity; it never silently
      // relabels it (3500 g must not become 3500 kg).
      if (newUnit) {
        const prevEntry = entries[id] || {};
        const prevUnit = prevEntry.unit || prepPriceDialog.unit;
        const prevQty = Number(prevEntry.quantity);
        if ((newQty === null || newQty === undefined)
            && Number.isFinite(prevQty) && prevQty > 0 && prevUnit && prevUnit !== newUnit) {
          updateEntry(id, 'quantity', String(convertQty(prevQty, prevUnit, newUnit)));
        }
        updateEntry(id, 'unit', newUnit);
      }
      // Queued AFTER the unit update, so a row carrying its own ₹ text is
      // re-expressed in its new unit — the price follows the unit, never the
      // label alone (2 kg @ ₹400 must not become 2000 g @ ₹400).
      if (prepPriceDialog?.rawId != null) applyPriceFills([{ id: prepPriceDialog.rawId, price, unit: dialogUnit }]);
      setPrepPriceDialog(null);
    } else if (prepPriceDialog?.target === 'negative') {
      setNegativeModalRows(prev => prev.map(r => {
        if (r.item_id !== id) return r;
        const qty = (newQty !== null && newQty !== undefined && newQty !== '' && !isNaN(Number(newQty)))
          ? String(newQty) : r.qty;
        const priced = { ...r, qty, unit_price: String(price) };
        // A unit change re-expresses count AND price together.
        if (!newUnit || String(newUnit).toLowerCase() === String(r.unit).toLowerCase()) return priced;
        const moved = reexpressRowUnit(priced.qty, priced.unit_price, r.unit, newUnit);
        return moved ? { ...priced, unit: newUnit, qty: moved.quantity, unit_price: moved.unit_price } : priced;
      }));
      applyPriceFills([{ id, price, unit: dialogUnit }]);
      setPrepPriceDialog(null);
    } else {
      // 'prep-child': id IS the atomic ingredient id.
      applyPriceFills([{ id, price, unit: dialogUnit }]);
      setPrepPriceDialog(null);
    }
  };

  // ── Spec A1 — owner override of an item's sheet unit (same family only) ──
  // Persisted through PUT /sheet-unit, then the row's figures are re-expressed
  // locally (no reload — the category selection must survive). An entered
  // count and price convert with it so nothing is lost or relabelled.
  const changeSheetUnit = useCallback(async (item) => {
    const opts = csUnitOptions(item.unit);
    if (opts.length < 2) return;
    const cur = String(item.unit).toLowerCase();
    const next = opts.find(u => u !== cur) || cur;
    if (next === cur) return;
    if (!window.confirm(`Count "${item.item_name}" in ${formatUnitLabel(next)} from now on? (currently ${formatUnitLabel(item.unit)})`)) return;
    try {
      const res = await http.put('/closing-stock/sheet-unit', { businessId, item_id: item.item_id, unit: next });
      if (!res || res.success === false) { alert((res && res.error) || 'Could not change the unit'); return; }
      const priceFactor = convertQty(1, next, item.unit);   // old units in one new unit
      // convertQty returns null when the pair cannot be converted. Writing that
      // null into current_stock/last_count blanked the figures the counter reads
      // — the row simply lost its numbers with no explanation. Leave the row
      // exactly as it was and say why instead.
      if (priceFactor === null || !Number.isFinite(priceFactor)) {
        alert(`"${item.item_name}" is stocked in ${formatUnitLabel(item.unit)}, which cannot be expressed in ${formatUnitLabel(next)}. The unit was not changed.`);
        return;
      }
      const keep = (converted, original) =>
        (converted === null || !Number.isFinite(converted)) ? original : converted;
      const conv = (ing) => (ing.item_id !== item.item_id ? ing : {
        ...ing, unit: next,
        current_stock: keep(convertQty(Number(ing.current_stock) || 0, ing.unit, next), ing.current_stock),
        last_count: ing.last_count == null ? ing.last_count : keep(convertQty(Number(ing.last_count), ing.unit, next), ing.last_count),
        latest_vendor_price: (Number(ing.latest_vendor_price) || 0) * priceFactor
      });
      setMasterCategories(prev => prev.map(c => ({ ...c, ingredients: (c.ingredients || []).map(conv) })));
      setUncategorizedIngredients(prev => prev.map(conv));
      // The one stored price is ₹ per the SHEET unit, so it moves with it:
      // ₹33/kg becomes ₹0.033/g. Left alone it would read as ₹33 per gram.
      setIngredientPrices(prev => {
        const s = Number(prev[item.item_id]);
        if (!(Number.isFinite(s) && s > 0)) return prev;
        return { ...prev, [item.item_id]: Number((s * priceFactor).toFixed(6)) };
      });
      const key = eKey('raw', item.item_id);
      setEntries(prev => {
        const e = prev[key];
        if (!e) return prev;
        const out = { ...e };
        delete out.unit;   // follow the item's unit again
        if (csHasCount(e.quantity)) {
          const q = convertQty(Number(e.quantity), item.unit, next);
          // Never overwrite a real count with 'null'.
          if (q !== null && Number.isFinite(q)) out.quantity = String(q);
        }
        if (e.unit_price !== undefined && e.unit_price !== '') out.unit_price = String(Number(e.unit_price) * priceFactor);
        return { ...prev, [key]: out };
      });
    } catch (e) {
      alert('Could not change the unit: ' + (e.message || 'unknown error'));
    }
  }, [businessId]);

  // ── Inline unit switch on a counted row (Consolidated Sheet) ──────────────
  // Distinct from changeSheetUnit above: that one changes the item's unit for
  // EVERY future sheet and persists it. This one re-expresses just this sheet's
  // row, which is what the inline dropdown means. Both obey the same rule — the
  // count converts and the price rebases, so the row's value is unchanged.
  const changeRowUnit = useCallback((rowKey, fromUnit, toUnit) => {
    if (!toUnit || String(fromUnit).toLowerCase() === String(toUnit).toLowerCase()) return;
    setEntries(prev => {
      const e = prev[rowKey] || {};
      const moved = reexpressRowUnit(e.quantity, e.unit_price, fromUnit, toUnit);
      // Unconvertible (different families, unknown symbol): change nothing at
      // all rather than relabel a number into a unit it was never counted in.
      if (!moved) return prev;
      // Only write back the fields the row actually had. Adding `quantity:
      // undefined` to a row that never carried one would make an uncounted row
      // look touched to everything that inspects the entry's own keys.
      const next = { ...e, unit: toUnit };
      if (e.quantity !== undefined) next.quantity = moved.quantity;
      if (e.unit_price !== undefined) next.unit_price = moved.unit_price;
      return { ...prev, [rowKey]: next };
    });
  }, []);

  // Negative Stock Clearance rows live in their own working copy, not `entries`,
  // but the rule is the same one.
  const changeNegativeRowUnit = useCallback((row, toUnit) => {
    if (!toUnit || String(row.unit).toLowerCase() === String(toUnit).toLowerCase()) return;
    const moved = reexpressRowUnit(row.qty, row.unit_price, row.unit, toUnit);
    if (!moved) return;
    setNegativeModalRows(prev => prev.map(r => (r.item_id !== row.item_id ? r : {
      ...r, unit: toUnit, qty: moved.quantity, unit_price: moved.unit_price,
    })));
  }, []);
  // ── Render Prep Row ─────────────────────────────────────────
  // Renders a preparation, a complimentary recipe OR a menu item (dish) — they
  // differ only in how they scale (batch vs portion vs plate) and in their id
  // space, so `kind` drives both.
  const renderPrepRow = (item, kind, rowNumber) => {
    const isComp = kind === 'comp';
    const isMenu = kind === 'menu';
    // A dish with no recipe cannot decompose; it is shown greyed and read-only
    // rather than hidden, so it is obvious why it cannot be counted.
    const noRecipe = isMenu && !item.has_recipe;
    // Stable keys across all tabs; 'prep'/'comp'/'menu' ids overlap, so never bare.
    const key = eKey(kind, item.item_id);
    const entry = entries[key] || {};
    const qty = entry.quantity !== undefined ? entry.quantity : '';
    const numQty = Number(qty || 0);
    // Fully expand to atomic raw ingredients (nested preps resolved).
    const expandedRaws = (numQty > 0 && !noRecipe)
      ? (isMenu ? getExpandedMenuRaws(item, numQty)
        : isComp ? getExpandedCompRaws(item, numQty)
          : getExpandedPrepRaws(item, numQty))
      : [];
    const breakdownPending = expandedRaws === null;
    const showComponentTable = !breakdownPending && expandedRaws.length > 0;
    const componentTable = showComponentTable ? (
      <div className="cs-prep-component-table">
        <div className="cs-prep-component-head">
          <span>Item</span>
          <span>Qty</span>
          <span>Unit</span>
          <span>Price</span>
          <span>Total</span>
        </div>
        {expandedRaws.map((raw, i) => {
          const compQty = raw.qty;              // already scaled, in the ingredient's sheet unit
          const compUnit = raw.unit || '';
          const compKey = componentPriceKey(
            isMenu ? 'menu' : isComp ? 'complimentary' : 'preparation',
            item.item_id, raw.atomic_item_id
          );
          const compName = raw.item_name;

          // Shared ingredient price → legacy per-parent → vendor price.
          const unitPrice = componentEffectivePrice(
            isMenu ? 'menu' : isComp ? 'complimentary' : 'preparation',
            item.item_id, raw.atomic_item_id
          );

          const hasPrice = typeof unitPrice === 'number' && Number.isFinite(unitPrice);
          const compTotal = hasPrice ? compQty * unitPrice : 0;
          if (raw.unresolvable) {
            return (
              <div key={compKey} className="cs-prep-component-row cs-prep-component-skipped">
                <span className="cs-prep-component-name">
                  <span className="cs-prep-component-index">{i + 1}.</span>
                  <span className="cs-prep-component-label">{compName || '-'}</span>
                </span>
                <span className="cs-prep-component-skip-note" style={{ gridColumn: '2 / -1' }}>
                  &#9888; not credited &mdash; {raw.reason}
                </span>
              </div>
            );
          }
          return (
            <div key={compKey} className="cs-prep-component-row">
              <span className="cs-prep-component-name">
                <span className="cs-prep-component-index">{i + 1}.</span>
                <span className="cs-prep-component-label">{compName || '-'}</span>
              </span>
              <span className="cs-prep-component-qty">
                {formatFixedDot(compQty, 2)}
              </span>
              <span className="cs-prep-component-unit">{compUnit}</span>
              <button
                type="button"
                className="cs-prep-price-btn"
                onClick={() => openPrepPriceDialog({ item_name: compName, unit_symbol: compUnit }, raw.atomic_item_id, compQty, compUnit, unitPrice)}
                disabled={!lockActive}
              >
                {hasPrice && unitPrice > 0 ? `\u20B9 ${unitPrice.toFixed(2)}` : '\u20B9 Price'}
              </button>
              <span className="cs-prep-component-total">
                {compTotal > 0 ? compTotal.toFixed(2) : '\u2014'}
              </span>
            </div>
          );
        })}
      </div>
    ) : null;

    return (
      <div key={key} className={`cs-item-row cs-prep-expand-row${noRecipe ? ' cs-no-recipe-row' : ''}`}>
        <div className="cs-prep-main-line">
          <div className="cs-item-name-cell">
            <div className="cs-item-number">{rowNumber}.</div>
            <div className="cs-item-name-wrap">
              <div className="cs-item-name">{item.item_name}</div>
              <div className="cs-item-name-sub">
                {isMenu
                  ? (noRecipe
                    ? 'No recipe'
                    : `Per plate · ${item.component_count} ingredient${item.component_count === 1 ? '' : 's'}${item.is_active === false ? ' · Off menu' : ''}`)
                  : isComp
                    ? `Per portion · ${item.component_count} ingredient${item.component_count === 1 ? '' : 's'}`
                    : `Batch: ${formatQuantity(item.batch_yield, item.unit)} · ${item.component_count} ingredients`}
              </div>
              {noRecipe && (
                <div className="cs-no-recipe-label">
                  &#9888; No recipe &mdash; this dish cannot be broken down, so it cannot be counted
                </div>
              )}
            </div>
          </div>
          <input
            type="number"
            className="cs-qty-input"
            placeholder={noRecipe ? '—' : 'Qty'}
            value={noRecipe ? '' : qty}
            onChange={(e) => handleQuantityChange(key, item.item_id, e.target.value, item.unit)}
            onFocus={(e) => {
              if (e.target.value !== '') {
                lastValidQuantityRef.current[key] = e.target.value;
              }
            }}
            onBlur={(e) => handleQuantityBlur(key, e.target.value, item.unit, item.item_name)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && e.target.value === '') {
                e.preventDefault();
                const prev = lastValidQuantityRef.current[key];
                if (prev !== undefined && prev !== '') {
                  setRemoveItemConfirm({ key, itemName: item.item_name, previousQuantity: prev });
                }
              }
            }}
            disabled={!lockActive || noRecipe}
            min="0"
            step="0.0001"
            inputMode="decimal"
          />
          <div className="cs-item-unit">{formatUnitLabel(item.unit)}</div>
        </div>
        {componentTable}
      </div>
    );
  };

  // ── Render Item Row ─────────────────────────────────────────
  const renderItemRow = (item, catId, rowNumber, nextItemId) => {
    // Item 4 — single canonical value per item, shared across every tab.
    const key = eKey('raw', item.item_id);
    const entry = entries[key] || {};
    const qty = entry.quantity !== undefined ? entry.quantity : '';
    // The box shows exactly what was typed into it; otherwise the ingredient's
    // one price (set anywhere — a recipe, the popup, an import) or its vendor
    // price, per the unit this row is counted in.
    const rowUnit = entry.unit || item.unit;
    const shownPrice = ingredientPriceIn(item.item_id, rowUnit);
    const price = entry.unit_price !== undefined ? entry.unit_price : (shownPrice > 0 ? String(shownPrice) : '');
    const total = Number(qty || 0) * Number(price || 0);
    const flag = flags[item.item_id];
    const isCounted = qty !== '' && qty !== undefined;        // Item 3: number OR explicit 0
    const isZero = isCounted && Number(qty) === 0;
    const tabName = catId === 'uncategorized'
      ? 'Other'
      : (masterCategories.find(c => c.master_category_id === catId)?.master_category_name || '');
    const countedElsewhere = isCounted && entry.sourceTab !== undefined
      && entry.sourceTab !== catId && entry.sourceTabName;
    const rowClass = `cs-item-row${flag?.status === 'warning' ? ' flagged-warning' : flag?.status === 'blocked' ? ' flagged-blocked' : ''}`;

    return (
      <div key={key} className={rowClass}>
        <div className="cs-item-name-cell">
          <div className="cs-item-number">{rowNumber}.</div>
          <div className="cs-item-name-wrap">
            <div className="cs-item-name">
              {item.item_name}
              <span className={`cs-status-dot ${isCounted ? 'counted' : 'notcounted'}`} aria-hidden="true" style={{ marginLeft: '6px', marginRight: '0' }} />
            </div>
            {flag ? (
              <div className="cs-item-name-sub" style={{ color: flag.status === 'blocked' ? '#f44336' : '#ff9800' }}>
                {flag.status === 'blocked' && (
                  <button
                    onClick={() => openPinModal(item.item_id)}
                    style={{ background: 'none', border: 'none', color: '#4361ee', fontSize: 10, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    Override PIN
                  </button>
                )}
                {' '}{flag.message?.slice(0, 60)}
              </div>
            ) : (
              <div className="cs-item-name-sub cs-item-status-line">
                {item.current_stock > 0 && (
                  <span className="cs-status-current">Current: {formatQty(item.current_stock, item.unit)}</span>
                )}
              </div>
            )}
            {countedElsewhere && (
              <div className="cs-item-name-sub cs-already-counted">
                Already counted: {formatQty(Number(qty), item.unit)} in {entry.sourceTabName}
              </div>
            )}
            {renderShelfPicker(item)}
          </div>
        </div>
        <input
          type="number"
          ref={el => { inputRefs.current[`qty_${item.item_id}`] = el; }}
          className={`cs-qty-input${flag?.status === 'warning' ? ' warning' : flag?.status === 'blocked' ? ' blocked' : ''}`}
          placeholder="Qty"
          value={qty}
          onChange={(e) => handleRawQuantityChange(item.item_id, e.target.value, item.unit, catId, tabName)}
          onFocus={(e) => {
            if (e.target.value !== '') {
              lastValidQuantityRef.current[key] = e.target.value;
            }
          }}
          onBlur={(e) => handleQuantityBlur(key, e.target.value, item.unit, item.item_name)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              inputRefs.current[`price_${item.item_id}`]?.focus();
            } else if (e.key === 'Backspace' && e.target.value === '') {
              e.preventDefault();
              const prev = lastValidQuantityRef.current[key];
              if (prev !== undefined && prev !== '') {
                setRemoveItemConfirm({ key, itemName: item.item_name, previousQuantity: prev });
              }
            }
          }}
          disabled={!lockActive}
          min="0"
          step="0.0001"
          inputMode="decimal"
        />
        <button
          type="button"
          className="cs-item-unit cs-item-unit-btn"
          title={csUnitOptions(item.unit).length > 1 ? `Change this item's sheet unit (${csUnitOptions(item.unit).map(formatUnitLabel).join(' / ')})` : undefined}
          disabled={!lockActive || csUnitOptions(item.unit).length < 2}
          onClick={() => changeSheetUnit(item)}
        >{formatUnitLabel(item.unit)}</button>
        <input
          type="number"
          ref={el => { inputRefs.current[`price_${item.item_id}`] = el; }}
          className="cs-qty-input"
          placeholder="&#x20B9; Price"
          value={price}
          onChange={(e) => {
            // Keep the typed text (so "33." and "0.5" type naturally) AND make
            // it the ingredient's one price; clearing the box clears it.
            updateEntry(key, 'unit_price', e.target.value);
            setIngredientPrices(prev => storeIngredientPrices(prev,
              [{ id: item.item_id, price: e.target.value, unit: rowUnit }],
              (id, fb) => sheetUnitOfRef.current(id, fb)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (nextItemId != null) inputRefs.current[`qty_${nextItemId}`]?.focus();
              else e.target.blur();
            }
          }}
          disabled={!lockActive}
          min="0"
          step="0.01"
          inputMode="decimal"
          style={{ fontSize: 11 }}
        />
        <div className="cs-item-total">
          {total > 0 ? formatCurrency(total) : '\u2014'}
        </div>
      </div>
    );
  };

  // ── Render Ingredient List ──────────────────────────────────
  const renderIngredientList = (ingredients, catId, startNumber = 0) => {
    const isAllTab = catId === 'all';
    return (
      <div className="cs-item-list">
        <div className="cs-item-row" style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '10px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#000', textTransform: 'uppercase', textAlign: 'left', justifySelf: 'start' }}>Item</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#000', textTransform: 'uppercase', textAlign: 'left', paddingLeft: 25 }}>Qty</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#000', textTransform: 'uppercase', textAlign: 'center' }}>Unit</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#000', textTransform: 'uppercase', textAlign: 'left', paddingLeft: 25 }}>Price</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#000', textTransform: 'uppercase', textAlign: 'right', marginLeft: 6 }}>Total</div>
        </div>
        {isAllTab
          ? ingredients.map((item, idx) => renderAllTabItemRow(item, startNumber + idx + 1))
          : ingredients.map((item, idx) => renderItemRow(item, catId, startNumber + idx + 1, ingredients[idx + 1]?.item_id))
        }
      </div>
    );
  };

  // ── Render All Tab Item Row (read-only summed view) ─────────
  // The shelf an item is counted on. Rendered on every raw row, because the row
  // is where a mis-filing is noticed: mid-count, in front of the shelf the item
  // is NOT on. One definition so the two row renderers cannot drift.
  const renderShelfPicker = (item) => (stockCategories.length === 0 ? null : (
    <div className="cs-item-name-sub cs-item-shelf">
      <select
        className="cs-item-shelf-select"
        aria-label={`Stock category for ${item.item_name}`}
        disabled={savingCategoryFor === item.item_id}
        value={String(
          itemCategories[item.item_id] !== undefined
            ? (itemCategories[item.item_id] ?? '')
            : (item.category_id ?? '')
        )}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => changeItemCategory(item.item_id, e.target.value)}
      >
        <option value="">Other / Uncategorized</option>
        {stockCategories.filter(c => c.consumable).map(c => (
          <option key={c.category_id} value={c.category_id}>{c.name}</option>
        ))}
      </select>
    </div>
  ));

  const renderAllTabItemRow = (item, rowNumber) => {
    // Item 4 — read the single canonical value; never sum across tabs.
    const entry = entries[eKey('raw', item.item_id)] || {};
    const isCounted = entry.quantity !== undefined && entry.quantity !== '';
    const totalQty = isCounted ? Number(entry.quantity) : 0;
    const isZero = isCounted && totalQty === 0;
    const bestPrice = ingredientPriceIn(item.item_id, entry.unit || item.unit);

    const total = totalQty * bestPrice;
    const flag = flags[item.item_id];
    const rowClass = `cs-item-row cs-all-tab-row${flag?.status === 'warning' ? ' flagged-warning' : flag?.status === 'blocked' ? ' flagged-blocked' : ''}`;

    const matchingCats = masterCategories.filter(cat =>
      (cat.ingredients || []).some(ing => ing.item_id === item.item_id)
    );
    const categories = matchingCats.length > 0
      ? matchingCats.map(c => ({ id: c.master_category_id, name: c.master_category_name }))
      : [{ id: 'uncategorized', name: 'Other' }];

    const handleQtyClick = () => {
      setCategoryGuideItem({
        itemId: item.item_id,
        itemName: item.item_name,
        categories,
        breakdown: getItemBreakdown(item.item_id)
      });
    };

    return (
      <div key={`all_${item.item_id}`} className={rowClass}>
        <div className="cs-item-name-cell">
          <div className="cs-item-number">{rowNumber}.</div>
          <div className="cs-item-name-wrap">
            <div className="cs-item-name">
              {item.item_name}
              <span className={`cs-status-dot ${isCounted ? 'counted' : 'notcounted'}`} aria-hidden="true" style={{ marginLeft: '6px', marginRight: '0' }} />
            </div>
            <div className="cs-item-name-sub cs-item-status-line">
              {item.current_stock > 0 && (
                <span className="cs-status-current">Current: {formatQty(item.current_stock, item.unit)}</span>
              )}
            </div>
            {renderShelfPicker(item)}
          </div>
        </div>
        <button
          type="button"
          className="cs-qty-display cs-qty-clickable-dash"
          onClick={handleQtyClick}
          title="Click to enter quantity in respective category"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '6px',
            color: isCounted ? '#111827' : '#9ca3af',
            fontSize: '13px',
            fontWeight: isCounted ? '600' : '500',
            textAlign: 'center',
            transition: 'all 0.15s ease',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {isCounted ? totalQty.toFixed(2) : '\u2014'}
        </button>
        <div className="cs-item-unit">{formatUnitLabel(item.unit)}</div>
        <div className="cs-qty-display" style={{ fontSize: 11 }}>{bestPrice > 0 ? bestPrice.toFixed(2) : '\u2014'}</div>
        <div className="cs-item-total">
          {total > 0 ? formatCurrency(total) : '\u2014'}
        </div>
      </div>
    );
  };


  // ── Main Render ─────────────────────────────────────────────
  if (blockedBySnc) {
    return (
      <div className="closing-stock-page">
        <BlockedBySalesNameCheck onOpen={onOpenSalesNameCheck} />
      </div>
    );
  }

  return (
    <div className="closing-stock-page">
      {/* Download spinner overlay (same app spinner, INVEXIS icon + ring) */}
      {downloading && (
        <div className="cs-download-overlay">
          <div style={{ position: 'relative', width: '95px', height: '95px', marginBottom: '8px' }}>
            <img
              src="/invexis-logo.png"
              alt="Invexis"
              loading="eager"
              style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px', height: '70px', borderRadius: '50%',
                backgroundColor: '#fff', padding: '0px', pointerEvents: 'none',
                objectFit: 'contain', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', zIndex: 1
              }}
            />
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70px', height: '70px',
              border: '6px solid #000000',
              borderTop: '6px solid #f9c806',
              borderRight: '6px solid #f9c806',
              borderRadius: '50%',
              animation: 'cs-logo-spin 1s linear infinite',
              zIndex: 16
            }}></div>
          </div>
          <span>Downloading closing stock…</span>
        </div>
      )}

      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-top">
          <button className="cs-back-btn" onClick={handleHeaderBack} aria-label="Back" title="Back">
            <FontAwesomeIcon icon={faArrowLeft} className="cs-back-icon" />
          </button>
          <div className="cs-title-wrap">
            <div className="cs-title">Closing Stock</div>
          </div>
          <div className="cs-header-actions">
            <div className="cs-lock-toggle">
              <button
                className="cs-clear-btn cs-redo-btn cs-desktop-only"
                type="button"
                onClick={() => setShowRedoConfirm(true)}
                disabled={!redoInfo}
                aria-label="Redo"
                title={redoInfo ? 'Restore the last deleted draft' : 'No deleted draft to restore'}
              >
                <span className="cs-scan-text">Redo</span>
              </button>
              <button
                className="cs-clear-btn cs-desktop-only"
                onClick={() => setShowClearConfirm(true)}
                disabled={isSheetClear}
                aria-label="Clear Sheet"
                title="Clear all counts and saved drafts"
              >
                <span className="cs-scan-text">Clear Sheet</span>
              </button>
              <button
                className="cs-scan-btn"
                onClick={openScanModeChooser}
                aria-label="Scan Sheet"
                title="Scan Sheet"
              >
                <FaQrcode className="cs-scan-icon" />
                <span className="cs-scan-text">Scan Sheet</span>
              </button>
            </div>
            <div className="cs-corner-menu-wrap" ref={menuRef}>
              <button className="cs-menu-btn cs-corner-menu-btn" onClick={() => setShowCornerMenu(!showCornerMenu)} aria-label="More options">
                <FaEllipsisV />
              </button>
              {showCornerMenu && (
                <div className="cs-corner-menu-dropdown">
                  <button className="cs-download-option" onClick={() => openDownloadChoice('template', 'pdf')}>Template (PDF)</button>
                  <button className="cs-download-option" onClick={() => openDownloadChoice('template', 'xlsx')}>Template (Excel)</button>
                  {/* The other half of the Excel template loop, next to the download. */}
                  <button className="cs-download-option" onClick={() => { setShowCornerMenu(false); sheetFileInputRef.current?.click(); }}>Upload Filled Sheet</button>
                  <button className="cs-download-option" onClick={() => openDownloadChoice('report', 'pdf')}>Report (PDF)</button>
                  <button className="cs-download-option" onClick={() => openDownloadChoice('report', 'xlsx')}>Report</button>
                  {(continuous || ccsStaff) && (
                    <button className="cs-download-option" onClick={() => { setShowCornerMenu(false); setShowCcsPanel(true); }}>
                      Months &amp; counts
                    </button>
                  )}
                  <button className="cs-download-option cs-mobile-only" onClick={openScanModeChooser}>Scan Sheet</button>
                  <button
                    className="cs-download-option cs-mobile-only"
                    onClick={() => { setShowCornerMenu(false); setShowRedoConfirm(true); }}
                    disabled={!redoInfo}
                  >
                    Redo
                  </button>
                  <button
                    className="cs-download-option cs-mobile-only"
                    onClick={() => { setShowCornerMenu(false); setShowClearConfirm(true); }}
                    disabled={isSheetClear}
                  >
                    Clear Sheet
                  </button>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={ocrFileInputRef}
              onChange={handleOCRFileSelect}
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              style={{ display: 'none' }}
              multiple
            />
            {/* Separate camera input: `capture` and `multiple` are mutually
                exclusive on mobile, so one shot at a time comes through here. */}
            <input
              type="file"
              ref={ocrCameraInputRef}
              onChange={handleOCRFileSelect}
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
            />
            {/* Filled template — parsed directly, never sent to the AI. */}
            <input
              type="file"
              ref={sheetFileInputRef}
              onChange={handleSheetInputChange}
              accept={SHEET_ACCEPT}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>


      {/* The chosen date, on every step (spec §4.8 "After Continue"). Replaces the old
          "Backdated entry" banner: a date before today is now normal, and the app only
          adjusts for sales and deliveries when the shelves were counted on a later day. */}
      {dateChosen && (
        <div className="cs-date-header">
          <span>Closing stock for <strong>{formatLongDate(closingDate)}</strong></span>
          <button type="button" className="cs-date-header-change" onClick={() => setShowDatePicker(true)}>
            Change date
          </button>
          {rollbackRequested && (
            <span className="cs-date-header-note">
              Counted on {formatLongDate(countedDate)} — sales and deliveries in between are adjusted.
            </span>
          )}
        </div>
      )}

      <ClosingDatePicker
        open={showDatePicker}
        firstOpen={!dateChosen}
        currentDate={dateChosen ? closingDate : null}
        currentCountedDate={dateChosen ? countedDate : null}
        hasWork={enteredCount > 0 || Boolean(resumeDraft)}
        onCancel={() => {
          if (dateChosen) setShowDatePicker(false);
          else goTo(isOnboarding ? 'inventory' : 'settings');
        }}
        onConfirm={({ closingDate: chosen, countedDate: counted, managerPin: pin, movedDraft }) => {
          if (chosen !== closingDate || movedDraft) draftUpdatedAtRef.current = null;
          setClosingDate(chosen);
          setCountedDate(counted || chosen);
          setManagerPin(pin || null);
          setDateChosen(true);
          setShowDatePicker(false);
        }}
      />



      <div className={`cs-page-content cs-lovable-body${(stockStep === 'summary' || stockStep === 'done') ? ' cs-summary-page-content' : ''}${stockStep === 'overview' ? ' cs-overview-body' : ''}`}>
        {/* Resume-draft Banner */}
        {resumeDraft && stockStep === 'overview' && (
          <div className="cs-resume-banner">
            <span className="cs-resume-text">
              {/* A draft keyed to another date (saved before midnight) must say
                  so — resuming it continues THAT day's count, not today's. */}
              {resumeDraft.fellBack && (resumeDraft.closing_date || resumeDraft.payload?.closing_date)
                ? `Draft for ${String(resumeDraft.closing_date || resumeDraft.payload?.closing_date).slice(0, 10)}`
                : resumeDraft.payload?.manual
                  ? 'Draft saved'
                  : `Unsaved count from ${resumeDraft.created_at
                      ? new Date(resumeDraft.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                      : 'earlier'}`}
              {typeof resumeDraft.expires_in_days === 'number'
                ? ` · expires in ${Math.max(1, Math.ceil(resumeDraft.expires_in_days))} day${Math.ceil(resumeDraft.expires_in_days) !== 1 ? 's' : ''}`
                : ''}
              {resumeDraft.payload?.manual ? ' · want to continue?' : ''}
            </span>
            <span className="cs-resume-actions">
              <button className="cs-resume-btn discard" onClick={discardDraft}>Discard</button>
              <button className="cs-resume-btn resume" onClick={resumeFromDraft}>
                {resumeDraft.payload?.manual ? 'Continue' : 'Resume'}
              </button>
            </span>
          </div>
        )}
        {loading ? (
          <div className="cs-loading">
            <div style={{ position: 'relative', width: '95px', height: '95px', marginBottom: '8px' }}>
              <img
                src="/invexis-logo.png"
                alt="Invexis"
                loading="eager"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  padding: '0px',
                  pointerEvents: 'none',
                  objectFit: 'contain',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  zIndex: 1
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px',
                height: '70px',
                border: '6px solid #000000',
                borderTop: '6px solid #f9c806',
                borderRight: '6px solid #f9c806',
                borderRadius: '50%',
                animation: 'cs-logo-spin 1s linear infinite',
                zIndex: 16
              }}></div>
            </div>
            <span>Loading...</span>
          </div>
        ) : (
          <>
            {stockStep === 'overview' && (
              <div className="cs-flow-overview">
                <h3 className="cs-flow-title">Steps to ensure a smooth closing stock</h3>
                <ul className="cs-flow-steps">
                  {[
                    ['1', 'Select Master Categories', 'Checklist pop-up'],
                    ['2', 'Inventory Entry', 'Category tabs + scan'],
                    ['3', 'Verification', 'Confirm anomalies'],
                    ['4', 'Consolidated Sheet', 'Sum of all values'],
                    ['5', 'Confirm', 'final verification']
                  ].map(([n, label, sub]) => (
                    <li key={n} className="cs-flow-step">
                      <span className="cs-flow-step-number">{n}</span>
                      <span className="cs-flow-step-copy">
                        <strong>{label}</strong>
                        <small>{sub}</small>
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  className="cs-primary-wide-btn"
                  disabled={unmappedGateChecking}
                  onClick={async () => {
                    // Gate BEFORE the checklist (spec §5.3). The server decides; this
                    // screen only shows the answer. FAIL CLOSED: intake starts only on
                    // an explicit "not blocking" — a thrown error or a { success:false }
                    // (403) shows Retry instead. /closing-stock/apply enforces it anyway.
                    setUnmappedGateChecking(true);
                    let proceed = false;
                    try {
                      const g = await checkGate(closingDate);
                      if (!g.ok) setUnmappedGate({ failed: true, error: g.error });
                      else if (g.blocking === true) setUnmappedGate({ count: g.count, amount: g.amount, dishes: g.dishes || [], from: 'intake' });
                      else proceed = true;
                      // Not in Menu passed: Not in Recipe next (a no-op unless it applies).
                      if (proceed) proceed = await checkNotInRecipeThen('intake');
                    } finally {
                      setUnmappedGateChecking(false);
                    }
                    if (proceed) setShowSelectionChecklist(true);
                  }}
                >
                  {unmappedGateChecking ? 'Checking…' : 'Start Closing Stock Intake'}
                </button>
              </div>
            )}

            {stockStep === 'entry' && (
              <>
                <div className="cs-sticky-search-tabs-header">
                  <div style={{ padding: '0', marginTop: '0', marginBottom: '8px' }}>
                    <StandardSearch
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onClear={() => setSearchTerm('')}
                      placeholder="Search items..."
                      showClearButton={true}
                    />
                  </div>

                  {/* ═══ MASTER CATEGORY TABS ═══ */}
                  <div className="cs-master-tabs-bar">
                    <button
                      className={`cs-master-tab${activeTab === 'all' ? ' active' : ''}`}
                      onClick={() => setActiveTab('all')}
                    >
                      All
                    </button>
                    {selectedMasterCategories.map(cat => {
                      const tc = tabCounts[cat.master_category_id];
                      return (
                        <button
                          key={cat.master_category_id}
                          className={`cs-master-tab${activeTab === cat.master_category_id ? ' active' : ''}`}
                          onClick={() => setActiveTab(cat.master_category_id)}
                        >
                          {cat.master_category_name}
                          {tc && tc.total > 0 && (
                            <span className="cs-tab-count">{tc.counted}/{tc.total}</span>
                          )}
                        </button>
                      );
                    })}
                    {uncategorizedIngredients.length > 0 && (
                      <button
                        className={`cs-master-tab${activeTab === 'uncategorized' ? ' active' : ''}`}
                        onClick={() => setActiveTab('uncategorized')}
                      >
                        Other
                        {tabCounts['uncategorized'] && tabCounts['uncategorized'].total > 0 && (
                          <span className="cs-tab-count">{tabCounts['uncategorized'].counted}/{tabCounts['uncategorized'].total}</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* ═══ UNIFIED ENTRY CARD: PREPS + RAW INGREDIENTS ═══ */}
                <div className="cs-entry-card cs-existing-raw-section">
                  {filteredTabIngredients.length === 0 && filteredTabPreparations.length === 0
                    && filteredTabComplimentary.length === 0 && filteredTabMenuItems.length === 0 ? (
                    <div className="cs-empty-state">No items match your search.</div>
                  ) : (
                    <>
                      {/* Saved Preparations sub-section */}
                      {filteredTabPreparations.length > 0 && (
                        <div className="cs-prep-inline-section">
                          <div className="cs-inline-section-label">
                            <span>Saved Preparations</span>
                            <span className="cs-inline-label-count">
                              {filteredTabPreparations.length} item{filteredTabPreparations.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="cs-prep-list">
                            {filteredTabPreparations.map((p, i) => renderPrepRow(p, 'prep', i + 1))}
                          </div>
                        </div>
                      )}

                      {/* Complimentary Items sub-section — counted in portions */}
                      {filteredTabComplimentary.length > 0 && (
                        <div className="cs-prep-inline-section">
                          <div className="cs-inline-section-label">
                            <span>Complimentary Items</span>
                            <span className="cs-inline-label-count">
                              {filteredTabComplimentary.length} item{filteredTabComplimentary.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="cs-prep-list">
                            {/* Numbering continues from the preparations above */}
                            {filteredTabComplimentary.map((c, i) => renderPrepRow(c, 'comp', filteredTabPreparations.length + i + 1))}
                          </div>
                        </div>
                      )}

                      {/* Menu Items sub-section — counted in plates. A dish with no
                          recipe is listed greyed out so it is clear why it cannot
                          be counted (see renderPrepRow). */}
                      {filteredTabMenuItems.length > 0 && (
                        <div className="cs-prep-inline-section">
                          <div className="cs-inline-section-label">
                            <span>Menu Items</span>
                            <span className="cs-inline-label-count">
                              {filteredTabMenuItems.length} item{filteredTabMenuItems.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="cs-prep-list">
                            {/* Numbering continues from preparations + complimentary above */}
                            {filteredTabMenuItems.map((m, i) => renderPrepRow(
                              m, 'menu',
                              filteredTabPreparations.length + filteredTabComplimentary.length + i + 1
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Raw Ingredients sub-section */}
                      {filteredTabIngredients.length > 0 && (
                        <div className="cs-raw-inline-section">
                          <div className="cs-inline-section-label">
                            <span>Raw Ingredients</span>
                            <span className="cs-inline-section-count">
                              {filteredTabIngredients.length} item{filteredTabIngredients.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {/* Numbering continues from preparations + complimentary + dishes above */}
                          {renderIngredientList(
                            filteredTabIngredients,
                            activeTab === 'all' ? 'all' : activeTab,
                            filteredTabPreparations.length + filteredTabComplimentary.length + filteredTabMenuItems.length
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Item 5 — live running total */}
                <div className="cs-entry-footer">
                  <span className="cs-entry-footer-count">
                    {enteredCount} / {totalScopeCount} counted
                  </span>
                  <span className="cs-entry-footer-total">{formatCurrency(totalValue)}</span>
                </div>

                <div className="cs-entry-actions">
                  <button className="cs-secondary-wide-btn cs-move-consolidate-btn" onClick={openVerification} disabled={!lockActive || enteredCount === 0}>
                    Submit for Verification
                  </button>
                  <button className="cs-primary-wide-btn" onClick={moveToConsolidate} disabled={!lockActive || enteredCount === 0}>
                    Move to Consolidate
                  </button>
                </div>
              </>
            )}

            {stockStep === 'verify' && (
              <div className="cs-entry-card cs-verification-backdrop">
                <div className="cs-skeleton-list">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="cs-skeleton-row">
                      <span />
                      <div><b /><small /></div>
                      <em />
                      <i />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stockStep === 'summary' && (
              <div className="cs-summary-screen">
                <div className="cs-summary-card">
                  {twinCounts.length > 0 && (
                    <div className="cs-twin-notice" data-testid="cs-twin-notice" role="status">
                      <div className="cs-twin-notice-text">
                        <strong>Counted on a saved preparation&rsquo;s stock row &mdash; these will not be saved:</strong>
                        <ul>
                          {twinCounts.map(t => (
                            <li key={t.key}>
                              {t.item_name} &middot; {t.entry.quantity}{t.entry.unit ? ` ${t.entry.unit}` : ''} &rarr; count it on the
                              {' '}&ldquo;{t.prep_name}&rdquo; preparation row instead
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button type="button" className="cs-twin-notice-btn" onClick={removeTwinCounts}>
                        Remove these rows
                      </button>
                    </div>
                  )}
                  <div className="cs-summary-header-bar">
                    <h2 className="cs-summary-title">Consolidated Sheet</h2>
                    <div className="cs-summary-header-actions">
                      <div className="cs-summary-search-wrap">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="cs-summary-search-icon">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                          type="text"
                          className="cs-summary-search-input"
                          placeholder="Search items..."
                          value={summarySearchTerm}
                          onChange={e => setSummarySearchTerm(e.target.value)}
                        />
                        {summarySearchTerm && (
                          <button
                            type="button"
                            className="cs-summary-search-clear"
                            onClick={() => setSummarySearchTerm('')}
                            aria-label="Clear search"
                          >
                            &times;
                          </button>
                        )}
                      </div>

                      <div className="cs-consolidated-add-more-wrap" ref={addMoreRef}>
                        <button
                          type="button"
                          className="cs-consolidated-add-more-btn"
                          onClick={() => setShowAddMoreChooser(!showAddMoreChooser)}
                        >
                          Add More
                        </button>
                        {showAddMoreChooser && (
                          <div className="cs-corner-menu-dropdown" style={{ right: 0, top: 'calc(100% + 4px)', minWidth: '160px', transform: 'none' }}>
                            <button
                              type="button"
                              className="cs-download-option"
                              onClick={() => { setShowAddMoreChooser(false); openScanPicker(); }}
                            >
                              Capture Image
                            </button>
                            <button
                              type="button"
                              className="cs-download-option"
                              onClick={() => { setShowAddMoreChooser(false); openManualEntry(); }}
                            >
                              Enter Manually
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cs-summary-grid cs-summary-head">
                    <span>Item Name</span><span>Qty</span><span>Unit</span><span>Unit &#x20B9;</span><span>Total &#x20B9;</span>
                  </div>
                  {filteredSummaryRows.length === 0 ? (
                    <div className="cs-empty-state">No items match &quot;{summarySearchTerm}&quot;</div>
                  ) : (
                    (() => {
                      let mainItemIndex = 0;
                      return filteredSummaryRows.map((row, idx) => {
                        if (row.kind === 'prep-header') {
                          mainItemIndex += 1;
                          const itemNum = mainItemIndex;
                          const prepKey = eKey(kindOfType(row.item_type), row.item_id);
                          const pe = entries[prepKey] || {};
                          
                          // Find all matching children that follow this header in the filtered list
                          const children = [];
                          for (let j = idx + 1; j < filteredSummaryRows.length; j++) {
                            const nextRow = filteredSummaryRows[j];
                            if (nextRow.kind === 'prep-child') {
                              children.push(nextRow);
                            } else {
                              break;
                            }
                          }

                          return (
                            <React.Fragment key={row.key}>
                              <div className="cs-summary-grid cs-summary-row cs-summary-prep-header">
                                <span>
                                  <span className="cs-summary-item-num">{itemNum}.</span>
                                  <span className="cs-summary-name">{row.name}</span>
                                  {row.hasChildren && (
                                    <span className="cs-summary-prep-tag">(breaks down to)</span>
                                  )}
                                  {row.breakdownPending && (
                                    // The server owns the breakdown now. Saying so is
                                    // the point: a locally guessed one is what used to
                                    // disagree with what /apply actually credited.
                                    <span className="cs-summary-prep-tag cs-breakdown-pending">breakdown unavailable</span>
                                  )}
                                </span>
                                <input
                                  className="cs-summary-edit cs-edit-center" type="number" inputMode="decimal"
                                  min="0" step="0.0001" value={pe.quantity !== undefined ? pe.quantity : ''}
                                  onChange={e => updateEntry(prepKey, 'quantity', e.target.value)}
                                  onFocus={(e) => {
                                    if (e.target.value !== '') {
                                      lastValidQuantityRef.current[prepKey] = e.target.value;
                                    }
                                  }}
                                  onBlur={e => handleQuantityBlur(prepKey, e.target.value, row.unit, row.name)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Backspace' && e.target.value === '') {
                                      e.preventDefault();
                                      const prev = lastValidQuantityRef.current[prepKey];
                                      if (prev !== undefined && prev !== '') {
                                        setRemoveItemConfirm({ key: prepKey, itemName: row.name, previousQuantity: prev });
                                      }
                                    }
                                  }}
                                />
                                {/* Batch unit is structural (drives the breakdown), so it stays fixed. */}
                                <span className="cs-edit-center">{row.unit}</span>
                                <span className="cs-edit-right">&mdash;</span>
                                <span className="cs-edit-right">&mdash;</span>
                              </div>
                              
                              {children.length > 0 && (
                                <div className="cs-prep-component-table cs-summary-prep-table">
                                  <div className="cs-prep-component-head">
                                    <span>Item</span>
                                    <span>Qty</span>
                                    <span>Unit</span>
                                    <span>Price</span>
                                    <span>Total</span>
                                  </div>
                                  {children.map((childRow, i) => {
                                    if (childRow.unresolvable) {
                                      return (
                                        <div key={childRow.key} className="cs-prep-component-row cs-prep-component-skipped">
                                          <span className="cs-prep-component-name">
                                            <span className="cs-prep-component-index">{i + 1}.</span>
                                            <span className="cs-prep-component-label cs-summary-name">{childRow.name}</span>
                                          </span>
                                          <span className="cs-prep-component-skip-note" style={{ gridColumn: '2 / -1' }}>
                                            &#9888; not credited &mdash; {childRow.reason}
                                          </span>
                                        </div>
                                      );
                                    }
                                    const parts = formatQuantityParts(childRow.quantity, childRow.unit);
                                    // The ingredient's one price (componentEffectivePrice), same as
                                    // its own row and the popup — no per-recipe override in front.
                                    const priceVal = childRow.unitPrice ?? '';
                                    const total = (Number(childRow.quantity) || 0) * (Number(priceVal) || 0);
                                    
                                    return (
                                      <div key={childRow.key} className="cs-prep-component-row">
                                        <span className="cs-prep-component-name">
                                          <span className="cs-prep-component-index">{i + 1}.</span>
                                          <span className="cs-prep-component-label cs-summary-name">{childRow.name}</span>
                                        </span>
                                        <span className="cs-prep-component-qty">{parts.qty}</span>
                                        <span className="cs-prep-component-unit">{parts.unit}</span>
                                        <button
                                          type="button"
                                          className="cs-prep-price-btn"
                                          onClick={() => openPrepChildPriceDialog(childRow, priceVal)}
                                          disabled={!lockActive}
                                        >
                                          {priceVal !== '' && priceVal > 0 ? `\u20B9 ${Number(priceVal).toFixed(2)}` : '\u20B9 Price'}
                                        </button>
                                        <span className="cs-prep-component-total">
                                          {total > 0 ? total.toFixed(2) : '\u2014'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        }

                        if (row.kind === 'prep-child') {
                          return null;
                        }

                        if (row.kind === 'neg-section-header') {
                          return (
                            <div key={row.key} className="cs-summary-neg-section">
                              <span className="cs-summary-neg-title">
                                &#9888; Negative Stock Clearance
                              </span>
                              <span className="cs-summary-neg-sub">
                                {row.count} item{row.count === 1 ? '' : 's'} counted to clear oversell
                              </span>
                            </div>
                          );
                        }

                        // Raw row — qty, unit and price all editable.
                        mainItemIndex += 1;
                        const itemNum = mainItemIndex;
                        const rawKey = eKey('raw', row.item_id);
                        const e = entries[rawKey] || {};
                        const qtyVal = e.quantity !== undefined ? e.quantity : '';
                        const unitVal = String(e.unit || row.unit || '').toLowerCase();
                        const priceVal = (e.unit_price !== undefined && e.unit_price !== '') ? e.unit_price : (row.unitPrice ?? '');
                        const total = (Number(qtyVal) || 0) * (Number(priceVal) || 0);

                        return (
                          <div key={row.key} className="cs-summary-grid cs-summary-row">
                            <span>
                              <span className="cs-summary-item-num">{itemNum}.</span>
                              <span className="cs-summary-name">{row.name}</span>
                            </span>
                              <input
                                className="cs-summary-edit cs-edit-center" type="number" inputMode="decimal"
                                min="0" step="0.0001" value={qtyVal}
                                onChange={ev => updateEntry(rawKey, 'quantity', ev.target.value)}
                                onFocus={(e) => {
                                  if (e.target.value !== '') {
                                    lastValidQuantityRef.current[rawKey] = e.target.value;
                                  }
                                }}
                                onBlur={ev => handleQuantityBlur(rawKey, ev.target.value, row.unit, row.name)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' && e.target.value === '') {
                                    e.preventDefault();
                                    const prev = lastValidQuantityRef.current[rawKey];
                                    if (prev !== undefined && prev !== '') {
                                      setRemoveItemConfirm({ key: rawKey, itemName: row.name, previousQuantity: prev });
                                    }
                                  }
                                }}
                              />
                              <SmartUnitDropdown
                                currentUnit={unitVal === 'l' ? 'L' : unitVal}
                                onUnitChange={(unitId, unitSymbol) => {
                                  // Same family (the dropdown only offers kg<->g,
                                  // L<->ml here), so the row is re-expressed, not
                                  // relabelled — count and price move together.
                                  changeRowUnit(rawKey, unitVal, unitSymbol);
                                }}
                                onCrossFamilyConversion={({ newUnit }) => {
                                  updateEntry(rawKey, 'unit', newUnit);
                                }}
                                hideOtherUnits={true}
                                direction="down"
                                borderless={true}
                                minWidth="128px"
                                className="cs-summary-edit cs-summary-edit-unit"
                                style={{ width: '100%' }}
                              />
                              <input
                                className="cs-summary-edit cs-edit-right cs-price-clickable"
                                type="text"
                                readOnly
                                value={priceVal !== '' && priceVal !== undefined ? priceVal : '0'}
                                onClick={() => openRawPriceDialog(row, qtyVal, unitVal, priceVal)}
                                title="Click to set price"
                              />
                              <span className="cs-edit-right">{total.toFixed(2)}</span>
                            </div>
                        );
                      });
                    })()
                  )}
                  {summaryRows.length === 0 && <div className="cs-empty-state">No entries to display.</div>}
                </div>
                <div className="cs-grand-total">
                  <span>Grand Total</span>
                  <strong>{formatCurrency(totalValue)}</strong>
                </div>
                {deferredPriceIds.length > 0 && (
                  <div className="cs-deferred-price-note">
                    {deferredPriceIds.length} item{deferredPriceIds.length > 1 ? 's' : ''} awaiting
                    first-purchase pricing &mdash; not included in the total yet.
                  </div>
                )}
                <div className="cs-summary-actions">
                  <button
                    className="cs-secondary-wide-btn"
                    onClick={saveDraftFromSummary}
                    disabled={savingDraft || allItemsForSummary.length === 0}
                  >
                    {savingDraft ? 'Saving…' : 'Save Draft'}
                  </button>
                  <button className="cs-success-wide-btn" onClick={() => handleApply()} disabled={applying || negChecking || pieceChecking || allItemsForSummary.length === 0}>
                    {applying ? 'Syncing...' : (negChecking || pieceChecking) ? 'Checking…' : 'Confirm & Sync'}
                  </button>
                </div>
                {draftToast && (
                  <div className="cs-draft-toast" role="status">
                    Draft saved — you can leave and continue later
                  </div>
                )}
              </div>
            )}

            {stockStep === 'done' && (
              <div className="cs-done-screen">
                <div className="cs-done-check">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <h2>Closing Stock Completed</h2>
                <div className="cs-done-metrics">
                  <div><small>Items Updated</small><strong>{allItemsForSummary.length}</strong></div>
                  <div><small>Total Value</small><strong>{formatCurrency(totalValue)}</strong></div>
                  <div><small>Status</small><strong>Unlocked</strong></div>
                </div>
                {appliedSkipped.length > 0 && (
                  <div className="cs-done-skipped">
                    <strong>&#9888; {appliedSkipped.length} component{appliedSkipped.length > 1 ? 's were' : ' was'} not credited</strong>
                    <ul>
                      {appliedSkipped.map((sk, i) => (
                        <li key={i}>{sk.item_name}{sk.parent_name ? ` in ${sk.parent_name}` : ''} &mdash; {sk.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {isOnboarding ? (
                  <button className="cs-primary-wide-btn" onClick={onOnboardingComplete}>Complete Onboarding</button>
                ) : (
                  <button className="cs-primary-wide-btn" onClick={() => goTo('dashboard')}>Go to App Overview</button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Unmapped-TSR gate — intake blocked until sales lines are mapped.
          Single primary action by design; the skip escape hatch lives inside
          the mapping flow itself, where the owner can see WHAT they skip. */}
      {unmappedGate && (
        <div className="cs-lovable-modal-overlay">
          <div className="cs-lovable-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="cs-lovable-modal-head">
              <div><h2>{unmappedGate.failed ? "Couldn't check unmapped sales" : 'Map these dishes first'}</h2></div>
              <button onClick={() => setUnmappedGate(null)} aria-label="Close">&times;</button>
            </div>
            <div style={{ padding: '4px 18px 14px' }}>
              {unmappedGate.failed ? (
                <>
                  <p style={{ margin: '6px 0 12px', color: '#374151', fontSize: '14px', lineHeight: 1.55 }}>
                    {unmappedGate.error || 'The check did not answer.'} Counting stock without it could hide sales whose ingredients were never deducted.
                  </p>
                  <button className="cs-primary-wide-btn" onClick={() => setUnmappedGate(null)}>Retry</button>
                </>
              ) : (
                <>
                  <p style={{ margin: '6px 0 10px', color: '#374151', fontSize: '14px', lineHeight: 1.55 }}>
                    {unmappedGate.dishes.length} dish{unmappedGate.dishes.length === 1 ? '' : 'es'} sold since your last count
                    (₹{Number(unmappedGate.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}) did not match any menu item,
                    so their ingredients were never deducted. A count now would hide that inside the count.
                  </p>
                  <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13, color: '#374151' }}>
                    {unmappedGate.dishes.slice(0, 8).map((d) => (
                      <li key={`${d.group || 'sales'}|${d.group_key}`}>{d.item_name} · {d.lines} line{d.lines === 1 ? '' : 's'}</li>
                    ))}
                    {unmappedGate.dishes.length > 8 && <li>…and {unmappedGate.dishes.length - 8} more</li>}
                  </ul>
                  <button className="cs-primary-wide-btn" onClick={() => { setNimFrom(unmappedGate.from || 'intake'); setUnmappedGate(null); setNimOpen(true); }}>Map the items</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <NotInMenuWorklist
        open={nimOpen}
        onClose={() => setNimOpen(false)}
        onProceed={async () => {
          // Same FAIL-CLOSED rule as Start Closing Stock Intake: move on only on an
          // explicit "not blocking" from the server, then resume the step the gate
          // interrupted — the checklist, or the count the server refused (draft kept).
          const g = await checkGate(closingDate);
          if (!g.ok) return { ok: false, error: g.error };
          if (g.blocking === true) {
            return { ok: false, dishes: g.dishes || [], error: `${g.count || 'Some'} sale line(s) still need mapping or skipping.` };
          }
          setNimOpen(false);
          // Not in Recipe comes next, so a business with both kinds of work answers them in order.
          if (!(await checkNotInRecipeThen(nimFrom))) return { ok: true };
          if (nimFrom === 'apply') handleApply();
          else setShowSelectionChecklist(true);
          return { ok: true };
        }}
      />

      {/* Not in Recipe (spec 2026-10-27): items no dish uses, at the count that closes a month. */}
      <NotInRecipePopup
        open={nirOpen}
        through={closingDate}
        fromApply={nirFrom === 'apply'}
        applyMessage={nirFrom === 'apply' ? nirApplyMessage : null}
        onClose={() => { setNirOpen(false); setNirApplyMessage(null); }}
        onProceed={async () => {
          // Same FAIL-CLOSED rule as the Not in Menu worklist: move on only on an explicit
          // "not blocking", then resume the step the check interrupted (draft kept).
          const r = await checkNotInRecipe(closingDate);
          if (!r.ok) return { ok: false, error: r.error };
          if (r.enabled === true && r.blocking === true) {
            return { ok: false, error: `${r.count_left || 'Some'} item(s) still need an expense head or a rate.` };
          }
          setNirOpen(false);
          setNirApplyMessage(null);
          if (nirFrom === 'apply') handleApply();
          else setShowSelectionChecklist(true);
          return { ok: true };
        }}
        onAddToRecipe={async () => {
          // Item Mapping is its own screen. A count already typed is kept as a draft first;
          // the popup's unconfirmed answers are held in memory until the check runs again.
          if (nirFrom === 'apply') {
            try {
              if (!(await saveDraftPayload())) return;
            } catch (e) {
              alert('Could not save the count as a draft: ' + (e.message || 'unknown error'));
              return;
            }
          }
          setNirOpen(false);
          setNirApplyMessage(null);
          goTo('itemmap');
        }}
      />
      {nirGate && (
        <div className="cs-lovable-modal-overlay">
          <div className="cs-lovable-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="cs-lovable-modal-head">
              <div><h2>Couldn&apos;t check items</h2></div>
              <button onClick={() => setNirGate(null)} aria-label="Close">&times;</button>
            </div>
            <div style={{ padding: '4px 18px 14px' }}>
              <p style={{ margin: '6px 0 12px', color: '#374151', fontSize: '14px', lineHeight: 1.55 }}>
                {nirGate.error || 'The check did not answer.'} The count cannot start until every item no dish uses has an expense head.
              </p>
              <button className="cs-primary-wide-btn" onClick={async () => {
                const from = nirGate.from;
                setNirGate(null);
                if (await checkNotInRecipeThen(from)) {
                  if (from === 'apply') handleApply();
                  else setShowSelectionChecklist(true);
                }
              }}>Retry</button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Checklist Modal */}
      {showSelectionChecklist && (
        <div className="cs-lovable-modal-overlay">
          <div className="cs-lovable-modal" onClick={e => e.stopPropagation()}>
            <div className="cs-lovable-modal-head">
              <div>
                <h2>Select Master Categories</h2>
              </div>
              <button onClick={() => setShowSelectionChecklist(false)} aria-label="Close">&times;</button>
            </div>
            <label className="cs-check-row cs-check-row-strong">
              <input type="checkbox" checked={allChecklistSelected} onChange={selectAllChecklistGroups} />
              <span>Select All</span>
            </label>
            <div className="cs-check-list">
              {checklistGroups.map(group => (
                <label key={group.id} className="cs-check-row">
                  <input type="checkbox" checked={selectedGroupIds.includes(group.id)} onChange={() => toggleChecklistGroup(group.id)} />
                  <span>{group.name} ({group.count} items)</span>
                </label>
              ))}
            </div>
            <div className="cs-check-consolidated">
              <button onClick={startConsolidatedStockTake}>All &middot; Single consolidated workspace</button>
            </div>
            <div className="cs-lovable-modal-actions">
              <button className="cs-secondary-wide-btn" onClick={() => setShowSelectionChecklist(false)}>Cancel</button>
              <button className="cs-primary-wide-btn" onClick={startStockTake} disabled={selectedGroupIds.length === 0}>Continue ({selectedGroupIds.length})</button>
            </div>
          </div>
        </div>
      )}

      {/* Verify Modal */}
      {stockStep === 'verify' && (
        <div className="cs-lovable-modal-overlay">
          <div className="cs-lovable-modal cs-verify-modal">
            <div className="cs-lovable-modal-head">
              <div>
                <h2>Verify Stock Take</h2>
                <p>Review consolidated values before commit.</p>
              </div>
              <button onClick={() => setStockStep('entry')} aria-label="Close">&times;</button>
            </div>
            <div className="cs-verify-stats">
              <div><span>Items counted</span><strong>{enteredCount}</strong></div>
              <div><span>Consolidated value</span><strong>{formatCurrency(totalValue)}</strong></div>
              <div><span>Anomalies</span><strong className={anomalyItems.length ? 'warn' : 'ok'}>{anomalyItems.length}</strong></div>
            </div>
            <label className="cs-verify-check">
              <input type="checkbox" checked={verificationAccepted} onChange={e => setVerificationAccepted(e.target.checked)} />
              <span>I have verified all entries are accurate.</span>
            </label>
            <div className="cs-lovable-modal-actions">
              <button className="cs-secondary-wide-btn" onClick={() => setStockStep('entry')}>Edit Entries</button>
              <button className="cs-primary-wide-btn" disabled={!verificationAccepted} onClick={() => setStockStep('summary')}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Backdate Confirmation */}
      <ContinuousStockPanel
        open={showCcsPanel}
        onClose={() => setShowCcsPanel(false)}
        items={[
          ...(masterCategories || []).flatMap((cat) => (cat.ingredients || []).map((i) => ({ item_id: i.item_id, name: i.item_name || i.name }))),
          ...(uncategorizedIngredients || []).map((i) => ({ item_id: i.item_id, name: i.item_name || i.name })),
        ].filter((i, idx, arr) => i.item_id && arr.findIndex((x) => x.item_id === i.item_id) === idx)}
      />

      {ccsPreview && (
        <CcsPreviewModal
          preview={ccsPreview}
          applying={applying}
          onCancel={() => setCcsPreview(null)}
          onConfirm={() => handleApply({ skipPrice: true, skipNotCounted: true, ccsConfirmed: true })}
        />
      )}

      {showBackdateConfirm && backdatePreview && (
        <div className="cs-modal-overlay" onClick={() => setShowBackdateConfirm(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="cs-modal-title">Backdated Adjustment Preview</div>
            <div className="cs-modal-subtitle">
              Target: {closingDate} &bull; Gap: {backdatePreview.gap_days} days
            </div>
            <div className="cs-backdate-table" style={{ margin: '0 0 16px', boxShadow: 'none', border: '1px solid #eee' }}>
              <div className="cs-bt-header">
                <div>Item</div>
                <div className="cs-bt-cell">Counted</div>
                <div className="cs-bt-cell">+ Sales</div>
                <div className="cs-bt-cell">&minus; Deliv.</div>
                <div className="cs-bt-cell">Adjusted</div>
              </div>
              {backdatePreview.adjustments.map((adj, i) => (
                <div key={i} className="cs-bt-row">
                  <div className="cs-bt-cell name">{adj.item_name}</div>
                  <div className="cs-bt-cell">{adj.physical_count}</div>
                  <div className="cs-bt-cell sales">+{Number(adj.sales_consumed).toFixed(1)}</div>
                  <div className="cs-bt-cell deliveries">&minus;{Number(adj.deliveries_received).toFixed(1)}</div>
                  <div className="cs-bt-cell adjusted">{Number(adj.adjusted_quantity).toFixed(1)}</div>
                </div>
              ))}
            </div>
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setShowBackdateConfirm(false)}>Cancel</button>
              <button className="cs-modal-confirm" onClick={() => handleApply({ skipPrice: true, skipNotCounted: true })} disabled={applying}>
                {applying ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIECE GUARD read-back — counted pcs said back as packets, from the Stock In
          answer, just before the attestation. "Yes" goes on to the attestation. */}
      {piecePackReview && (
        <div className="cs-modal-overlay" onClick={() => setPiecePackReview(null)}>
          <div className="cs-modal cs-piece-pack-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="cs-modal-title">Check pieces and packets</div>
            <div className="cs-modal-subtitle">
              {piecePackReview.length === 1 ? 'This item is' : 'These items are'} counted in pieces. Going by your Stock In:
            </div>
            <div className="cs-piece-pack-list">
              {piecePackReview.map(line => (
                <div key={line.id} className="cs-piece-pack-line">
                  <div className="cs-piece-pack-name">{line.name}</div>
                  <div className="cs-piece-pack-sentence">{packetsSentence(line)}</div>
                  <div className="cs-piece-pack-basis">1 packet = {line.piecesPerPack} pieces</div>
                </div>
              ))}
            </div>
            <div className="cs-piece-pack-question">Is this the count you mean to save?</div>
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setPiecePackReview(null)}>No, change it</button>
              <button
                className="cs-modal-confirm"
                onClick={() => {
                  setPiecePackReview(null);
                  // Every earlier gate has already passed on this run, exactly as the
                  // attestation's own resume below assumes.
                  handleApply({ skipPrice: true, skipNotCounted: true, skipNegative: true, skipPiecePacks: true });
                }}
              >
                Yes, continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attestation (spec C6-4) — mandatory before /apply on every path */}
      <ClosingStockAttestation
        isOpen={showAttestation}
        onCancel={() => setShowAttestation(false)}
        onConfirm={(p) => {
          attestationRef.current = p;
          setShowAttestation(false);
          handleApply({ skipPrice: true, skipNotCounted: true, skipNegative: true });
        }}
      />

      {/* Not-counted Warning (Item 3) */}
      {showNotCountedWarning && (
        <div className="cs-modal-overlay" onClick={() => setShowNotCountedWarning(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="cs-modal-title">{notCountedList.length} item{notCountedList.length !== 1 ? 's' : ''} not counted</div>
            <div className="cs-modal-subtitle">
              Their stock stays unchanged (not set to zero). Review them, or continue to save only the counted items.
            </div>
            <div className="cs-notcounted-list">
              {notCountedList.map(nc => (
                <div key={nc.key} className="cs-notcounted-item">{nc.item_name}</div>
              ))}
            </div>
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setShowNotCountedWarning(false)}>Review</button>
              <button
                className="cs-modal-confirm"
                onClick={() => {
                  setNotCountedAcknowledged(true);
                  setShowNotCountedWarning(false);
                  handleApply({ skipNotCounted: true });
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Negative Stock Clearance — mandatory count of oversold ingredients */}
      {showNegativeModal && (() => {
        const setRow = (id, field, value) => setNegativeModalRows(prev =>
          prev.map(r => r.item_id === id ? { ...r, [field]: value } : r));
        const rowValid = (r) =>
          r.qty !== '' && Number.isFinite(Number(r.qty)) && Number(r.qty) >= 0
          && Number.isFinite(Number(r.unit_price)) && Number(r.unit_price) > 0;
        const allValid = negativeModalRows.length > 0 && negativeModalRows.every(rowValid);
        return (
          <div className="cs-modal-overlay">
            <div className="cs-modal cs-neg-modal" onClick={e => e.stopPropagation()}>
              <div className="cs-neg-modal-head">
                <span className="cs-neg-modal-icon">&#9888;</span>
                <div>
                  <div className="cs-modal-title" style={{ margin: 0 }}>Negative Stock Clearance</div>
                  <div className="cs-modal-subtitle" style={{ margin: '4px 0 0' }}>
                    {negativeModalRows.length} ingredient{negativeModalRows.length === 1 ? '' : 's'} {negativeModalRows.length === 1 ? 'is' : 'are'} oversold below zero.
                    Enter the real physical count for each — this overrides and clears the negative before the closing stock is saved.
                  </div>
                </div>
              </div>

              <div className="cs-neg-table">
                <div className="cs-neg-grid cs-neg-head">
                  <span>Item Name</span><span>Qty</span><span>Unit</span><span>Unit &#x20B9;</span>
                </div>
                <div className="cs-neg-body">
                  {negativeModalRows.map((r, i) => (
                    <div key={r.item_id} className="cs-neg-grid cs-neg-row">
                      <span className="cs-neg-name-cell">
                        <span className="cs-neg-num">{i + 1}.</span>
                        <span>
                          <span className="cs-summary-name">{r.name}</span>
                          <span className="cs-neg-current">Current: {Number(r.current_stock).toFixed(2)} {r.unit}</span>
                        </span>
                      </span>
                      <input
                        className="cs-summary-edit cs-edit-center" type="number" inputMode="decimal"
                        min="0" step="0.0001" placeholder="0"
                        value={r.qty}
                        onChange={ev => setRow(r.item_id, 'qty', ev.target.value)}
                      />
                      <SmartUnitDropdown
                        currentUnit={String(r.unit).toLowerCase() === 'l' ? 'L' : String(r.unit).toLowerCase()}
                        onUnitChange={(unitId, unitSymbol) => changeNegativeRowUnit(r, unitSymbol)}
                        onCrossFamilyConversion={({ newUnit }) => setRow(r.item_id, 'unit', newUnit)}
                        hideOtherUnits={true}
                        direction="down"
                        borderless={true}
                        minWidth="112px"
                        className="cs-summary-edit cs-summary-edit-unit"
                        style={{ width: '100%' }}
                      />
                      <button
                        type="button"
                        className={`cs-summary-edit cs-edit-right cs-price-clickable${!(Number(r.unit_price) > 0) ? ' cs-neg-missing' : ''}`}
                        onClick={() => openNegativePriceDialog(r)}
                        title="Click to set price"
                      >
                        {Number(r.unit_price) > 0 ? `₹ ${Number(r.unit_price).toFixed(2)}` : '₹ price'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cs-modal-actions">
                <button
                  type="button"
                  className="cs-modal-cancel"
                  onClick={handleCancelNegativeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cs-modal-cancel cs-guide-save-draft-btn"
                  onClick={saveDraftFromNegativeModal}
                  disabled={savingDraft}
                  title="Save current count and negative clearance items as draft"
                >
                  {savingDraft ? 'Saving Draft…' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  className="cs-modal-confirm"
                  onClick={confirmNegativeClearance}
                  disabled={!allValid}
                  title={allValid ? '' : 'Enter a quantity and price for every item'}
                >
                  Confirm &amp; Add to Sheet
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Guided Price Fill (Item 2) */}
      {showPriceGuide && (
        <div className="cs-modal-overlay">
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <button
              type="button"
              className="cs-modal-close-btn"
              onClick={handleCancelPriceGuide}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="cs-modal-title">Enter price to save</div>
            <div className="cs-modal-subtitle">
              These items have a quantity but no price. Add a price for each to continue.
            </div>

            {(() => {
              const term = priceGuideSearch.trim().toLowerCase();
              const shown = term
                ? priceGuideItems.filter(i => String(i.item_name || '').toLowerCase().includes(term))
                : priceGuideItems;
              const prepCount = priceGuideItems.filter(i => i.is_prep).length;
              const doneCount = priceGuideItems.filter(i => Number(priceGuideValues[i.key]) > 0).length;
              return (
                <>
                  <div className="cs-priceguide-toolbar">
                    <input
                      type="text"
                      className="cs-priceguide-search"
                      placeholder="Search items..."
                      value={priceGuideSearch}
                      onChange={e => setPriceGuideSearch(e.target.value)}
                    />
                    <span className="cs-priceguide-count">
                      {doneCount}/{priceGuideItems.length} priced
                    </span>
                  </div>
                  {prepCount > 0 && (
                    <div className="cs-priceguide-legend">
                      <span className="cs-priceguide-dot" /> saved preparation ({prepCount})
                    </div>
                  )}

                  <div className="cs-priceguide-list">
                    {shown.length === 0 ? (
                      <div className="cs-priceguide-empty">No items match &quot;{priceGuideSearch}&quot;</div>
                    ) : shown.map((item) => {
                      const val = Number(priceGuideValues[item.key]);
                      const hasPrice = Number.isFinite(val) && val > 0;
                      const isExpanded = expandedGuideKeys.has(item.key);
                      const directRecipes = item.direct_recipes || [];
                      const savedPreps = item.saved_preps || [];
                      const closingQty = Number(item.closing_stock_qty) || 0;
                      const recipesTotal = directRecipes.reduce((s, r) => addQty(s, r.qty), 0);
                      const prepsTotal = savedPreps.reduce((s, p) => addQty(s, p.qty), 0);

                      return (
                        <div key={item.key} className={`cs-priceguide-item-group${isExpanded ? ' is-expanded' : ''}`}>
                          <div className="cs-priceguide-row">
                            <span className="cs-priceguide-name">
                              {item.is_prep && (
                                <span className="cs-priceguide-dot" title="Saved preparation — made in-house" />
                              )}
                              {item.item_name}
                            </span>
                            <button
                              type="button"
                              className={`cs-priceguide-qty cs-priceguide-qty-btn${isExpanded ? ' is-expanded' : ''}`}
                              onClick={() => toggleGuideExpand(item.key)}
                              title="Click to view quantity breakdown (Closing stock, Recipes, Saved prep)"
                              aria-expanded={isExpanded}
                            >
                              <span>{formatQty(item.quantity, item.unit)}</span>
                              <span className={`cs-priceguide-caret${isExpanded ? ' open' : ''}`}>▾</span>
                            </button>
                            <button
                              type="button"
                              className={`cs-priceguide-setbtn${hasPrice ? ' has-price' : ''}`}
                              onClick={() => openGuidePriceDialog(item)}
                            >
                              {hasPrice ? `₹ ${val.toFixed(2)}/${item.unit}` : 'Set price'}
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="cs-priceguide-dropdown">
                              <div className="cs-pg-breakdown-card">
                                <div className="cs-pg-breakdown-header">
                                  <span className="cs-pg-breakdown-title">
                                    {item.base_name || item.item_name} breakdown
                                  </span>
                                  {hasPrice && (
                                    <span className="cs-pg-breakdown-rate">
                                      @ ₹{val.toFixed(2)} / {item.unit}
                                    </span>
                                  )}
                                </div>

                                {/* 1st: Closing stock value of that item */}
                                <div className="cs-pg-section cs-pg-section-closing">
                                  <div className="cs-pg-section-row">
                                    <div className="cs-pg-section-left">
                                      <span className="cs-pg-num-badge">1</span>
                                      <div className="cs-pg-label-group">
                                        <span className="cs-pg-section-name">Closing stock value of item</span>
                                        <span className="cs-pg-section-sub">
                                          {item.counted_raw
                                            ? 'Counted directly on closing stock sheet'
                                            : 'Not counted directly on sheet (0.00)'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="cs-pg-section-right">
                                      <span className="cs-pg-section-qty">{formatQty(closingQty, item.unit)}</span>
                                      <span className="cs-pg-section-val">
                                        {hasPrice ? `₹ ${(closingQty * val).toFixed(2)}` : 'Pending price'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* 2nd: How much quantity of chicken is used in which all items */}
                                <div className="cs-pg-section cs-pg-section-recipes">
                                  <div className="cs-pg-section-row">
                                    <div className="cs-pg-section-left">
                                      <span className="cs-pg-num-badge">2</span>
                                      <div className="cs-pg-label-group">
                                        <span className="cs-pg-section-name">
                                          Used in direct recipes ({directRecipes.length})
                                        </span>
                                        <span className="cs-pg-section-sub">
                                          {directRecipes.length > 0
                                            ? 'Required for menu dishes & complimentary recipes'
                                            : 'None'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="cs-pg-section-right">
                                      <span className="cs-pg-section-qty">{formatQty(recipesTotal, item.unit)}</span>
                                      <span className="cs-pg-section-val">
                                        {hasPrice ? `₹ ${(recipesTotal * val).toFixed(2)}` : '—'}
                                      </span>
                                    </div>
                                  </div>
                                  {directRecipes.length > 0 ? (
                                    <div className="cs-pg-subitems">
                                      {directRecipes.map((r, rIdx) => (
                                        <div key={rIdx} className="cs-pg-subitem">
                                          <span className="cs-pg-subitem-arrow">↳</span>
                                          <span className="cs-pg-subitem-name">{r.name}</span>
                                          <span className="cs-pg-subitem-qty">{formatQty(r.qty, r.unit || item.unit)}</span>
                                          {hasPrice && (
                                            <span className="cs-pg-subitem-val">
                                              ₹{((Number(r.qty) || 0) * val).toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="cs-pg-empty-note">No direct recipes use this item</div>
                                  )}
                                </div>

                                {/* 3rd: How much qty of chicken is used in saved prep if any */}
                                <div className="cs-pg-section cs-pg-section-preps">
                                  <div className="cs-pg-section-row">
                                    <div className="cs-pg-section-left">
                                      <span className="cs-pg-num-badge">3</span>
                                      <div className="cs-pg-label-group">
                                        <span className="cs-pg-section-name">
                                          Used in saved prep ({savedPreps.length})
                                        </span>
                                        <span className="cs-pg-section-sub">
                                          {savedPreps.length > 0
                                            ? 'Required for in-house preparation batches'
                                            : 'None'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="cs-pg-section-right">
                                      <span className="cs-pg-section-qty">{formatQty(prepsTotal, item.unit)}</span>
                                      <span className="cs-pg-section-val">
                                        {hasPrice ? `₹ ${(prepsTotal * val).toFixed(2)}` : '—'}
                                      </span>
                                    </div>
                                  </div>
                                  {savedPreps.length > 0 ? (
                                    <div className="cs-pg-subitems">
                                      {savedPreps.map((p, pIdx) => (
                                        <div key={pIdx} className="cs-pg-subitem">
                                          <span className="cs-pg-subitem-arrow">↳</span>
                                          <span className="cs-pg-subitem-name">{p.name}</span>
                                          <span className="cs-pg-subitem-qty">{formatQty(p.qty, p.unit || item.unit)}</span>
                                          {hasPrice && (
                                            <span className="cs-pg-subitem-val">
                                              ₹{((Number(p.qty) || 0) * val).toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="cs-pg-empty-note">None (not used in saved preparations)</div>
                                  )}
                                </div>

                                {/* Summary footer */}
                                <div className="cs-pg-total-bar">
                                  <span className="cs-pg-total-label">Total Required:</span>
                                  <span className="cs-pg-total-qty">{formatQty(item.quantity, item.unit)}</span>
                                  <span className="cs-pg-total-val">
                                    {hasPrice
                                      ? `Total: ₹ ${(item.quantity * val).toFixed(2)}`
                                      : '(Enter price to calculate total value)'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            <div className="cs-modal-actions">
              <button
                type="button"
                className="cs-modal-cancel"
                onClick={handleCancelPriceGuide}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cs-modal-cancel cs-guide-save-draft-btn"
                onClick={saveDraftFromPriceGuide}
                disabled={savingDraft}
                title="Save your counted items as a draft and review on the sheet"
              >
                {savingDraft ? 'Saving Draft…' : 'Save Draft'}
              </button>
              {/* Deferred pricing: confirm with a provisional value now; the
                  first stock-in bill containing each item prices its line. */}
              <button
                type="button"
                className="cs-modal-cancel cs-defer-price-btn"
                onClick={deferPriceGuide}
                title="Confirm the count now — each item's first purchase bill fills its price in automatically"
              >
                Price from first purchase
              </button>
              {/* Partial pricing is allowed: whatever is filled gets saved and the
                  sheet opens for review. Only a COMPLETE fill resumes the commit. */}
              <button
                type="button"
                className="cs-modal-confirm"
                onClick={savePriceGuide}
                disabled={priceGuideFilledCount === 0}
                title={priceGuideComplete
                  ? 'Save all prices and continue'
                  : `Save the ${priceGuideFilledCount} price${priceGuideFilledCount === 1 ? '' : 's'} entered and review the sheet — the rest will be asked for again`}
              >
                {priceGuideComplete
                  ? 'Save & Continue'
                  : `Save ${priceGuideFilledCount} & Review`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Modal */}
      {showPinModal && (
        <div className="cs-modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <div className="cs-modal-title">Manager Override</div>
            <div className="cs-modal-subtitle">Enter 4-digit PIN to override the guard rail</div>
            <div className="cs-pin-inputs">
              {[0, 1, 2, 3].map(i => (
                <input
                  key={i}
                  ref={el => pinRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="cs-pin-digit"
                  value={pinValue[i]}
                  onChange={e => handlePinInput(i, e.target.value)}
                  onKeyDown={e => handlePinKeyDown(i, e)}
                />
              ))}
            </div>
            {pinError && <div className="cs-pin-error">{pinError}</div>}
            <div className="cs-modal-actions">
              <button className="cs-modal-cancel" onClick={() => setShowPinModal(false)}>Cancel</button>
              <button
                className="cs-modal-confirm"
                onClick={verifyPin}
                disabled={pinValue.some(d => !d) || pinAttempts >= 3}
              >
                {pinAttempts >= 3 ? 'Locked' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {prepPriceDialog && (
        <PrepComponentPriceDialog
          item={prepPriceDialog}
          onClose={closePrepPriceDialog}
          onSave={handlePrepDialogSave}
        />
      )}

      {/* With Recipes / Without Recipes — asked before every PDF/Excel download */}
      {recipeChoice && (
        <div className="cs-scan-source-overlay centered" onClick={() => setRecipeChoice(null)}>
          <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
            <div className="cs-scan-source-title">
              Download {recipeChoice.kind === 'report' ? 'Report' : 'Template'} ({recipeChoice.format === 'pdf' ? 'PDF' : 'Excel'})
            </div>
            <div className="cs-clear-confirm-text">
              Include the menu items (dishes)? Ingredients, saved preparations and complimentary items are always included.
            </div>
            {recipeChoice.kind === 'template' && (
              <label className="cs-price-col-toggle">
                <input
                  type="checkbox"
                  checked={includePriceCol}
                  onChange={(e) => chooseIncludePriceCol(e.target.checked)}
                />
                <span className="cs-price-col-text">
                  <strong>With prices</strong>
                  <small>
                    Adds the &#8377; column so the counter can write what each item cost.
                    Leave it off to print a count-only sheet.
                  </small>
                </span>
              </label>
            )}
            {/* Which sheet: the consolidated one (shelves) or one station's.
                The shelf list is rendered INSIDE the 'all' branch, so it is
                structurally impossible to see it in menu mode. */}
            <div className="cs-download-scope">
              <label className={`cs-download-scope-opt${downloadScope === 'all' ? ' active' : ''}`}>
                <input type="radio" name="cs-download-scope" checked={downloadScope === 'all'}
                       onChange={() => { setDownloadScope('all'); setDownloadMenuCatId(null); loadShelves(); }} />
                <span>
                  <strong>All shelves &mdash; consolidated</strong>
                  <small>
                    {recipeChoice.kind === 'template' && recipeChoice.format !== 'pdf'
                      ? 'One workbook: a tab per kitchen, the Store, and a Consolidated total that adds them up.'
                      : 'One sheet for the whole store, grouped by stock category.'}
                  </small>
                </span>
              </label>
              <label className={`cs-download-scope-opt${downloadScope === 'menu' ? ' active' : ''}`}>
                <input type="radio" name="cs-download-scope" checked={downloadScope === 'menu'}
                       onChange={() => setDownloadScope('menu')} />
                <span>
                  <strong>One menu category</strong>
                  <small>
                    {recipeChoice.kind === 'template' && recipeChoice.format !== 'pdf'
                      ? 'Just that kitchen’s tab — pick kg/g, L/ml or pcs on each row.'
                      : 'Just what that station’s dishes use, in g / ml / pcs.'}
                  </small>
                </span>
              </label>
            </div>

            {downloadScope === 'menu' && (
              <div className="cs-download-cats">
                <div className="cs-download-cats-head"><span>Menu category</span></div>
                <div className="cs-download-cats-list">
                  {checklistGroups.length === 0 && (
                    <div className="cs-download-cats-note">No menu categories yet.</div>
                  )}
                  {checklistGroups.map(g => (
                    <label key={g.id} className="cs-download-cat">
                      <input type="radio" name="cs-download-menu-cat"
                             checked={downloadMenuCatId === g.id}
                             onChange={() => setDownloadMenuCatId(g.id)} />
                      <span>{g.name}</span>
                      <small>{g.count}</small>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* The Excel workbook makes a tab per kitchen (master category). The
                owner can leave one out — a PLATTER menu group is not a place
                where stock sits. None ticked still gives Store + Consolidated. */}
            {downloadScope === 'all' && recipeChoice.kind === 'template' && recipeChoice.format !== 'pdf'
              && checklistGroups.length > 0 && (
              <div className="cs-download-cats cs-download-kitchens">
                <div className="cs-download-cats-head">
                  <span>Kitchen tabs</span>
                  <button type="button" className="cs-download-cats-all" onClick={() => setDownloadKitchenIds(null)}>
                    All kitchens
                  </button>
                </div>
                <div className="cs-download-kitchens-list">
                  {checklistGroups.map(g => {
                    const picked = downloadKitchenIds === null || downloadKitchenIds.includes(g.id);
                    return (
                      <label key={g.id} className="cs-download-kitchen">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => setDownloadKitchenIds(prev => {
                            const all = checklistGroups.map(x => x.id);
                            const base = prev === null ? all : prev;
                            const next = base.includes(g.id) ? base.filter(id => id !== g.id) : [...base, g.id];
                            return next.length === all.length ? null : next;
                          })}
                        />
                        <span>{g.name}</span>
                        <small>{g.count}</small>
                      </label>
                    );
                  })}
                </div>
                <div className="cs-download-cats-note">
                  Every tab lists the whole store; each kitchen&rsquo;s own items come first. Store and Consolidated are always included.
                </div>
              </div>
            )}

            {downloadScope === 'all' && stockCategories.length > 0 && (
              <div className="cs-download-cats">
                <div className="cs-download-cats-head">
                  <span>Sections to print</span>
                  <button type="button" className="cs-download-cats-all" onClick={() => setDownloadCatIds([])}>
                    All shelves
                  </button>
                </div>
                <div className="cs-download-cats-list">
                  {stockCategories.filter(c => c.consumable).map((c, _i, shelves) => {
                    const picked = downloadCatIds.length === 0 || downloadCatIds.includes(c.category_id);
                    // An empty list means "every shelf", so the last ticked shelf
                    // cannot be unticked: that would silently re-tick them all.
                    const pickedCount = downloadCatIds.length === 0 ? shelves.length : downloadCatIds.length;
                    const isLast = picked && pickedCount === 1;
                    return (
                      <label key={c.category_id} className="cs-download-cat"
                             title={isLast ? 'At least one shelf has to be printed' : undefined}>
                        <input
                          type="checkbox"
                          checked={picked}
                          disabled={isLast}
                          onChange={() => setDownloadCatIds(prev => {
                            // An empty list means "all", so the first untick has to
                            // start from the full list rather than from nothing.
                            const base = prev.length === 0
                              ? stockCategories.filter(x => x.consumable).map(x => x.category_id)
                              : prev;
                            if (base.includes(c.category_id) && base.length === 1) return prev;
                            return base.includes(c.category_id)
                              ? base.filter(id => id !== c.category_id)
                              : [...base, c.category_id];
                          })}
                        />
                        <span>{c.name}</span>
                        <small>{c.item_count}</small>
                      </label>
                    );
                  })}
                </div>
                {uncategorisedCount > 0 && (
                  <div className="cs-download-cats-note">
                    {uncategorisedCount} item{uncategorisedCount > 1 ? 's have' : ' has'} no stock category &mdash;
                    {' '}printed last under &ldquo;Other / Uncategorized&rdquo;.
                  </div>
                )}
              </div>
            )}
            {needsMenuCategory && (
              <div className="cs-download-cats-note">
                Pick a menu category above to download that station&rsquo;s sheet.
              </div>
            )}
            <div className="cs-recipe-choice-options">
              <button type="button" className="cs-scan-source-btn" disabled={needsMenuCategory}
                      onClick={() => runDownloadChoice(true)}>
                With Recipes
              </button>
              <button type="button" className="cs-scan-source-btn" disabled={needsMenuCategory}
                      onClick={() => runDownloadChoice(false)}>
                Without Recipes
              </button>
            </div>
            <div className="cs-clear-confirm-actions">
              <button type="button" className="cs-scan-source-cancel" onClick={() => setRecipeChoice(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Selection Preview Modal */}
      {/* Camera vs gallery — mobile only (see openScanPicker) */}
      {/* Clear Sheet confirmation — destructive, wipes drafts + entered counts */}
      {showRedoConfirm && redoInfo && (
        <div className="cs-scan-source-overlay centered" onClick={() => setShowRedoConfirm(false)}>
          <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
            <div className="cs-scan-source-title">Restore deleted draft?</div>
            <div className="cs-clear-confirm-text">
              Bring back the saved draft from{' '}
              <strong>{String(redoInfo.closing_date || '').slice(0, 10)}</strong>?
              It will reopen on the Consolidated Sheet with everything you had entered.
            </div>
            <div className="cs-clear-confirm-actions">
              <button
                type="button"
                className="cs-scan-source-cancel"
                onClick={() => setShowRedoConfirm(false)}
                disabled={redoBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cs-scan-source-btn"
                onClick={restoreRedo}
                disabled={redoBusy}
              >
                {redoBusy ? 'Restoring…' : 'Restore Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="cs-scan-source-overlay centered" onClick={() => setShowClearConfirm(false)}>
          <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
            <div className="cs-scan-source-title">Clear this sheet?</div>
            <div className="cs-clear-confirm-text">
              This deletes every saved draft and erases all the counts you&apos;ve entered.
              This can&apos;t be undone.
            </div>
            <div className="cs-clear-confirm-actions">
              <button
                type="button"
                className="cs-scan-source-cancel"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cs-scan-source-btn cs-clear-danger-btn"
                onClick={() => {
                  setShowClearConfirm(false);
                  setShowClearSecondConfirm(true);
                }}
              >
                Clear Sheet
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearSecondConfirm && (() => {
        const sectionsToClear = [];
        for (const cat of masterCategories) {
          let count = 0;
          for (const ing of cat.ingredients) {
            const q = entries[eKey('raw', ing.item_id)]?.quantity;
            if (q !== undefined && q !== '') count++;
          }
          for (const prep of cat.preparations) {
            const q = entries[eKey('prep', prep.item_id)]?.quantity;
            if (q !== undefined && q !== '') count++;
          }
          for (const comp of (cat.complimentary || [])) {
            const q = entries[eKey('comp', comp.item_id)]?.quantity;
            if (q !== undefined && q !== '') count++;
          }
          for (const menu of (cat.menu_items || [])) {
            const q = entries[eKey('menu', menu.item_id)]?.quantity;
            if (q !== undefined && q !== '') count++;
          }
          if (count > 0) {
            sectionsToClear.push({ name: cat.master_category_name, count });
          }
        }
        let otherCount = 0;
        for (const ing of uncategorizedIngredients) {
          const q = entries[eKey('raw', ing.item_id)]?.quantity;
          if (q !== undefined && q !== '') otherCount++;
        }
        const assignedPrepIds = new Set();
        masterCategories.forEach(c => {
          c.preparations.forEach(p => assignedPrepIds.add(p.item_id));
        });
        const orphanedPreps = allPreparations.filter(p => !assignedPrepIds.has(p.item_id));
        for (const prep of orphanedPreps) {
          const q = entries[eKey('prep', prep.item_id)]?.quantity;
          if (q !== undefined && q !== '') otherCount++;
        }
        const assignedCompIds = new Set();
        masterCategories.forEach(c => {
          (c.complimentary || []).forEach(x => assignedCompIds.add(x.item_id));
        });
        const orphanedComps = allComplimentary.filter(x => !assignedCompIds.has(x.item_id));
        for (const comp of orphanedComps) {
          const q = entries[eKey('comp', comp.item_id)]?.quantity;
          if (q !== undefined && q !== '') otherCount++;
        }
        const assignedMenuIds = new Set();
        masterCategories.forEach(c => {
          (c.menu_items || []).forEach(x => assignedMenuIds.add(x.item_id));
        });
        const orphanedMenus = allMenuItems.filter(x => !assignedMenuIds.has(x.item_id));
        for (const menu of orphanedMenus) {
          const q = entries[eKey('menu', menu.item_id)]?.quantity;
          if (q !== undefined && q !== '') otherCount++;
        }
        if (otherCount > 0) {
          sectionsToClear.push({ name: 'Other', count: otherCount });
        }

        return (
          <div className="cs-scan-source-overlay centered" onClick={() => setShowClearSecondConfirm(false)}>
            <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
              <div className="cs-scan-source-title">Confirm clearing data</div>
              <div className="cs-clear-confirm-text" style={{ paddingBottom: '8px' }}>
                The counts for the following sections will be permanently deleted:
              </div>
              <div className="cs-clear-sections-box">
                <div className="cs-clear-sections-title">Affected Sections</div>
                <div className="cs-clear-sections-list">
                  {sectionsToClear.length === 0 ? (
                    <div className="cs-empty-state" style={{ padding: '8px 0', fontSize: '13px' }}>No items counted yet.</div>
                  ) : (
                    sectionsToClear.map(sec => (
                      <div key={sec.name} className="cs-clear-section-row">
                        <span className="cs-clear-section-name">{sec.name}</span>
                        <span className="cs-clear-section-count">{sec.count} item{sec.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="cs-clear-confirm-actions">
                <button
                  type="button"
                  className="cs-scan-source-cancel"
                  onClick={() => setShowClearSecondConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cs-scan-source-btn cs-clear-danger-btn"
                  onClick={clearSheet}
                >
                  Clear Sheet
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirmation modal for removing an item completely */}
      {removeItemConfirm && (
        <div className="cs-scan-source-overlay centered" onClick={handleCancelRemoveItem}>
          <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
            <div className="cs-scan-source-title">Remove item completely?</div>
            <div className="cs-clear-confirm-text">
              Do you want to completely remove <strong>{removeItemConfirm.itemName}</strong> from this sheet?
            </div>
            <div className="cs-clear-confirm-actions">
              <button
                type="button"
                className="cs-scan-source-cancel"
                onClick={handleCancelRemoveItem}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cs-scan-source-btn cs-clear-danger-btn"
                onClick={handleConfirmRemoveItem}
              >
                Yes, remove
              </button>
            </div>
          </div>
        </div>
      )}

      {showExitConfirm && (
        <div className="cs-scan-source-overlay centered">
          <div className="cs-scan-source-sheet" style={{ position: 'relative', maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="cs-modal-close-btn"
              onClick={() => {
                setShowExitConfirm(false);
                setPendingExitAction(null);
              }}
            >
              ✕
            </button>
            <div className="cs-scan-source-title" style={{ marginTop: '8px' }}>Save Draft?</div>
            <div className="cs-clear-confirm-text">
              You have counted items on your sheet. Would you like to save them as a draft before leaving?
            </div>
            <div className="cs-clear-confirm-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                type="button"
                className="cs-scan-source-btn"
                style={{ flex: 1, borderColor: '#111827', color: '#111827', margin: 0, padding: '10px 14px', height: '40px', boxSizing: 'border-box' }}
                onClick={() => {
                  setShowExitConfirm(false);
                  savedStateRef.current = { entries: {}, prepComponentPrices: {}, ingredientPrices: {} }; // bypass dirty check
                  if (pendingExitAction) pendingExitAction();
                  setPendingExitAction(null);
                }}
              >
                Discard Changes
              </button>
              <button
                type="button"
                className="cs-scan-source-btn cs-clear-danger-btn"
                style={{ flex: 1, margin: 0, height: '40px', boxSizing: 'border-box' }}
                disabled={savingDraft}
                onClick={async () => {
                  await handleSaveDraftAndExit();
                }}
              >
                {savingDraft ? 'Saving Draft…' : 'Save as Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Sheet cleared" toast */}
      {clearToast && (
        <div className="cs-clear-toast" role="status">
          Sheet cleared
        </div>
      )}

      {/* Category Guide Modal when clicking Qty dash in All Tab */}
      {categoryGuideItem && (
        <div className="cs-modal-overlay">
          <div
            className="cs-modal"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 420, textAlign: 'center', borderRadius: 16, padding: '24px 20px' }}
          >
            <button
              type="button"
              className="cs-modal-close-btn"
              onClick={() => setCategoryGuideItem(null)}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="cs-modal-title" style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginTop: 4, marginBottom: 8 }}>
              Enter Quantity in Category
            </div>

            <div className="cs-modal-subtitle" style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.5, marginBottom: 20 }}>
              To add or edit the quantity for <strong style={{ color: '#111827' }}>"{categoryGuideItem.itemName}"</strong>, please go to its respective category tab.
            </div>

            {categoryGuideItem.breakdown && (categoryGuideItem.breakdown.directRecipes.length > 0 || categoryGuideItem.breakdown.savedPreps.length > 0) && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 16,
                textAlign: 'left'
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 6, textTransform: 'uppercase' }}>
                  Quantity Usage Breakdown
                </div>
                <div style={{ fontSize: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>1. Closing Stock on Sheet:</span>
                  <strong style={{ color: '#0f172a' }}>{formatQty(categoryGuideItem.breakdown.closingStockQty, categoryGuideItem.breakdown.unit)}</strong>
                </div>
                <div style={{ fontSize: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>2. Used in Recipes ({categoryGuideItem.breakdown.directRecipes.length}):</span>
                  <strong style={{ color: '#0f172a' }}>{formatQty(categoryGuideItem.breakdown.recipesTotalQty, categoryGuideItem.breakdown.unit)}</strong>
                </div>
                {categoryGuideItem.breakdown.directRecipes.map((r, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#64748b', paddingLeft: 12, display: 'flex', justifyContent: 'space-between' }}>
                    <span>↳ {r.name}</span>
                    <span>{formatQty(r.qty, r.unit || categoryGuideItem.breakdown.unit)}</span>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: '#334155', display: 'flex', justifyContent: 'space-between', marginTop: 4, marginBottom: 4 }}>
                  <span>3. Used in Saved Prep ({categoryGuideItem.breakdown.savedPreps.length}):</span>
                  <strong style={{ color: '#0f172a' }}>{formatQty(categoryGuideItem.breakdown.prepsTotalQty, categoryGuideItem.breakdown.unit)}</strong>
                </div>
                {categoryGuideItem.breakdown.savedPreps.map((p, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#64748b', paddingLeft: 12, display: 'flex', justifyContent: 'space-between' }}>
                    <span>↳ {p.name}</span>
                    <span>{formatQty(p.qty, p.unit || categoryGuideItem.breakdown.unit)}</span>
                  </div>
                ))}
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Required:</span>
                  <span>{formatQty(categoryGuideItem.breakdown.totalRequiredQty, categoryGuideItem.breakdown.unit)}</span>
                </div>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginBottom: 4,
                maxHeight: '272px',
                overflowY: 'auto',
                paddingRight: '4px',
                paddingLeft: '2px',
                paddingTop: '2px',
                paddingBottom: '2px'
              }}
            >
              {categoryGuideItem.categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(cat.id);
                    setCategoryGuideItem(null);
                    setSearchTerm(categoryGuideItem.itemName);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textAlign: 'left'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: '#111827'
                    }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      Go to {cat.name}
                    </span>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}


      {showScanModeChooser && (
        <div className="cs-lovable-modal-overlay" onClick={() => setShowScanModeChooser(false)}>
          <div className="cs-lovable-modal cs-scan-mode-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cs-lovable-modal-head cs-scan-mode-head">
              <div>
                <h2>Scan Sheet Mode</h2>
                <p>Please select a category mode to scan sheet.</p>
              </div>
              <button
                type="button"
                className="cs-scan-mode-close"
                onClick={() => setShowScanModeChooser(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="cs-scan-mode-body">
              <button
                type="button"
                className="cs-primary-wide-btn cs-scan-mode-btn"
                onClick={() => startScanFlow('master')}
              >
                Scan with Master Categories
              </button>
              <button
                type="button"
                className="cs-secondary-wide-btn cs-scan-mode-btn"
                onClick={() => startScanFlow('normal')}
              >
                Scan without Category
              </button>
            </div>
          </div>
        </div>
      )}

      {showScanSourceChooser && (() => {
        const activeCategoryName = activeTab === 'all' 
          ? '' 
          : (masterCategories.find(c => c.master_category_id === activeTab)?.master_category_name || '');
        const showMasterCategoryControls = scanCategoryMode === 'master';
        return (
          <div
            className="cs-scan-source-overlay centered"
            onClick={() => { setShowScanSourceChooser(false); setShowScanSourceMenu(false); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              handleDroppedFiles(e.dataTransfer?.files);
            }}
          >
            <div className="cs-scan-source-sheet" onClick={e => e.stopPropagation()}>
              <div className="cs-scan-source-header" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 2,
                paddingBottom: '18px',
                width: '100%'
              }}>
                <div className="cs-scan-source-title" style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  color: '#111827',
                  textAlign: 'center',
                  paddingBottom: 0,
                  margin: '0 auto'
                }}>
                  Add count sheet
                </div>
                <div style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  marginTop: '-9px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {showMasterCategoryControls && activeCategoryName && (
                    <span style={{
                      fontSize: '12.5px',
                      fontWeight: '600',
                      color: '#374151',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '3px 8px',
                      whiteSpace: 'nowrap'
                    }}>
                      {activeCategoryName}
                    </span>
                  )}
                  {showMasterCategoryControls && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowScanSourceMenu(!showScanSourceMenu)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6B7280',
                          borderRadius: '50%',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <FaEllipsisV size={16} />
                      </button>

                      {showScanSourceMenu && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '4px',
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          zIndex: 1000,
                          minWidth: '180px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          padding: '4px 0'
                        }}>
                          <div style={{
                            padding: '6px 12px',
                            fontSize: '11px',
                            color: '#9ca3af',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            borderBottom: '1px solid #f3f4f6',
                            textAlign: 'left'
                          }}>
                            Categories
                          </div>
                          {masterCategories.map(cat => {
                            const isChecked = selectedGroupIds.includes(cat.master_category_id);
                            return (
                              <button
                                key={cat.master_category_id}
                                type="button"
                                onClick={() => {
                                  toggleChecklistGroup(cat.master_category_id);
                                  if (!isChecked) {
                                    setActiveTab(cat.master_category_id);
                                  } else if (activeTab === cat.master_category_id) {
                                    setActiveTab('all');
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '10px 12px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  color: '#111827',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <span style={{
                                  fontWeight: activeTab === cat.master_category_id ? '600' : 'normal',
                                  textAlign: 'left'
                                }}>
                                  {cat.master_category_name}
                                </span>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}} /* Handled by parent button click */
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#171717',
                                    margin: 0
                                  }}
                                />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
 
              <div
                className={`cs-scan-drop-zone${isDragging ? ' dragging' : ''}`}
                onClick={() => {
                  setShowScanSourceChooser(false);
                  ocrFileInputRef.current?.click();
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cs-drop-icon">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <div className="cs-drop-text">Drag &amp; drop here</div>
                <div className="cs-drop-subtext">Photos of a written sheet, the filled Excel template, or a POS stock report</div>
              </div>
 
              <div className="cs-scan-source-divider"><span>OR</span></div>
 
              <button
                type="button"
                className="cs-scan-source-btn"
                onClick={() => { setShowScanSourceChooser(false); sheetFileInputRef.current?.click(); }}
              >
                Upload Sheet or POS Report
              </button>
              {isTouchDevice && (
                <button
                  type="button"
                  className="cs-scan-source-btn"
                  onClick={() => { setShowScanSourceChooser(false); ocrCameraInputRef.current?.click(); }}
                >
                  Take Photo
                </button>
              )}
              <button
                type="button"
                className="cs-scan-source-btn"
                onClick={() => { setShowScanSourceChooser(false); ocrFileInputRef.current?.click(); }}
              >
                Choose Images
              </button>
              <button
                type="button"
                className="cs-scan-source-cancel"
                onClick={() => setShowScanSourceChooser(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {showFilePreview && (
        <div
          className={`ocr-file-preview-overlay${isDragging ? ' dragging' : ''}`}
          onClick={handleClearAllFiles}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            handleDroppedFiles(e.dataTransfer?.files);
          }}
        >
          <div className="ocr-file-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="ocr-file-preview-header">
              <h3 className="ocr-file-preview-title">Pages ({selectedFiles.length}/{MAX_OCR_PAGES})</h3>
              <div className="ocr-file-preview-actions">
                <button onClick={openScanPicker} className="ocr-file-add-btn" type="button">Add</button>
                <button onClick={handleClearAllFiles} className="ocr-file-clear-btn" type="button">Clear</button>
              </div>
            </div>
            {selectedFiles.length > 1 && (
              <div className="ocr-file-preview-hint">
                All {selectedFiles.length} pages are read together as one count.
                If an item is written twice with different values, you&apos;ll be asked which is right.
              </div>
            )}
            <div className="ocr-files-grid">
              {ocrImagePreviews.map((preview, index) => (
                <div key={index} className="ocr-file-item">
                  <img
                    src={preview}
                    alt={`Sheet ${index + 1}`}
                    className="ocr-file-preview-img"
                    onClick={() => setExpandedImage(preview)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="ocr-file-number">#{index + 1}</div>
                  <button onClick={() => handleRemoveFile(index)} className="ocr-file-remove-btn" type="button" title="Remove this image">&times;</button>
                </div>
              ))}
            </div>
            <button onClick={handleConfirmFiles} className="ocr-file-confirm-btn" disabled={selectedFiles.length === 0} type="button">
              Confirm {selectedFiles.length} Image{selectedFiles.length > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {expandedImage && (
        <div
          className="ocr-lightbox-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            cursor: 'zoom-out'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: 'relative', display: 'inline-block', cursor: 'default' }}
          >
            <img
              src={expandedImage}
              alt="Expanded view"
              style={{
                display: 'block',
                maxWidth: 'min(90vw, 600px)',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)'
              }}
            />
            <button
              type="button"
              onClick={() => setExpandedImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#fff',
                color: '#111',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                lineHeight: 1
              }}
            >
              &times;
            </button>
          </div>
        </div>
      )}


      {/* OCR Processing Overlay */}
      {ocrProcessing && (
        <div className="ocr-processing-overlay">
          <div className="ocr-processing-card">
            <div className="ocr-processing-spinner" />
            {/* Pages are read concurrently in a single request, so there is no
                page-by-page progress to report — just how many are in flight. */}
            <div className="ocr-processing-text">
              {ocrProgress && ocrProgress.mode === 'sheet'
                ? 'Reading your filled sheet'
                : ocrProgress && ocrProgress.total > 1
                  ? `Reading ${ocrProgress.total} pages together...`
                  : 'Scanning sheet'}
            </div>
          </div>
        </div>
      )}

      {/* OCR Review Modal */}
      <ClosingStockOCRReview
        isOpen={showOCRReview}
        onClose={() => {
          setShowOCRReview(false);
          setOcrResult(null);
          ocrImagePreviews.forEach(preview => {
            if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
          });
          setOcrImagePreview(null);
          setOcrImagePreviews([]);
          setSelectedFiles([]);
          setCurrentImageIndex(0);
        }}
        ocrResult={ocrResult}
        existingQtyOf={ocrExistingQtyOf}
        onConfirm={handleOCRConfirm}
        imagePreviewUrl={ocrImagePreview}
        imagePreviewUrls={ocrImagePreviews}
      />

      {/* Set Price Modal (Pic 2 Implementation) */}
      {prepPriceDialog && (
        <PrepComponentPriceDialog
          item={prepPriceDialog}
          onClose={() => setPrepPriceDialog(null)}
          onSave={(id, price, newQty, newUnit) => handlePrepDialogSave(id, price, newQty, newUnit)}
        />
      )}
    </div>
  );
};

// Strict-5, one spelling each. The old list carried seven entries for five
// units — 'l' AND 'liter', plus 'unit' as a third name for 'pcs'. Whatever the
// user picked was stored verbatim, so the same physical unit accumulated in the
// data under several spellings (67 litre-variant rows had to be repaired by
// hand). The converter accepts every spelling, so nothing ever errored; the
// data just drifted. Offering one name per unit is what stops that at source.
const PRICE_UNITS = ['kg', 'g', 'L', 'ml', 'pcs'];

const PrepPriceDropdown = ({ value, options, onChange, menuKey, openMenu, setOpenMenu }) => {
  const open = openMenu === menuKey;
  const selected = options.find(option => option.value === value);
  return (
    <div className="cs-prep-dropdown">
      <button
        type="button"
        className={`cs-prep-dropdown-trigger${open ? ' open' : ''}`}
        onClick={() => setOpenMenu(open ? null : menuKey)}
      >
        <span>{selected?.label || value}</span>
        <span className="cs-prep-dropdown-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div className="cs-prep-dropdown-menu">
          {options.map(option => (
            <button
              type="button"
              key={option.value}
              className={`cs-prep-dropdown-option${option.value === value ? ' selected' : ''}`}
              onClick={() => {
                onChange(option.value);
                setOpenMenu(null);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PrepComponentPriceDialog = ({ item, onClose, onSave }) => {
  // A zero/absent quantity means NOT COUNTED. Seed the helper with 1 so the
  // price maths works; the dirty flags below keep it out of the count.
  const initialQty = item?.quantity > 0 ? String(Number(item.quantity.toFixed(4))) : '1';
  const [qty, setQty] = useState(initialQty);
  const [unit, setUnit] = useState(item?.unit || 'g');
  // The dialog's qty/unit are PRICING HELPERS: "what does this much cost?".
  // They are seeded from the row purely for convenience and must NEVER flow
  // back into the count unless the user actually edits them. Without this the
  // dialog silently stamped 0.01 into every uncounted row it was opened on.
  const [qtyDirty, setQtyDirty] = useState(false);
  const [unitDirty, setUnitDirty] = useState(false);
  const [priceType, setPriceType] = useState('invoice');
  const [directPrice, setDirectPrice] = useState('');
  const [invoiceQty, setInvoiceQty] = useState('1');
  const [invoiceUnit, setInvoiceUnit] = useState(item?.unit || 'g');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    const qtyVal = item?.quantity > 0 ? String(Number(item.quantity.toFixed(4))) : '1';
    setQty(qtyVal);
    setUnit(item?.unit || 'g');
    setQtyDirty(false);
    setUnitDirty(false);

    const currPrice = item?.currentPrice;
    if (typeof currPrice === 'number' && Number.isFinite(currPrice) && currPrice > 0) {
      setPriceType('invoice');
      setDirectPrice((currPrice * Number(qtyVal)).toFixed(2));
    } else {
      setPriceType('invoice');
      setDirectPrice('');
    }

    setInvoiceQty('1');
    setInvoiceUnit(item?.unit || 'g');
    setInvoiceAmount('');
    setOpenMenu(null);
  }, [item?.id, item?.unit, item?.quantity, item?.currentPrice]);

  const qtyNumber = parseFloat(qty);
  const directNumber = parseFloat(directPrice);
  const invoiceQtyNumber = parseFloat(invoiceQty);
  const invoiceAmountNumber = parseFloat(invoiceAmount);

  // Convert entered quantity to the invoice unit before multiplying
  const qtyInInvoiceUnit = convertQty(qtyNumber, unit, invoiceUnit);   // null across families

  const priceForEnteredQty = priceType === 'direct'
    ? (Number.isFinite(directNumber) ? directNumber : NaN)
    : (Number.isFinite(invoiceAmountNumber) && Number.isFinite(invoiceQtyNumber) && invoiceQtyNumber > 0 && Number.isFinite(qtyInInvoiceUnit)
      ? (invoiceAmountNumber / invoiceQtyNumber) * qtyInInvoiceUnit
      : NaN);

  const unitPrice = priceType === 'direct'
    ? (Number.isFinite(directNumber) && Number.isFinite(qtyNumber) && qtyNumber > 0 ? directNumber / qtyNumber : NaN)
    : (Number.isFinite(invoiceAmountNumber) && Number.isFinite(invoiceQtyNumber) && invoiceQtyNumber > 0 ? invoiceAmountNumber / invoiceQtyNumber : NaN);

  const helperText = priceType === 'direct'
    ? `Enter price for the selected quantity. Per-unit: ${Number.isFinite(unitPrice) ? `\u20B9${unitPrice.toFixed(2)}/${unit}` : 'not available yet'}.`
    : `Enter the invoice total for the selected quantity.`;

  // A row stored in a legacy spelling ('liter', 'unit', lowercase 'l') is folded
  // into its canonical symbol rather than appended as a sixth option — otherwise
  // the dialog would keep offering, and re-saving, the very spelling being
  // retired. csUnitOptions/toCanonicalUnit already own that mapping.
  const canonicalPriceUnit = (u) => {
    const c = toCanonicalUnit(u);
    const hit = PRICE_UNITS.find((p) => p.toLowerCase() === String(c || u).toLowerCase());
    return hit || null;
  };
  const priceUnitOptions = (u) => {
    const canon = canonicalPriceUnit(u);
    // Genuinely unrecognised units still appear, so a row can never be edited
    // into silently claiming a unit it was not counted in.
    return canon ? PRICE_UNITS : [u, ...PRICE_UNITS].filter(Boolean);
  };
  const unitOptions = priceUnitOptions(unit);
  const invoiceUnitOptions = priceUnitOptions(invoiceUnit);
  const unitDropdownOptions = unitOptions.map(u => ({ value: u, label: u }));
  const invoiceUnitDropdownOptions = invoiceUnitOptions.map(u => ({ value: u, label: u }));
  const priceTypeOptions = [
    { value: 'direct', label: 'Direct Price' },
    { value: 'invoice', label: 'Invoice Total' }
  ];

  const handleFixPrice = () => {
    if (!Number.isFinite(unitPrice)) return;
    setPriceType('direct');
    setDirectPrice((unitPrice * (Number.isFinite(qtyNumber) ? qtyNumber : 1)).toFixed(2));
  };

  const handleSave = () => {
    let finalPriceInRecipeUnit = NaN;
    if (priceType === 'direct') {
      const qtyInRecipeUnit = convertQty(qtyNumber, unit, item.unit);
      if (qtyInRecipeUnit > 0) {
        finalPriceInRecipeUnit = directNumber / qtyInRecipeUnit;
      }
    } else {
      const invoiceQtyInRecipeUnit = convertQty(invoiceQtyNumber, invoiceUnit, item.unit);
      if (invoiceQtyInRecipeUnit > 0) {
        finalPriceInRecipeUnit = invoiceAmountNumber / invoiceQtyInRecipeUnit;
      }
    }

    if (Number.isFinite(finalPriceInRecipeUnit)) {
      // null = "the user did not touch this" → the receiver leaves the count
      // alone. Passing qty unconditionally is what let a pricing action
      // overwrite a counted 3.5 kg with the dialog's helper quantity.
      onSave(
        item.id,
        finalPriceInRecipeUnit,
        qtyDirty ? qtyNumber : null,
        unitDirty ? unit : null
      );
    }
  };

  return (
    <div className="cs-prep-price-overlay" onClick={onClose}>
      <div className="cs-prep-price-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-prep-price-header">
          <h2>Set price</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="cs-prep-price-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="cs-prep-price-body">
          <label className="cs-prep-price-field cs-prep-price-field-full">
            <span>Item Name</span>
            <input value={item?.name || ''} readOnly />
          </label>

          <div className="cs-prep-price-grid cs-prep-price-grid-2">
            <label className="cs-prep-price-field">
              <span>Quantity *</span>
              <input
                aria-label="Quantity"
                value={qty}
                onChange={e => { setQty(e.target.value); setQtyDirty(true); }}
                inputMode="decimal"
              />
            </label>
            <label className="cs-prep-price-field">
              <span>Unit *</span>
              <PrepPriceDropdown
                value={unit}
                options={unitDropdownOptions}
                onChange={(u) => { setUnit(u); setUnitDirty(true); }}
                menuKey="unit"
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            </label>
          </div>

          <div className="cs-prep-price-grid cs-prep-price-grid-2">
            <label className="cs-prep-price-field">
              <span>Price Type *</span>
              <PrepPriceDropdown
                value={priceType}
                options={priceTypeOptions}
                onChange={setPriceType}
                menuKey="priceType"
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            </label>
            <label className="cs-prep-price-field">
              <span>Price for {Number.isFinite(qtyNumber) ? qtyNumber : 0} {unit} (&#x20B9;)</span>
              <input
                value={priceType === 'direct' ? directPrice : (Number.isFinite(priceForEnteredQty) ? priceForEnteredQty.toFixed(2) : '')}
                onChange={e => {
                  if (priceType === 'invoice') {
                    setPriceType('direct');
                  }
                  setDirectPrice(e.target.value);
                }}
                onFocus={() => {
                  if (priceType === 'invoice') {
                    setPriceType('direct');
                    if (Number.isFinite(priceForEnteredQty)) {
                      setDirectPrice(priceForEnteredQty.toFixed(2));
                    }
                  }
                }}
                placeholder="0.00"
                inputMode="decimal"
              />
            </label>
          </div>

          <p className="cs-prep-price-help">{helperText}</p>

          <div className="cs-prep-price-grid cs-prep-price-grid-3">
            <label className="cs-prep-price-field">
              <span>Invoice Quantity</span>
              <input value={invoiceQty} onChange={e => setInvoiceQty(e.target.value)} inputMode="decimal" />
            </label>
            <label className="cs-prep-price-field">
              <span>Units</span>
              <PrepPriceDropdown
                value={invoiceUnit}
                options={invoiceUnitDropdownOptions}
                onChange={setInvoiceUnit}
                menuKey="invoiceUnit"
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            </label>
            <label className="cs-prep-price-field">
              <span>Invoice Amount (&#x20B9;)</span>
              <input value={invoiceAmount} onChange={e => setInvoiceAmount(e.target.value)} placeholder="&#x20B9; 0.00" inputMode="decimal" />
            </label>
          </div>

          <button type="button" className="cs-prep-fix-price-btn" onClick={handleFixPrice}>
            Fix Price
          </button>
        </div>

        <div className="cs-prep-price-actions">
          <button type="button" className="cs-prep-price-cancel" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="cs-prep-price-save"
            onClick={handleSave}
            disabled={!Number.isFinite(unitPrice)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Exported for regression tests: the price dialog's write-back contract is a
// data-integrity boundary and is tested directly, not through the page.
export { PrepComponentPriceDialog };

export default ClosingStockPage;
